import React, { useState, useEffect, useRef, useMemo } from "react";
import AdminDashboard from "./adminDashboard";
import { isHeadOfficeAdmin } from "./adminApi";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { exportFullWorkbook, exportSingle, exportBobCorpStyle, exportVAT201 } from "./exports";
import {
  filterJournals,
  profitAndLoss,
  balanceSheet,
  trialBalance,
  generalLedger,
  monthsCovered,
  journalsForMonth,
  vatSummary,
  bankReconciliation,
} from "./computeFromJournals";
// Bank Feed (Stitch) — wired but dormant; hidden until BANK_FEED_ENABLED is flipped on
// AND a Stitch client id exists. See src/bankFeed/config.js.
import { BANK_FEED_ENABLED } from "./bankFeed/config";
import BankFeedSettings from "./bankFeed/BankFeedSettings";
import { handleStitchRedirect } from "./bankFeed/callback";
import { saveBankFeedLink } from "./bankFeed/sync";
import {
  loadProject,
  saveProject,
  loadCOA,
  seedCOA,
  upsertAccount,
  deleteAccount,
  loadJournals,
  postJournal,
  postJournals,
  reverseJournal,
  loadLedgerSettings,
  setLockDate,
  loadAuditLog,
  onAuth,
  signIn,
  resetPassword,
  registerStokvelUser,
  resendVerification,
  refreshEmailVerified,
  getUserProfile,
  joinStokvelByCode,
  signOutUser,
  deleteUserAccount,
  authHeader,
  ensureUserRecord,
  listProjectsForUser,
  addProjectMember,
  removeProjectMember,
  claimProjectIfUnclaimed,
  findUserByEmail,
  loadOpeningBalancesAsAt,
  saveOpeningBalances,
  saveInvoice,
  loadInvoices,
  deleteInvoice,
  nextInvoiceNumber,
  loadCategorisationRules,
  saveCategorisationRules,
  loadInvItems, addInvItem, updateInvItem,
  loadInvSuppliers, addInvSupplier,
  loadInvMovements, addInvMovement,
  loadPayEmployees, addPayEmployee, deletePayEmployee,
  loadPayRuns, addPayRun,
} from "./firebase";
import { normDesc, journalFromRule, learnRules, ruleCount } from "./categorisationRules";
import { computePayslip, DEFAULTS as PAY_DEFAULTS } from "./payrollEngine";
import { DEFAULT_COA, ACCOUNT_TYPES } from "./coa";
import { ISSUER, BANKING, SERVICE_CATALOG, VAT_RATE, formatAddress } from "./issuer";
import { buildEInvoice } from "./einvoice"; // SARS VAT-Modernisation readiness (dormant)
import { parseBankCsv, parseBankXlsx } from "./csvParser";
import { extractJournalsFromBankRows, extractJournalsFromPdf, extractRowsFromStatementText, validateJournals } from "./extractJournals";
import { extractPdfText } from "./pdfText";
import InsightsView, { InsightsStrip } from "./InsightsView";
import { ClientsView, PipelineView } from "./crm";
import { SuppliersAP, MatchPayments } from "./suppliers";
import { DuplicateScan } from "./dupscan";
import { ComplianceView } from "./compliance";
import { TasksView } from "./tasks";
import { StokvelView } from "./stokvel";
import { askAssistant } from "./assistant";
import { splitPdfBase64 } from "./pdfChunk";
import {
  xlsxToText,
  extractOpeningBalancesFromText,
  extractOpeningBalancesFromPdf,
  toFirestoreBalances,
  validateBalances,
} from "./extractOpeningBalances";

const f = (n) => (n == null || isNaN(n) ? "0.00" : Math.abs(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// Sign-aware money for computed results (profit/loss, equity, balances) — `f` strips
// the sign via Math.abs, which would otherwise show a loss as if it were a profit.
const fS = (n) => (Number(n) < 0 ? "-R " : "R ") + f(n);

const EMPTY_PROJECT = {
  id: "",
  name: "",
  type: "",
  year: "",
  yearStart: "",
  yearEnd: "",
  taxType: "Non-SBC",
  taxRate: 27,
  address: "",
  defaultBankCode: "100010",
  // Customer details (used when invoicing this project)
  email: "",
  contactPerson: "",
  vatNumber: "",
  regNumber: "",
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(",")[1]);
  reader.onerror = () => reject(new Error("Failed to read " + file.name));
  reader.readAsDataURL(file);
});

const fileToArrayBuffer = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("Failed to read " + file.name));
  reader.readAsArrayBuffer(file);
});

const Logo = () => (
  <svg width={90} height={28} viewBox="0 0 128 40">
    <rect x="0" y="4" width="32" height="32" rx="6" fill="#c8a24e" />
    <path d="M8 14h16M8 20h12M8 26h16" stroke="#1a1a2e" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="22" cy="22" r="4" fill="none" stroke="#1a1a2e" strokeWidth="2" />
    <text x="40" y="27" fontFamily="'DM Sans',sans-serif" fontSize="18" fontWeight="700" fill="currentColor">Tlhakiso</text>
  </svg>
);

function MC({ l, v, s, a }) {
  return (
    <div style={{ background: a ? "#1a1a2e" : "#faf9f6", borderRadius: 10, padding: "14px 18px", border: a ? "1px solid #2a2a4a" : "1px solid #e8e4dc" }}>
      <div style={{ fontSize: 10, color: a ? "#8b8ba0" : "#9a9488", textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 600 }}>{l}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: a ? "#c8a24e" : "#1a1a2e", marginTop: 3 }}>{v}</div>
      {s && <div style={{ fontSize: 10, color: a ? "#5a5a7a" : "#b0aa9e", marginTop: 2 }}>{s}</div>}
    </div>
  );
}

// The SA year of assessment currently in progress (1 March – end Feb).
// Used as the default reporting period — the period is an output-time concern,
// not a property fixed when the company profile is created.
function currentSATaxYear() {
  const d = new Date();
  const y = d.getFullYear();
  const startYear = d.getMonth() >= 2 ? y : y - 1; // getMonth() 2 = March
  const endYear = startYear + 1;
  const feb = ((endYear % 4 === 0 && endYear % 100 !== 0) || endYear % 400 === 0) ? 29 : 28;
  return { start: startYear + "-03-01", end: endYear + "-02-" + feb };
}

// SA years of assessment are named by the year they END (YOA 2026 = 1 Mar 2025 – 28 Feb 2026).
function yoaEndYear(dateStr) {
  const y = parseInt(String(dateStr).slice(0, 4), 10);
  const m = parseInt(String(dateStr).slice(5, 7), 10);
  return m >= 3 ? y + 1 : y; // March onward belongs to the next YOA
}
function yoaPeriod(endYear) {
  const feb = ((endYear % 4 === 0 && endYear % 100 !== 0) || endYear % 400 === 0) ? 29 : 28;
  return { endYear, label: "YOA " + endYear, from: (endYear - 1) + "-03-01", to: endYear + "-02-" + feb };
}
// Build the list of YOAs to offer: every year spanned by the journals, plus the
// current one, newest first — so the dropdown always covers the data present.
function listYOAOptions(journals) {
  const curEnd = yoaPeriod(parseInt(currentSATaxYear().end.slice(0, 4), 10)).endYear;
  let minE = curEnd, maxE = curEnd;
  (journals || []).forEach((j) => {
    if (!j.date) return;
    const e = yoaEndYear(j.date);
    if (e < minE) minE = e;
    if (e > maxE) maxE = e;
  });
  const out = [];
  for (let e = maxE; e >= minE; e--) out.push(yoaPeriod(e));
  return out;
}
// Describe a from/to range for labelling reports & exports. If it matches a SA
// year of assessment exactly, label it "YOA <year>"; otherwise use the raw range.
function periodLabelFor(from, to) {
  const range = (from || "?") + " to " + (to || "?");
  if (from && to && from.slice(5) === "03-01" && to.slice(5, 7) === "02") {
    const p = yoaPeriod(parseInt(to.slice(0, 4), 10));
    if (p.from === from && p.to === to) {
      return { label: p.label, title: p.label + " (" + from + " to " + to + ")", file: p.label };
    }
  }
  return { label: range, title: range, file: range };
}

function NewProjectModal({ onClose, onCreate }) {
  const [fm, setFm] = useState({ name: "", type: "", address: "", taxType: "Non-SBC", taxRate: "27", email: "", contactPerson: "", vatNumber: "", regNumber: "" });
  const u = (k, v) => setFm({ ...fm, [k]: v });
  const is = { width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 13, outline: "none", background: "#fff", marginTop: 4 };
  const ls = { fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginTop: 14 };
  const submit = () => {
    if (!fm.name.trim()) return alert("Please enter a company name");
    const id = fm.name.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-");
    onCreate({ ...EMPTY_PROJECT, id, name: fm.name, type: fm.type, address: fm.address, taxType: fm.taxType, taxRate: parseInt(fm.taxRate) || 27, email: fm.email, contactPerson: fm.contactPerson, vatNumber: fm.vatNumber, regNumber: fm.regNumber });
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: "24px 28px", width: 520, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>New Project</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#999" }}>&times;</button>
        </div>
        <label style={ls}>Company Name *</label><input value={fm.name} onChange={(e) => u("name", e.target.value)} placeholder="e.g. ABC Trading (Pty) Ltd" style={is} />
        <label style={ls}>Business Type</label><input value={fm.type} onChange={(e) => u("type", e.target.value)} placeholder="e.g. Retail, Services" style={is} />
        <label style={ls}>Address</label><input value={fm.address} onChange={(e) => u("address", e.target.value)} style={is} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={ls}>Tax Type</label><select value={fm.taxType} onChange={(e) => u("taxType", e.target.value)} style={{ ...is, cursor: "pointer" }}><option value="Non-SBC">Non-SBC</option><option value="SBC">SBC</option></select></div>
          <div><label style={ls}>Tax Rate %</label><input value={fm.taxRate} onChange={(e) => u("taxRate", e.target.value)} type="number" style={is} /></div>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: "#9a9488", fontStyle: "italic" }}>The reporting period (year of assessment) is chosen when you generate financials, not here.</div>
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #eceae4" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9a9488", textTransform: "uppercase", letterSpacing: 1 }}>Customer Details (used for invoicing)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={ls}>Email</label><input value={fm.email} onChange={(e) => u("email", e.target.value)} placeholder="billing@company.co.za" style={is} /></div>
            <div><label style={ls}>Contact Person</label><input value={fm.contactPerson} onChange={(e) => u("contactPerson", e.target.value)} placeholder="John Doe" style={is} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={ls}>Registration Number</label><input value={fm.regNumber} onChange={(e) => u("regNumber", e.target.value)} placeholder="YYYY/NNNNNN/07" style={is} /></div>
            <div><label style={ls}>VAT Number (if applicable)</label><input value={fm.vatNumber} onChange={(e) => u("vatNumber", e.target.value)} placeholder="4xxxxxxxxx" style={is} /></div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #e0ddd8", background: "#fff", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={submit} style={{ padding: "9px 24px", borderRadius: 8, border: "none", background: "#1a1a2e", color: "#c8a24e", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Create Project</button>
        </div>
      </div>
    </div>
  );
}

function Dash({ project, journals, opening, coa }) {
  const empty = !journals || journals.length === 0;
  if (empty) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: "#9a9488" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>&#128203;</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#5a5a5a" }}>No journals posted yet</div>
        <div style={{ fontSize: 13, marginTop: 8 }}>Go to the Process tab to upload a bank statement (CSV, XLSX, or PDF) and post journals.</div>
      </div>
    );
  }
  const pl = profitAndLoss(coa, journals);
  const bs = balanceSheet(coa, opening || {}, journals);
  const taxRate = project.taxRate || 27;
  const tp = Math.max(0, pl.np * (taxRate / 100));
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10, marginBottom: 20 }}>
        <MC l="Total Assets" v={"R " + f(bs.totalAssets)} s="From Balance Sheet" a />
        <MC l="Net Profit / (Loss)" v={fS(pl.np)} s={pl.np < 0 ? "Loss" : "Year to date"} />
        <MC l="Tax @ Rate" v={"R " + f(tp)} s={"@ " + taxRate + "% " + (project.taxType || "")} />
        <MC l="Journals Posted" v={journals.length} s="Across all months" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>Profit & Loss</div>
          <div style={{ background: "#faf9f6", borderRadius: 8, border: "1px solid #e8e4dc", overflow: "hidden" }}>
            {[["Revenue", pl.rev], ["Less: Cost of Sales", pl.cogs, 1], ["Gross Profit", pl.gp, 0, 1], ["Less: Operating Expenses", pl.opex, 1], ["Operating Income", pl.opInc], ["Plus: Other Income", pl.oi], ["Less: Other Expenses", pl.oe, 1], ["Net Profit", pl.np, 0, 1]].map(([l, v, neg, b], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 14px", borderBottom: "1px solid #eceae4", background: b ? "#eef0f4" : "transparent", fontWeight: b ? 600 : 400, fontSize: 12 }}>
                <span>{l}</span><span style={{ color: v < 0 ? "#b33" : "#1a1a2e" }}>{neg ? "(" + f(v) + ")" : fS(v)}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>Balance Sheet Summary</div>
          <div style={{ background: "#faf9f6", borderRadius: 8, border: "1px solid #e8e4dc", overflow: "hidden" }}>
            {[["Total Assets", bs.totalAssets, 0, 1], ["Total Liabilities", bs.totalLiabilities], ["Total Equity (incl. CYE)", bs.totalEquity], ["L + E", bs.totalLiabilities + bs.totalEquity, 0, 1], ["Difference (A - (L+E))", bs.diff]].map(([l, v, neg, b], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 14px", borderBottom: "1px solid #eceae4", background: b ? "#eef0f4" : "transparent", fontWeight: b ? 600 : 400, fontSize: 12 }}>
                <span>{l}</span><span style={{ color: Math.abs(v) > 0.01 && l.indexOf("Difference") === 0 ? "#b33" : "#1a1a2e" }}>{fS(v)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <InsightsStrip coa={coa} opening={opening} journals={journals} />
    </div>
  );
}

function JournalReviewTable({ proposed, coa, onChange, onRemove, dupSet }) {
  const codes = coa.map((a) => a.code + " — " + a.name);
  return (
    <div style={{ marginTop: 12, border: "1px solid #e8e4dc", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ background: "#1a1a2e", color: "#c8a24e", fontSize: 12, padding: "8px 14px", fontWeight: 600 }}>
        Proposed Journals ({proposed.length}) — review before posting
      </div>
      <div style={{ maxHeight: 460, overflowY: "auto" }}>
        {proposed.map((j, idx) => {
          const dr = (j.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
          const cr = (j.lines || []).reduce((s, l) => s + (Number(l.cr) || 0), 0);
          const balanced = Math.abs(dr - cr) < 0.01;
          return (
            <div key={idx} style={{ borderBottom: "1px solid #eceae4", padding: 10, background: dupSet && dupSet.has(idx) ? "#fff7e6" : (balanced ? "#fff" : "#fff4f4") }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                <input value={j.date} onChange={(e) => onChange(idx, { ...j, date: e.target.value })} style={{ width: 110, padding: 4, fontSize: 12, border: "1px solid #e0ddd8", borderRadius: 4 }} />
                <input value={j.narration} onChange={(e) => onChange(idx, { ...j, narration: e.target.value })} style={{ flex: 1, padding: 4, fontSize: 12, border: "1px solid #e0ddd8", borderRadius: 4 }} />
                {dupSet && dupSet.has(idx) && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#f3e0b0", color: "#8a6d1d", fontWeight: 600, whiteSpace: "nowrap" }}>⚠ already posted</span>}
                <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: j.confidence === "high" ? "#dff5e1" : j.confidence === "medium" ? "#fff3c4" : "#ffd5d5", color: "#333" }}>{j.confidence || "—"}</span>
                <button onClick={() => onRemove(idx)} style={{ background: "none", border: "1px solid #e0ddd8", borderRadius: 4, padding: "2px 8px", cursor: "pointer", color: "#b33", fontSize: 11 }}>Remove</button>
              </div>
              {j.sourceRow && (
                <div style={{ fontSize: 11, color: "#9a9488", marginBottom: 4 }}>Source: {j.sourceRow.description} · R {f(j.sourceRow.amount)}</div>
              )}
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f4f2ed", color: "#7a7568" }}>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>Account</th>
                    <th style={{ textAlign: "right", padding: "4px 6px" }}>Debit</th>
                    <th style={{ textAlign: "right", padding: "4px 6px" }}>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {(j.lines || []).map((l, li) => (
                    <tr key={li}>
                      <td style={{ padding: "3px 6px" }}>
                        <select value={l.accountCode} onChange={(e) => {
                          const code = e.target.value;
                          const acc = coa.find((a) => a.code === code);
                          const lines = j.lines.slice();
                          lines[li] = { ...l, accountCode: code, accountName: acc ? acc.name : l.accountName };
                          onChange(idx, { ...j, lines });
                        }} style={{ width: "100%", padding: 3, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4 }}>
                          {codes.map((c, i) => <option key={i} value={coa[i].code}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ textAlign: "right", padding: "3px 6px" }}>
                        <input type="number" step="0.01" value={l.dr || ""} onChange={(e) => {
                          const lines = j.lines.slice();
                          lines[li] = { ...l, dr: parseFloat(e.target.value) || 0 };
                          onChange(idx, { ...j, lines });
                        }} style={{ width: 90, textAlign: "right", padding: 3, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4 }} />
                      </td>
                      <td style={{ textAlign: "right", padding: "3px 6px" }}>
                        <input type="number" step="0.01" value={l.cr || ""} onChange={(e) => {
                          const lines = j.lines.slice();
                          lines[li] = { ...l, cr: parseFloat(e.target.value) || 0 };
                          onChange(idx, { ...j, lines });
                        }} style={{ width: 90, textAlign: "right", padding: 3, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4 }} />
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: "#f4f2ed", fontWeight: 600 }}>
                    <td style={{ padding: "3px 6px" }}>Totals</td>
                    <td style={{ textAlign: "right", padding: "3px 6px" }}>R {f(dr)}</td>
                    <td style={{ textAlign: "right", padding: "3px 6px" }}>R {f(cr)}</td>
                  </tr>
                </tbody>
              </table>
              {!balanced && <div style={{ color: "#b33", fontSize: 10, marginTop: 4 }}>Out of balance by R {f(dr - cr)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Process({ project, coa, onPosted, journals }) {
  const [parsed, setParsed] = useState(null); // { rows: [...], pdfs: [{pdfBase64, source, sizeKB}], sources: [name] }
  const [proposed, setProposed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [bankCode, setBankCode] = useState(project.defaultBankCode || "100010");
  const [rules, setRules] = useState({});
  const [dragging, setDragging] = useState(false);
  const fr = useRef(null);
  const dragCounter = useRef(0);

  // Page-level drag & drop. dataTransfer.types includes "Files" only when the
  // user is dragging real files (not text/links), so we ignore everything else.
  const dragHasFiles = (e) => e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files");
  const onDragEnter = (e) => { if (!dragHasFiles(e)) return; e.preventDefault(); dragCounter.current += 1; setDragging(true); };
  const onDragOver = (e) => { if (!dragHasFiles(e)) return; e.preventDefault(); };
  const onDragLeave = (e) => { if (!dragHasFiles(e)) return; e.preventDefault(); dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setDragging(false); } };
  const onDrop = (e) => { if (!dragHasFiles(e)) return; e.preventDefault(); dragCounter.current = 0; setDragging(false); if (e.dataTransfer.files && e.dataTransfer.files.length) onFiles(e.dataTransfer.files); };

  useEffect(() => {
    let live = true;
    loadCategorisationRules(project.id).then((r) => { if (live) setRules(r || {}); }).catch(() => {});
    return () => { live = false; };
  }, [project.id]);

  const clearRules = async () => {
    if (!window.confirm("Clear all saved categorisation rules for this client? New statements will go back to automatic categorisation until re-learned.")) return;
    setRules({});
    try { await saveCategorisationRules(project.id, {}); setStatus("Categorisation memory cleared."); } catch (e) { setStatus("Clear failed: " + e.message); }
  };

  const onFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    setProposed(null);
    setParsed(null);
    setStatus("Reading " + files.length + " file" + (files.length > 1 ? "s" : "") + "...");
    const rows = [];   // combined rows from every CSV/XLSX/PDF, each tagged with its source file
    const pdfs = [];   // [{ pdfBase64, source, sizeKB }] — PDFs read directly (no text layer)
    const sources = [];
    const report = []; // per-file outcome so every uploaded file is visibly accounted for
    const errors = [];
    for (const file of files) {
      try {
        const name = file.name.toLowerCase();
        if (name.endsWith(".csv") || name.endsWith(".txt")) {
          const text = await file.text();
          const r = parseBankCsv(text);
          (r.rows || []).forEach((row) => rows.push({ ...row, __source: file.name }));
          sources.push(file.name);
          if (r.error && !(r.rows && r.rows.length)) errors.push(file.name + ": " + r.error);
          report.push({ name: file.name, kind: (r.rows && r.rows.length) ? "rows" : (r.error ? "error" : "empty"), count: (r.rows || []).length, note: r.error });
        } else if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
          const buf = await fileToArrayBuffer(file);
          const r = parseBankXlsx(buf, XLSX);
          (r.rows || []).forEach((row) => rows.push({ ...row, __source: file.name }));
          sources.push(file.name);
          if (r.error && !(r.rows && r.rows.length)) errors.push(file.name + ": " + r.error);
          report.push({ name: file.name, kind: (r.rows && r.rows.length) ? "rows" : (r.error ? "error" : "empty"), count: (r.rows || []).length, note: r.error });
        } else if (name.endsWith(".pdf")) {
          const b64 = await fileToBase64(file);
          // Convert the PDF to rows first (read its text layer, then transcribe to
          // rows) so it flows through the reliable CSV path + review table. Falls
          // back to reading the PDF directly only when there is no text layer (scanned).
          let rowsFromPdf = null; // null = not converted; array = converted (may be empty)
          try {
            setStatus("Converting " + file.name + " — reading PDF text…");
            const { text } = await extractPdfText(b64);
            if (text && text.trim().length > 80) {
              setStatus("Converting " + file.name + " — structuring rows…");
              const period = project && project.yearStart && project.yearEnd ? project.yearStart + " to " + project.yearEnd : null;
              const r = await extractRowsFromStatementText({ text, sourceLabel: file.name, period });
              rowsFromPdf = r.rows || [];
            }
          } catch (e) {
            errors.push(file.name + ": " + e.message);
          }
          sources.push(file.name);
          if (rowsFromPdf) {
            rowsFromPdf.forEach((row) => rows.push({ ...row, __source: file.name, __fromPdf: true }));
            report.push({ name: file.name, kind: rowsFromPdf.length ? "rows" : "empty", count: rowsFromPdf.length });
          } else {
            pdfs.push({ pdfBase64: b64, source: file.name, sizeKB: Math.round(file.size / 1024) });
            report.push({ name: file.name, kind: "pdf" });
          }
        } else {
          errors.push("Unsupported: " + file.name);
          report.push({ name: file.name, kind: "error", note: "unsupported file type" });
        }
      } catch (err) {
        errors.push(file.name + ": " + err.message);
        report.push({ name: file.name, kind: "error", note: err.message });
      }
    }
    if (!rows.length && !pdfs.length) {
      // Nothing to extract — but still surface the per-file report (e.g. statements with no transactions).
      setParsed({ rows, pdfs, sources, report });
      const allEmpty = report.length > 0 && report.every((x) => x.kind === "empty");
      setStatus(allEmpty
        ? "No transactions in the uploaded statement" + (report.length > 1 ? "s" : "") + " (opening balance only) — nothing to extract."
        : (errors.length ? errors.join("; ") : "No usable data found in the selected files."));
      return;
    }
    setParsed({ rows, pdfs, sources, report });
    const parts = [];
    if (rows.length) parts.push(rows.length + " rows");
    if (pdfs.length) parts.push(pdfs.length + " PDF" + (pdfs.length > 1 ? "s" : ""));
    setStatus("Loaded " + parts.join(" + ") + " from " + sources.length + " file" + (sources.length > 1 ? "s" : "") + (errors.length ? " · " + errors.length + " skipped" : "") + ".");
  };

  const onExtract = async () => {
    if (!parsed) return;
    const rows = parsed.rows || [];
    const pdfs = parsed.pdfs || [];
    if (!rows.length && !pdfs.length) { setStatus("Nothing to extract"); return; }
    setLoading(true);
    try {
      let all = [];
      let fromMemory = 0, fromAi = 0;
      // 1. Row-based files (CSV/XLSX): match known descriptions locally, only
      //    send the rest to the categorisation engine. Every journal keeps its source file name.
      // Run async task factories with bounded concurrency (parallelises the AI batches).
      const runPool = async (taskFns, concurrency) => {
        const out = new Array(taskFns.length); let idx = 0;
        const worker = async () => { while (idx < taskFns.length) { const cur = idx++; out[cur] = await taskFns[cur](); } };
        await Promise.all(Array.from({ length: Math.min(concurrency, taskFns.length) }, worker));
        return out;
      };
      if (rows.length) {
        const bankName = (coa.find((a) => a.code === bankCode) || {}).name || "";
        const known = [], unknown = [];
        rows.forEach((r) => {
          const rule = rules[normDesc(r.description)];
          if (rule) known.push({ ...journalFromRule(r, rule, bankCode, bankName), __sourceFile: r.__source });
          else unknown.push(r);
        });
        fromMemory = known.length;
        let aiJournals = [];
        if (unknown.length) {
          // Categorise in smaller batches run IN PARALLEL (bounded concurrency) so a
          // large statement completes in a fraction of the time — output stays bounded per call.
          const BATCH = 25;
          const slices = [];
          for (let b = 0; b < unknown.length; b += BATCH) slices.push(unknown.slice(b, b + BATCH));
          let done = 0;
          setStatus("Matched " + known.length + " from memory; categorising " + unknown.length + " transactions (" + slices.length + " parallel batches)…");
          const parts = await runPool(slices.map((slice) => async () => {
            const r = await extractJournalsFromBankRows({ project, coa, bankAccountCode: bankCode, rows: slice });
            done += slice.length;
            setStatus("Categorised " + Math.min(done, unknown.length) + " of " + unknown.length + " transactions…");
            return (r.journals || []).map((j) => ({ ...j, __sourceFile: j.sourceRow && j.sourceRow.__source }));
          }), 6);
          aiJournals = parts.flat();
        } else {
          setStatus("All " + known.length + " transactions matched from memory — no processing needed.");
        }
        fromAi += aiJournals.length;
        all = all.concat(known, aiJournals);
      }
      // 2. PDF files: split into page-chunks (bounded output), then read ALL chunks
      //    IN PARALLEL (bounded concurrency) so large statements complete much faster.
      const pdfTasks = [];
      for (let i = 0; i < pdfs.length; i++) {
        const pdf = pdfs[i];
        let split, couldNotSplit = false;
        try { split = await splitPdfBase64(pdf.pdfBase64, 3); }
        catch (e) { couldNotSplit = true; split = { total: 1, chunks: [{ base64: pdf.pdfBase64, from: 1, to: 1 }] }; }
        split.chunks.forEach((ch) => pdfTasks.push({ pdf, ch, couldNotSplit, nChunks: split.chunks.length }));
      }
      if (pdfTasks.length) {
        let done = 0;
        setStatus("Reading " + pdfs.length + " statement(s) in " + pdfTasks.length + " parallel chunks…");
        const parts = await runPool(pdfTasks.map((t) => async () => {
          let r;
          try {
            r = await extractJournalsFromPdf({ project, coa, bankAccountCode: bankCode, pdfBase64: t.ch.base64, fileName: t.pdf.source + (t.nChunks > 1 ? " p" + t.ch.from + "-" + t.ch.to : "") });
          } catch (err) {
            if (t.couldNotSplit && /truncat|token limit/i.test(err.message)) {
              throw new Error("“" + t.pdf.source + "” is a locked/encrypted PDF, so it can't be auto-split, and it's too large to read in one pass. Re-save it as a fresh PDF (open it, then Print → “Save as PDF” / “Microsoft Print to PDF”) and upload that copy — or upload a single-month statement.");
            }
            throw err;
          }
          done++;
          setStatus("Read " + done + " of " + pdfTasks.length + " statement chunks…");
          return (r.journals || []).map((j) => ({ ...j, __sourceFile: t.pdf.source }));
        }), 6);
        const js = parts.flat();
        fromAi += js.length;
        all = all.concat(js);
      }
      all.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
      const warnings = validateJournals(all, coa);
      setProposed(all);
      setStatus("Got " + all.length + " proposed journals from " + (parsed.sources || []).length + " file(s) (" + fromMemory + " from memory, " + fromAi + " auto-categorised)." + (warnings.length ? " Warnings: " + warnings.length : ""));
    } catch (err) {
      setStatus("Extraction failed: " + err.message);
    }
    setLoading(false);
  };

  const onPost = async () => {
    if (!proposed || proposed.length === 0) return;
    setLoading(true);
    setStatus("Posting " + proposed.length + " journals to Firestore...");
    try {
      const ids = await postJournals(project.id, proposed.map((p) => ({
        date: p.date,
        narration: p.narration,
        confidence: p.confidence || null,
        notes: p.notes || null,
        source: { type: "bank_statement", file: p.__sourceFile || (parsed.sources || []).join(", ") || null, description: p.sourceRow ? p.sourceRow.description : null, amount: p.sourceRow ? p.sourceRow.amount : null },
        lines: (p.lines || []).map((l) => ({ accountCode: l.accountCode, accountName: l.accountName || "", dr: Number(l.dr) || 0, cr: Number(l.cr) || 0 })),
      })));
      // Learn categorisation rules from what was actually posted (incl. edits).
      try {
        const learned = learnRules(rules, proposed, bankCode);
        setRules(learned);
        await saveCategorisationRules(project.id, learned);
      } catch (e) { /* non-fatal */ }
      setStatus("Posted " + ids.length + " journals. Reports will refresh.");
      setProposed(null);
      setParsed(null);
      if (onPosted) onPosted();
    } catch (err) {
      setStatus("Post failed: " + err.message);
    }
    setLoading(false);
  };

  const bankAccountOptions = coa.filter((a) => a.type === "asset" && a.code.startsWith("100"));

  // Duplicate detection: flag proposed transactions that match an already-posted
  // one (same date + amount + description), so overlapping/re-uploaded statements
  // don't double-count. A flag the user can override — genuine repeats happen.
  const dupSet = useMemo(() => {
    const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
    const fp = (d, a, desc) => String(d || "") + "|" + (Math.round((Number(a) || 0) * 100) / 100) + "|" + norm(desc);
    const posted = new Set();
    (journals || []).forEach((j) => {
      const amt = j && j.source && typeof j.source.amount === "number" ? j.source.amount : null;
      if (amt == null) return;
      const desc = j.source && j.source.description != null ? j.source.description : j.narration;
      posted.add(fp(j.date, amt, desc));
    });
    const set = new Set();
    (proposed || []).forEach((p, i) => {
      const sr = p.sourceRow || {};
      const amt = typeof sr.amount === "number" ? sr.amount : null;
      if (amt == null) return;
      if (posted.has(fp(p.date, amt, sr.description))) set.add(i);
    });
    return set;
  }, [proposed, journals]);

  return (
    <div onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} style={{ position: "relative" }}>
      {dragging && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(26,26,46,0.45)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ background: "#fff", border: "3px dashed #c8a24e", borderRadius: 16, padding: "48px 72px", textAlign: "center", boxShadow: "0 24px 70px rgba(0,0,0,0.35)" }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>📥</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>Drop bank statements to upload</div>
            <div style={{ fontSize: 13, color: "#9a9488", marginTop: 6 }}>CSV, XLSX, or PDF · multiple files supported</div>
          </div>
        </div>
      )}
      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>1. Upload bank statements</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input ref={fr} type="file" multiple accept=".csv,.xlsx,.xls,.pdf,.txt" style={{ display: "none" }} onChange={(e) => { if (e.target.files && e.target.files.length) onFiles(e.target.files); e.target.value = ""; }} />
          <button onClick={() => fr.current.click()} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #1a1a2e", background: "#fff", cursor: "pointer", fontSize: 13, color: "#1a1a2e", fontWeight: 600 }}>Choose files...</button>
          <div style={{ fontSize: 12, color: "#9a9488" }}>CSV, XLSX, or PDF · pick several, or drag &amp; drop them anywhere on this page.</div>
          {ruleCount(rules) > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#3d7a52" }}>
              <span title="Known descriptions are categorised instantly from memory.">🧠 {ruleCount(rules)} remembered</span>
              <button onClick={clearRules} style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 10, cursor: "pointer", color: "#b33" }}>Clear</button>
            </div>
          )}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, color: "#666" }}>Bank account:</span>
            <select value={bankCode} onChange={(e) => setBankCode(e.target.value)} style={{ padding: 6, fontSize: 12, border: "1px solid #e0ddd8", borderRadius: 4 }}>
              {bankAccountOptions.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
            </select>
          </div>
        </div>
        <div
          onClick={() => fr.current.click()}
          style={{ marginTop: 12, padding: "18px 16px", border: "2px dashed #d8d2c6", borderRadius: 8, background: "#fffdf8", textAlign: "center", cursor: "pointer", color: "#9a9488", fontSize: 12 }}
        >
          <span style={{ fontSize: 18, marginRight: 6 }}>⬆</span> Drag &amp; drop one or more bank statements here, or click to browse.
        </div>
        {parsed && parsed.report && parsed.report.length > 0 && (
          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {parsed.report.map((r, i) => {
              const label = r.kind === "rows" ? (r.count + " row" + (r.count === 1 ? "" : "s"))
                : r.kind === "empty" ? "no transactions"
                : r.kind === "pdf" ? "read directly (PDF)"
                : (r.note || "skipped");
              const c = r.kind === "rows" ? { bg: "#eef2ec", fg: "#3d7a52", bd: "#d6e2d2" }
                : r.kind === "empty" ? { bg: "#f6f1e6", fg: "#9a7d3d", bd: "#e6dcc4" }
                : r.kind === "pdf" ? { bg: "#eef0f6", fg: "#3d567a", bd: "#d2dae8" }
                : { bg: "#f8ecec", fg: "#b33", bd: "#e8cccc" };
              return (
                <span key={i} title={r.name + " — " + label} style={{ fontSize: 11, background: c.bg, color: c.fg, border: "1px solid " + c.bd, borderRadius: 12, padding: "2px 10px" }}>
                  📄 {r.name} — <strong>{label}</strong>
                </span>
              );
            })}
          </div>
        )}
        {status && <div style={{ marginTop: 10, fontSize: 12, color: "#444" }}>{status}</div>}
      </div>

      {parsed && parsed.rows && parsed.rows.length > 0 && (
        <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>2. Review parsed rows ({parsed.rows.length}) and extract journals</div>
            <button onClick={onExtract} disabled={loading} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: loading ? "#ccc" : "#1a1a2e", color: loading ? "#888" : "#c8a24e", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>{loading ? "Extracting..." : ((parsed.pdfs && parsed.pdfs.length) ? "Extract all" : "Extract transactions")}</button>
          </div>
          <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 10, border: "1px solid #eceae4", borderRadius: 6, background: "#fff" }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead style={{ background: "#f4f2ed", position: "sticky", top: 0 }}>
                <tr><th style={{ padding: "4px 8px", textAlign: "left" }}>Date</th><th style={{ padding: "4px 8px", textAlign: "left" }}>Description</th><th style={{ padding: "4px 8px", textAlign: "right" }}>Amount</th><th style={{ padding: "4px 8px", textAlign: "right" }}>Balance</th></tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, 200).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f0eeea" }}>
                    <td style={{ padding: "3px 8px" }}>{r.date}</td>
                    <td style={{ padding: "3px 8px" }}>{r.description}</td>
                    <td style={{ padding: "3px 8px", textAlign: "right", color: r.amount < 0 ? "#b33" : "#1a1a2e" }}>R {f(r.amount)}</td>
                    <td style={{ padding: "3px 8px", textAlign: "right", color: "#666" }}>{r.balance == null ? "" : "R " + f(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {parsed && parsed.pdfs && parsed.pdfs.length > 0 && (
        <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{parsed.rows && parsed.rows.length > 0 ? "PDF statements" : "2. Extract journals from PDF"} ({parsed.pdfs.length})</div>
              <div style={{ fontSize: 11, color: "#9a9488", marginTop: 4 }}>Statements are read directly — no pre-parsing. One pass per file.</div>
            </div>
            {!(parsed.rows && parsed.rows.length > 0) && (
              <button onClick={onExtract} disabled={loading} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: loading ? "#ccc" : "#1a1a2e", color: loading ? "#888" : "#c8a24e", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>{loading ? "Extracting..." : (parsed.pdfs.length > 1 ? "Extract all" : "Extract transactions")}</button>
            )}
          </div>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
            {parsed.pdfs.map((p, i) => (
              <div key={i} style={{ fontSize: 11, color: "#555", display: "flex", justifyContent: "space-between", background: "#fff", border: "1px solid #eceae4", borderRadius: 4, padding: "4px 10px" }}>
                <span>📄 {p.source}</span><span style={{ color: "#9a9488" }}>{p.sizeKB} KB</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, padding: 10, background: "#fff8e6", border: "1px solid #f0d97f", borderRadius: 6, fontSize: 11, color: "#7a5b00", lineHeight: 1.5 }}>
            PDFs are slower than CSV/XLSX — expect 30-60 seconds per statement. Each PDF is sent separately, so multiple files take proportionally longer. A single statement with more than ~80 transactions may approach the 8K-token response cap; split it or use CSV.
          </div>
        </div>
      )}

      {proposed && proposed.length > 0 && (
        <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>3. Review proposed journals and post</div>
            <button onClick={onPost} disabled={loading} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: loading ? "#ccc" : "#3d7a52", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>{loading ? "Posting..." : "Post all to Firestore"}</button>
          </div>
          {dupSet.size > 0 && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: "#fff7e6", border: "1px solid #f0d9a8", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 12, color: "#8a6d1d" }}>⚠ <strong>{dupSet.size}</strong> of these look already posted (same date, amount &amp; description) — highlighted below. Exclude them unless they're genuine repeats.</div>
              <button onClick={() => setProposed(proposed.filter((_, i) => !dupSet.has(i)))} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #d9b24e", background: "#fff", color: "#8a6d1d", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Exclude {dupSet.size} duplicate{dupSet.size > 1 ? "s" : ""}</button>
            </div>
          )}
          <JournalReviewTable
            proposed={proposed}
            coa={coa}
            dupSet={dupSet}
            onChange={(idx, next) => { const arr = proposed.slice(); arr[idx] = next; setProposed(arr); }}
            onRemove={(idx) => setProposed(proposed.filter((_, i) => i !== idx))}
          />
        </div>
      )}
    </div>
  );
}

function ManualJournal({ project, coa, onPosted }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const blankLine = () => ({ accountCode: "", dr: 0, cr: 0 });

  const [date, setDate] = useState(todayIso);
  const [narration, setNarration] = useState("");
  const [reference, setReference] = useState("");
  const [lines, setLines] = useState([blankLine(), blankLine()]);
  const [posting, setPosting] = useState(false);
  const [status, setStatus] = useState("");

  const totals = lines.reduce(
    (acc, l) => ({ dr: acc.dr + (Number(l.dr) || 0), cr: acc.cr + (Number(l.cr) || 0) }),
    { dr: 0, cr: 0 }
  );
  const diff = totals.dr - totals.cr;
  const balanced = Math.abs(diff) < 0.01 && (totals.dr > 0 || totals.cr > 0);
  const lineErrors = lines.map((l) => {
    if (!l.accountCode) return "Pick an account";
    const drN = Number(l.dr) || 0;
    const crN = Number(l.cr) || 0;
    if (drN < 0 || crN < 0) return "Amounts must be positive";
    if (drN > 0 && crN > 0) return "A line is debit OR credit, not both";
    if (drN === 0 && crN === 0) return "Enter a debit or credit";
    return null;
  });
  const hasLineError = lineErrors.some((e) => e !== null);
  const canPost = !!narration.trim() && !!date && lines.length >= 2 && !hasLineError && balanced && !posting;

  const updateLine = (i, patch) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => prev.concat([blankLine()]));
  const removeLine = (i) => setLines((prev) => (prev.length <= 2 ? prev : prev.filter((_, j) => j !== i)));
  const reset = () => {
    setNarration(""); setReference(""); setLines([blankLine(), blankLine()]);
    setStatus("");
  };

  const findCode = (matchType) => {
    const a = coa.find((x) => x.code === matchType || x.name.toLowerCase() === String(matchType).toLowerCase());
    return a ? a.code : "";
  };

  const templates = [
    {
      label: "Depreciation",
      apply: () => {
        setNarration("Depreciation for the period");
        setReference("Depreciation schedule");
        setLines([
          { accountCode: findCode("610500") || (coa.find((a) => a.type === "expense") || {}).code || "", dr: 0, cr: 0 },
          { accountCode: findCode("130090") || "", dr: 0, cr: 0 },
        ]);
      },
    },
    {
      label: "Accrual",
      apply: () => {
        setNarration("Accrual - ");
        setReference("");
        setLines([blankLine(), blankLine()]);
      },
    },
    {
      label: "Reversal",
      apply: () => {
        setNarration("Reversal of: ");
        setReference("Reverses journal ID ");
        setLines([blankLine(), blankLine()]);
      },
    },
    {
      label: "Year-end closing (revenue→RE)",
      apply: () => {
        setNarration("Close revenue to Retained Earnings");
        setReference("Year-end closing entries");
        setLines([
          { accountCode: findCode("500010") || "", dr: 0, cr: 0 },
          { accountCode: findCode("300020") || "", dr: 0, cr: 0 },
        ]);
      },
    },
  ];

  const onPost = async () => {
    if (!canPost) return;
    setPosting(true);
    setStatus("Posting journal...");
    try {
      await postJournal(project.id, {
        date,
        narration: narration.trim(),
        source: { type: "manual", reference: reference.trim() || null },
        lines: lines.map((l) => {
          const acc = coa.find((a) => a.code === l.accountCode);
          return {
            accountCode: l.accountCode,
            accountName: acc ? acc.name : "",
            dr: Number(l.dr) || 0,
            cr: Number(l.cr) || 0,
          };
        }),
      });
      setStatus("Posted. Reports and Journals view will refresh.");
      reset();
      if (onPosted) await onPosted();
    } catch (err) {
      setStatus("Post failed: " + err.message);
    }
    setPosting(false);
  };

  const labelStyle = { fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 };
  const inputStyle = { width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, outline: "none", background: "#fff" };

  return (
    <div>
      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>New Manual Journal</div>
          <div style={{ display: "flex", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#9a9488", alignSelf: "center" }}>Templates:</span>
            {templates.map((t) => (
              <button key={t.label} onClick={t.apply} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer", color: "#1a1a2e" }}>{t.label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 1fr", gap: 10, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Narration *</label>
            <input value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="e.g. Depreciation for May 2025" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Reference (optional)</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Depreciation schedule, invoice #" style={inputStyle} />
          </div>
        </div>

        <div style={{ border: "1px solid #eceae4", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px 60px", background: "#1a1a2e", color: "#c8a24e", fontSize: 11, padding: "7px 10px", fontWeight: 600 }}>
            <div>Account</div>
            <div style={{ textAlign: "right" }}>Debit (R)</div>
            <div style={{ textAlign: "right" }}>Credit (R)</div>
            <div></div>
          </div>
          {lines.map((line, i) => (
            <div key={i} style={{ borderTop: "1px solid #f0eeea", padding: "6px 10px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px 60px", gap: 8, alignItems: "center" }}>
                <select value={line.accountCode} onChange={(e) => {
                  const code = e.target.value;
                  updateLine(i, { accountCode: code });
                }} style={{ padding: 6, fontSize: 12, border: "1px solid #e0ddd8", borderRadius: 4, background: "#fff" }}>
                  <option value="">— Select account —</option>
                  {coa.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name} ({a.type})</option>)}
                </select>
                <input type="number" step="0.01" value={line.dr || ""} onChange={(e) => updateLine(i, { dr: parseFloat(e.target.value) || 0 })} placeholder="0.00" style={{ textAlign: "right", padding: 6, fontSize: 12, border: "1px solid #e0ddd8", borderRadius: 4 }} />
                <input type="number" step="0.01" value={line.cr || ""} onChange={(e) => updateLine(i, { cr: parseFloat(e.target.value) || 0 })} placeholder="0.00" style={{ textAlign: "right", padding: 6, fontSize: 12, border: "1px solid #e0ddd8", borderRadius: 4 }} />
                <button onClick={() => removeLine(i)} disabled={lines.length <= 2} style={{ padding: "4px 8px", background: "none", border: "1px solid #e0ddd8", borderRadius: 4, color: lines.length <= 2 ? "#ccc" : "#b33", cursor: lines.length <= 2 ? "default" : "pointer", fontSize: 11 }}>Remove</button>
              </div>
              {lineErrors[i] && <div style={{ fontSize: 10, color: "#b33", marginTop: 4 }}>{lineErrors[i]}</div>}
            </div>
          ))}
          <div style={{ borderTop: "1px solid #f0eeea", padding: "6px 10px" }}>
            <button onClick={addLine} style={{ padding: "4px 12px", background: "none", border: "1px dashed #1a1a2e", borderRadius: 4, color: "#1a1a2e", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ Add line</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px 60px", padding: "8px 10px", background: "#eef0f4", fontSize: 12, fontWeight: 600, borderTop: "1px solid #d8d8d8" }}>
            <div>Totals</div>
            <div style={{ textAlign: "right" }}>R {f(totals.dr)}</div>
            <div style={{ textAlign: "right" }}>R {f(totals.cr)}</div>
            <div></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px 60px", padding: "6px 10px", background: balanced ? "#eaf3ee" : "#fff4f4", fontSize: 11, color: balanced ? "#3d7a52" : "#b33", borderTop: "1px solid #d8d8d8" }}>
            <div>{balanced ? "Balanced" : "Unbalanced — adjust before posting"}</div>
            <div style={{ textAlign: "right" }} colSpan={2}>Diff: R {f(diff)}</div>
            <div></div>
            <div></div>
          </div>
        </div>

        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={onPost} disabled={!canPost} style={{ padding: "9px 22px", borderRadius: 6, border: "none", background: canPost ? "#3d7a52" : "#ccc", color: canPost ? "#fff" : "#888", cursor: canPost ? "pointer" : "default", fontSize: 13, fontWeight: 600 }}>{posting ? "Posting..." : "Post Journal"}</button>
          <button onClick={reset} style={{ padding: "9px 18px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: "pointer", fontSize: 13 }}>Clear</button>
          {status && <span style={{ fontSize: 12, color: status.indexOf("failed") >= 0 ? "#b33" : "#3d7a52" }}>{status}</span>}
        </div>
      </div>

      <div style={{ background: "#fff8e6", border: "1px solid #f0d97f", borderRadius: 8, padding: 12, fontSize: 11, color: "#7a5b00", lineHeight: 1.5 }}>
        <strong>When to use manual journals:</strong> depreciation, accruals, prepayments, year-end closing entries, corrections, opening journals for new accounts. Anything not from a bank statement. Every entry is validated to balance before it&apos;s written to Firestore.
      </div>
    </div>
  );
}

function PriorImport({ project, coa, opening, onSaved }) {
  const [periodStart, setPeriodStart] = useState(currentSATaxYear().start);
  const [items, setItems] = useState(null); // proposed [{accountCode, accountName, type, balance, sourceLabel, confidence, notes}]
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [sourceLabel, setSourceLabel] = useState("");
  const fr = useRef(null);

  const fileToBase64Local = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("Failed to read " + file.name));
    reader.readAsDataURL(file);
  });
  const fileToBuf = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read " + file.name));
    reader.readAsArrayBuffer(file);
  });

  const onFiles = async (filesList) => {
    const file = filesList[0];
    if (!file) return;
    setItems(null);
    setWarnings([]);
    setSourceLabel(file.name);
    setLoading(true);
    setStatus("Reading " + file.name + "...");
    try {
      const name = file.name.toLowerCase();
      let result;
      if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        const buf = await fileToBuf(file);
        const text = xlsxToText(buf, XLSX);
        setStatus("Extracting closing balances from " + file.name + "...");
        result = await extractOpeningBalancesFromText({ project: { ...project, yearStart: periodStart }, coa, text, sourceLabel: file.name });
      } else if (name.endsWith(".pdf")) {
        const b64 = await fileToBase64Local(file);
        setStatus("Extracting closing balances from PDF...");
        result = await extractOpeningBalancesFromPdf({ project: { ...project, yearStart: periodStart }, coa, pdfBase64: b64, sourceLabel: file.name });
      } else if (name.endsWith(".csv") || name.endsWith(".txt")) {
        const text = await file.text();
        setStatus("Extracting closing balances from " + file.name + "...");
        result = await extractOpeningBalancesFromText({ project: { ...project, yearStart: periodStart }, coa, text, sourceLabel: file.name });
      } else {
        setStatus("Unsupported file. Use XLSX, PDF, CSV or TXT.");
        setLoading(false);
        return;
      }
      const w = validateBalances(result.balances, coa);
      setItems(result.balances);
      setWarnings(w);
      setStatus("Extracted " + result.balances.length + " balances" + (result.periodEnd ? " as at " + result.periodEnd : "") + (w.length ? " · " + w.length + " warnings" : ""));
    } catch (err) {
      setStatus("Extraction failed: " + err.message);
    }
    setLoading(false);
  };

  const updateItem = (idx, patch) => {
    setItems((prev) => {
      const arr = prev.slice();
      arr[idx] = { ...arr[idx], ...patch };
      return arr;
    });
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const addBlankRow = () => {
    setItems((prev) => (prev || []).concat([{ accountCode: coa[0] ? coa[0].code : "", accountName: coa[0] ? coa[0].name : "", type: coa[0] ? coa[0].type : "asset", balance: 0, sourceLabel: "(manual)", confidence: "high" }]));
  };

  const onSave = async () => {
    if (!items || !items.length) return;
    const w = validateBalances(items, coa);
    if (w.filter((x) => x.indexOf("unknown account code") >= 0 || x.indexOf("non-numeric") >= 0).length > 0) {
      if (!window.confirm("Some rows have hard errors. Save anyway?\n\n" + w.join("\n"))) return;
    }
    setLoading(true);
    setStatus("Saving " + items.length + " opening balances against period start " + periodStart + "...");
    try {
      const balances = toFirestoreBalances(items);
      await saveOpeningBalances(project.id, periodStart, balances);
      setStatus("Saved opening balances for " + periodStart + ". Reports will refresh.");
      setItems(null);
      setWarnings([]);
      if (onSaved) await onSaved();
    } catch (err) {
      setStatus("Save failed: " + err.message);
    }
    setLoading(false);
  };

  const totals = (items || []).reduce(
    (acc, it) => {
      const amt = Number(it.balance) || 0;
      const drNatural = ["asset", "expense", "cogs", "other_expense"].indexOf(it.type) >= 0;
      if (drNatural) acc.dr += amt; else acc.cr += amt;
      return acc;
    },
    { dr: 0, cr: 0 }
  );
  const balanceCheck = totals.dr - totals.cr;

  // Show what's already saved for this period so user knows the current state.
  const existingCount = Object.keys(opening || {}).length;

  return (
    <div>
      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>1. Upload prior period financials</div>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 10, lineHeight: 1.5 }}>
          Upload last period&apos;s Balance Sheet, Trial Balance, or full workbook as XLSX or PDF. The system will extract closing balances per account and propose them as opening balances for the new period.
          Only Asset / Liability / Equity accounts carry forward — P&amp;L accounts reset to zero each fiscal year.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input ref={fr} type="file" accept=".xlsx,.xls,.pdf,.csv,.txt" style={{ display: "none" }} onChange={(e) => { if (e.target.files && e.target.files.length) onFiles(e.target.files); e.target.value = ""; }} />
          <button onClick={() => fr.current.click()} disabled={loading} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #1a1a2e", background: loading ? "#eee" : "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, color: "#1a1a2e", fontWeight: 600 }}>Choose file...</button>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 12, color: "#666" }}>Set as opening at:</label>
            <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} style={{ padding: 6, fontSize: 12, border: "1px solid #e0ddd8", borderRadius: 4 }} />
          </div>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: "#9a9488" }}>
          {existingCount > 0
            ? existingCount + " account(s) already have opening balances saved for the period starting " + periodStart + ". Saving again will overwrite them."
            : "No opening balances saved yet for this period start."}
        </div>
        {status && <div style={{ marginTop: 10, fontSize: 12, color: "#444" }}>{status}</div>}
      </div>

      {warnings.length > 0 && (
        <div style={{ background: "#fff8e6", border: "1px solid #f0d97f", borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#7a5b00", marginBottom: 6 }}>Warnings ({warnings.length})</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: "#7a5b00" }}>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {items && (
        <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>2. Review and save ({items.length} balances)</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addBlankRow} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>+ Add row</button>
              <button onClick={onSave} disabled={loading || !items.length} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: loading ? "#ccc" : "#3d7a52", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>{loading ? "Saving..." : "Save opening balances"}</button>
            </div>
          </div>
          <div style={{ border: "1px solid #eceae4", borderRadius: 6, background: "#fff", maxHeight: 520, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead style={{ background: "#1a1a2e", color: "#c8a24e", position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>Source label</th>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, width: 260 }}>Account</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 600, width: 130 }}>Balance (signed)</th>
                  <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600, width: 90 }}>Confidence</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 600, width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f0eeea", background: it.confidence === "low" ? "#fffaf0" : "#fff" }}>
                    <td style={{ padding: "4px 8px" }}>
                      <div style={{ color: "#1a1a2e" }}>{it.sourceLabel}</div>
                      {it.notes && <div style={{ fontSize: 10, color: "#9a9488", marginTop: 2 }}>{it.notes}</div>}
                    </td>
                    <td style={{ padding: "4px 8px" }}>
                      <select value={it.accountCode} onChange={(e) => {
                        const code = e.target.value;
                        const acc = coa.find((a) => a.code === code);
                        updateItem(i, { accountCode: code, accountName: acc ? acc.name : it.accountName, type: acc ? acc.type : it.type });
                      }} style={{ width: "100%", padding: 3, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4 }}>
                        {coa.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name} ({a.type})</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>
                      <input type="number" step="0.01" value={it.balance} onChange={(e) => updateItem(i, { balance: parseFloat(e.target.value) || 0 })} style={{ width: 110, textAlign: "right", padding: 3, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4 }} />
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "center" }}>
                      <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: it.confidence === "high" ? "#dff5e1" : it.confidence === "medium" ? "#fff3c4" : "#ffd5d5", color: "#333" }}>{it.confidence || "—"}</span>
                    </td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>
                      <button onClick={() => removeItem(i)} style={{ background: "none", border: "1px solid #e0ddd8", borderRadius: 4, padding: "2px 8px", cursor: "pointer", color: "#b33", fontSize: 10 }}>Remove</button>
                    </td>
                  </tr>
                ))}
                <tr style={{ background: "#eef0f4", fontWeight: 600 }}>
                  <td style={{ padding: "6px 8px" }} colSpan={2}>Σ dr-natural − Σ cr-natural (should be 0 if balance sheet ties)</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", color: Math.abs(balanceCheck) > 0.01 ? "#b33" : "#3d7a52" }}>R {f(balanceCheck)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function JournalsBrowser({ project, coa, opening, journals, onRefresh, canWrite = true, canLock = true }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [text, setText] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [deleting, setDeleting] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [lockDate, setLockDateState] = useState(null);
  const [savingLock, setSavingLock] = useState(false);

  useEffect(() => {
    let live = true;
    loadLedgerSettings(project.id).then((s) => { if (live) setLockDateState((s && s.lockDate) || null); }).catch(() => {});
    return () => { live = false; };
  }, [project.id]);

  // IDs of journals that have been reversed (an append-only correction marker).
  const reversedIds = new Set((journals || []).filter((j) => j.reverses).map((j) => j.reverses));

  const min = minAmount === "" ? null : parseFloat(minAmount);
  const max = maxAmount === "" ? null : parseFloat(maxAmount);
  const textLower = text.trim().toLowerCase();

  const filtered = (journals || []).filter((j) => {
    if (from && j.date < from) return false;
    if (to && j.date > to) return false;
    if (accountCode && !(j.lines || []).some((l) => l.accountCode === accountCode)) return false;
    if (textLower) {
      const narr = (j.narration || "").toLowerCase();
      const src = ((j.source && j.source.description) || "").toLowerCase();
      const notes = (j.notes || "").toLowerCase();
      if (narr.indexOf(textLower) < 0 && src.indexOf(textLower) < 0 && notes.indexOf(textLower) < 0) return false;
    }
    const total = (j.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
    if (min != null && !isNaN(min) && total < min) return false;
    if (max != null && !isNaN(max) && total > max) return false;
    return true;
  });

  const sorted = filtered.slice().sort((a, b) => {
    if (sortBy === "date_desc") return (b.date || "").localeCompare(a.date || "");
    if (sortBy === "date_asc") return (a.date || "").localeCompare(b.date || "");
    if (sortBy === "amount_desc") {
      const ta = (a.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
      const tb = (b.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
      return tb - ta;
    }
    if (sortBy === "amount_asc") {
      const ta = (a.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
      const tb = (b.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
      return ta - tb;
    }
    return 0;
  });

  const totalAmount = filtered.reduce((s, j) => s + (j.lines || []).reduce((ss, l) => ss + (Number(l.dr) || 0), 0), 0);

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  // Append-only ledger: you reverse an entry (post an offsetting entry), never delete it.
  const onReverse = async (id) => {
    const reason = window.prompt("Reason for reversing this entry? (recorded in the audit trail)");
    if (reason === null) return; // cancelled
    setDeleting(id);
    try {
      await reverseJournal(project.id, id, reason);
      if (onRefresh) await onRefresh();
    } catch (err) {
      alert("Reverse failed: " + err.message);
    }
    setDeleting("");
  };

  const applyLock = async (d) => {
    setSavingLock(true);
    try { await setLockDate(project.id, d || null); setLockDateState(d || null); }
    catch (e) { alert("Could not update the lock: " + e.message); }
    setSavingLock(false);
  };

  const clearFilters = () => {
    setFrom(""); setTo(""); setAccountCode(""); setText(""); setMinAmount(""); setMaxAmount("");
  };

  const onExportFiltered = () => {
    if (!sorted.length) return;
    exportSingle("journals", coa, opening || {}, sorted, project.name, from || "", to || "");
  };

  const accountLabel = (code) => {
    const acc = coa.find((a) => a.code === code);
    return acc ? acc.code + " — " + acc.name : code;
  };

  if (!journals.length) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: "#9a9488" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>&#9998;</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#5a5a5a" }}>No journals posted yet</div>
        <div style={{ fontSize: 13, marginTop: 8 }}>Use the Process Statement tab to upload a bank statement.</div>
      </div>
    );
  }

  const filterStyle = { padding: "6px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, background: "#fff" };

  return (
    <div>
      <div style={{ background: "#eef0f4", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Date</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={filterStyle} />
            <span style={{ color: "#9a9488", fontSize: 12 }}>to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={filterStyle} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Account</label>
            <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)} style={{ ...filterStyle, minWidth: 200 }}>
              <option value="">All accounts</option>
              {coa.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Search</label>
            <input type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder="narration / source / notes" style={{ ...filterStyle, flex: 1 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Amount</label>
            <input type="number" step="0.01" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="min" style={{ ...filterStyle, width: 90 }} />
            <span style={{ color: "#9a9488", fontSize: 12 }}>–</span>
            <input type="number" step="0.01" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} placeholder="max" style={{ ...filterStyle, width: 90 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Sort</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={filterStyle}>
              <option value="date_desc">Newest first</option>
              <option value="date_asc">Oldest first</option>
              <option value="amount_desc">Amount: high to low</option>
              <option value="amount_asc">Amount: low to high</option>
            </select>
          </div>
          <button onClick={clearFilters} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>Clear filters</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 10, paddingTop: 10, borderTop: "1px solid #d8d8d8" }}>
          <div style={{ fontSize: 12, color: "#1a1a2e", fontWeight: 600 }}>{sorted.length} of {journals.length} journals</div>
          <div style={{ fontSize: 12, color: "#1a1a2e" }}>Total: R {f(totalAmount)}</div>
          <button onClick={onExportFiltered} disabled={!sorted.length} style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: 6, border: "none", background: sorted.length ? "#1a1a2e" : "#ccc", color: sorted.length ? "#c8a24e" : "#888", fontSize: 12, fontWeight: 600, cursor: sorted.length ? "pointer" : "default" }}>Export filtered ({sorted.length})</button>
          <button onClick={onRefresh} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>Refresh</button>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "12px 0", padding: "8px 12px", background: lockDate ? "#fff8e6" : "#f4f6f8", border: "1px solid " + (lockDate ? "#f0d97f" : "#e0ddd8"), borderRadius: 8, fontSize: 12, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700, color: "#1a1a2e" }}>🔒 Period lock</span>
        {lockDate
          ? <span style={{ color: "#7a5b00" }}>Entries on/before <strong>{lockDate}</strong> are locked — they can't be posted, changed, or reversed.</span>
          : <span style={{ color: "#9a9488" }}>No lock set — closed periods can still be altered.</span>}
        {canLock ? (
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#666" }}>Lock through:</span>
            <input type="date" value={lockDate || ""} onChange={(e) => applyLock(e.target.value)} disabled={savingLock} style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12 }} />
            {lockDate && <button onClick={() => applyLock(null)} disabled={savingLock} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>Unlock</button>}
          </span>
        ) : (
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#9a9488" }}>Only a reviewer or admin can change the lock.</span>
        )}
      </div>

      <div style={{ border: "1px solid #e8e4dc", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
        <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 280px 110px 110px 80px", background: "#1a1a2e", color: "#c8a24e", fontSize: 11, padding: "8px 12px", fontWeight: 600 }}>
          <div>Date</div><div>Narration</div><div>Accounts (codes)</div><div style={{ textAlign: "right" }}>Total Dr</div><div style={{ textAlign: "right" }}>Total Cr</div><div style={{ textAlign: "right" }}>Actions</div>
        </div>
        {sorted.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "#9a9488", fontSize: 13 }}>No journals match the current filters.</div>
        )}
        {sorted.map((j) => {
          const totalDr = (j.lines || []).reduce((s, l) => s + (Number(l.dr) || 0), 0);
          const totalCr = (j.lines || []).reduce((s, l) => s + (Number(l.cr) || 0), 0);
          const codes = (j.lines || []).map((l) => l.accountCode).join(", ");
          const isOpen = expanded.has(j.id);
          return (
            <div key={j.id} style={{ borderTop: "1px solid #f0eeea" }}>
              <div onClick={() => toggleExpand(j.id)} style={{ display: "grid", gridTemplateColumns: "100px 1fr 280px 110px 110px 80px", padding: "7px 12px", fontSize: 12, cursor: "pointer", background: isOpen ? "#faf9f6" : "#fff" }}>
                <div>{j.date}</div>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={j.narration}>
                  {reversedIds.has(j.id) && <span style={{ fontSize: 9, background: "#fde8e8", color: "#b33", border: "1px solid #f0c0c0", borderRadius: 8, padding: "1px 6px", marginRight: 6 }}>REVERSED</span>}
                  {j.reverses && <span style={{ fontSize: 9, background: "#eef0f4", color: "#555", border: "1px solid #d8d8e0", borderRadius: 8, padding: "1px 6px", marginRight: 6 }}>↩ REVERSAL</span>}
                  <span style={{ textDecoration: reversedIds.has(j.id) ? "line-through" : "none", color: reversedIds.has(j.id) ? "#999" : undefined }}>{j.narration}</span>
                </div>
                <div style={{ color: "#666", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={codes}>{codes}</div>
                <div style={{ textAlign: "right" }}>R {f(totalDr)}</div>
                <div style={{ textAlign: "right" }}>R {f(totalCr)}</div>
                <div style={{ textAlign: "right" }}>
                  {canWrite && !j.reverses && !reversedIds.has(j.id) ? (
                    <button onClick={(e) => { e.stopPropagation(); onReverse(j.id); }} disabled={deleting === j.id} title="Post a reversing entry (append-only — the original is kept)" style={{ background: "none", border: "1px solid #e0ddd8", borderRadius: 4, padding: "2px 8px", cursor: deleting === j.id ? "default" : "pointer", color: "#b33", fontSize: 11 }}>{deleting === j.id ? "..." : "↩ Reverse"}</button>
                  ) : (
                    <span style={{ fontSize: 11, color: "#bbb" }}>—</span>
                  )}
                </div>
              </div>
              {isOpen && (
                <div style={{ padding: "10px 16px 14px", background: "#faf9f6", fontSize: 12, borderTop: "1px solid #eceae4" }}>
                  {j.source && j.source.description && (
                    <div style={{ marginBottom: 6, fontSize: 11, color: "#666" }}>
                      <span style={{ fontWeight: 600 }}>Source:</span> {j.source.description} {j.source.amount != null ? "· R " + f(j.source.amount) : ""} {j.source.file ? "· " + j.source.file : ""}
                    </div>
                  )}
                  {j.confidence && (
                    <div style={{ marginBottom: 6, fontSize: 11, color: "#666" }}>
                      <span style={{ fontWeight: 600 }}>Confidence:</span> {j.confidence}{j.notes ? " · " + j.notes : ""}
                    </div>
                  )}
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: "#eef0f4", color: "#7a7568" }}>
                        <th style={{ textAlign: "left", padding: "4px 8px" }}>Account</th>
                        <th style={{ textAlign: "right", padding: "4px 8px", width: 120 }}>Debit</th>
                        <th style={{ textAlign: "right", padding: "4px 8px", width: 120 }}>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(j.lines || []).map((l, li) => (
                        <tr key={li} style={{ borderBottom: "1px solid #eceae4" }}>
                          <td style={{ padding: "4px 8px" }}>{accountLabel(l.accountCode)}</td>
                          <td style={{ padding: "4px 8px", textAlign: "right" }}>{l.dr ? "R " + f(l.dr) : ""}</td>
                          <td style={{ padding: "4px 8px", textAlign: "right" }}>{l.cr ? "R " + f(l.cr) : ""}</td>
                        </tr>
                      ))}
                      <tr style={{ background: "#eef0f4", fontWeight: 600 }}>
                        <td style={{ padding: "4px 8px" }}>Totals</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>R {f(totalDr)}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>R {f(totalCr)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Report previews ──────────────────────────────────────────────────────────
const MONTH_LABEL = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function monthLabel(ym) { const [y, m] = ym.split('-'); return MONTH_LABEL[parseInt(m, 10) - 1] + ' ' + y; }

const sectionTitle = { background: "#1a1a2e", color: "#c8a24e", fontSize: 11, padding: "8px 12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 };
const sheetTable = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const cellLeft = { padding: "6px 12px", borderBottom: "1px solid #eceae4" };
const cellRight = { padding: "6px 12px", borderBottom: "1px solid #eceae4", textAlign: "right" };
const subtotalRow = { background: "#eef0f4", fontWeight: 600 };
const totalRow = { background: "#1a1a2e", color: "#c8a24e", fontWeight: 700 };

function PreviewPnL({ coa, journals }) {
  const pl = profitAndLoss(coa, journals);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (code) => { const n = new Set(expanded); if (n.has(code)) n.delete(code); else n.add(code); setExpanded(n); };

  // Per-account contributing transactions (the journal lines that make up each line item).
  const txByCode = {};
  (journals || []).forEach((j) => {
    (j.lines || []).forEach((l) => {
      if (!l.accountCode) return;
      (txByCode[l.accountCode] = txByCode[l.accountCode] || []).push({
        date: j.date,
        desc: (j.source && j.source.description) || j.narration || "",
        dr: Number(l.dr) || 0,
        cr: Number(l.cr) || 0,
      });
    });
  });
  const sideOf = (code) => {
    const acc = coa.find((a) => a.code === code);
    return acc && ACCOUNT_TYPES[acc.type] ? ACCOUNT_TYPES[acc.type].side : "dr";
  };
  const contrib = (code, l) => (sideOf(code) === "cr" ? l.cr - l.dr : l.dr - l.cr);

  const renderSection = (title, items, total) => (
    <>
      <tr><td colSpan={3} style={sectionTitle}>{title}</td></tr>
      {items.length === 0 && <tr><td colSpan={3} style={{ ...cellLeft, fontStyle: "italic", color: "#9a9488" }}>No entries</td></tr>}
      {items.map((e, i) => {
        const txs = (txByCode[e.code] || []).slice().sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
        const isOpen = expanded.has(e.code);
        return (
          <React.Fragment key={i}>
            <tr onClick={() => txs.length && toggle(e.code)} style={{ cursor: txs.length ? "pointer" : "default" }}>
              <td style={cellLeft}>{e.code}</td>
              <td style={cellLeft}>{txs.length ? (isOpen ? "▾ " : "▸ ") : ""}{e.name}{txs.length ? <span style={{ color: "#9a9488", fontSize: 11 }}> ({txs.length})</span> : null}</td>
              <td style={cellRight}>R {f(e.amount)}</td>
            </tr>
            {isOpen && txs.map((t, ti) => (
              <tr key={ti} style={{ background: "#fbfaf7" }}>
                <td style={{ ...cellLeft, fontSize: 11, color: "#9a9488" }}>{t.date}</td>
                <td style={{ ...cellLeft, fontSize: 11, color: "#6a6a6a", paddingLeft: 26 }}>{t.desc}</td>
                <td style={{ ...cellRight, fontSize: 11, color: "#6a6a6a" }}>{fS(contrib(e.code, t))}</td>
              </tr>
            ))}
          </React.Fragment>
        );
      })}
      <tr style={subtotalRow}><td style={cellLeft}></td><td style={cellLeft}>Total {title.replace(/^Less: /, "").replace(/^Plus: /, "")}</td><td style={cellRight}>R {f(total)}</td></tr>
    </>
  );
  return (
    <table style={sheetTable}>
      <thead>
        <tr style={{ background: "#f4f2ed", color: "#7a7568" }}>
          <th style={{ ...cellLeft, fontWeight: 600, width: 90 }}>Code</th>
          <th style={{ ...cellLeft, fontWeight: 600 }}>Account</th>
          <th style={{ ...cellRight, fontWeight: 600, width: 140 }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {renderSection("Revenue", pl.sections.revenue, pl.rev)}
        {renderSection("Less: Cost of Revenue", pl.sections.cogs, pl.cogs)}
        <tr style={totalRow}><td style={cellLeft}></td><td style={cellLeft}>Gross Profit / (Loss)</td><td style={{ ...cellRight, color: pl.gp < 0 ? "#b33" : undefined }}>{fS(pl.gp)}</td></tr>
        {renderSection("Less: Operating Expenses", pl.sections.expense, pl.opex)}
        <tr style={totalRow}><td style={cellLeft}></td><td style={cellLeft}>Operating Income (or Loss)</td><td style={{ ...cellRight, color: pl.opInc < 0 ? "#b33" : undefined }}>{fS(pl.opInc)}</td></tr>
        {pl.sections.other_income.length > 0 && renderSection("Plus: Other Income", pl.sections.other_income, pl.oi)}
        {pl.sections.other_expense.length > 0 && renderSection("Less: Other Expenses", pl.sections.other_expense, pl.oe)}
        <tr style={totalRow}><td style={cellLeft}></td><td style={cellLeft}>Net Profit / (Loss)</td><td style={{ ...cellRight, color: pl.np < 0 ? "#b33" : undefined }}>{fS(pl.np)}</td></tr>
      </tbody>
    </table>
  );
}

function PreviewBS({ coa, opening, journals }) {
  const bs = balanceSheet(coa, opening || {}, journals);
  const section = (title, items, total) => (
    <>
      <tr><td colSpan={3} style={sectionTitle}>{title}</td></tr>
      {items.length === 0 && <tr><td colSpan={3} style={{ ...cellLeft, fontStyle: "italic", color: "#9a9488" }}>No entries</td></tr>}
      {items.map((e, i) => (
        <tr key={i}><td style={cellLeft}>{e.code}</td><td style={cellLeft}>{e.name}</td><td style={{ ...cellRight, color: e.amount < 0 ? "#b33" : undefined }}>{fS(e.amount)}</td></tr>
      ))}
      <tr style={subtotalRow}><td style={cellLeft}></td><td style={cellLeft}>Total {title}</td><td style={cellRight}>{fS(total)}</td></tr>
    </>
  );
  return (
    <table style={sheetTable}>
      <thead>
        <tr style={{ background: "#f4f2ed", color: "#7a7568" }}>
          <th style={{ ...cellLeft, fontWeight: 600, width: 90 }}>Code</th>
          <th style={{ ...cellLeft, fontWeight: 600 }}>Account</th>
          <th style={{ ...cellRight, fontWeight: 600, width: 140 }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {section("Assets", bs.assets, bs.totalAssets)}
        {section("Liabilities", bs.liabilities, bs.totalLiabilities)}
        {section("Equity", bs.equity, bs.totalEquity)}
        <tr style={totalRow}><td style={cellLeft}></td><td style={cellLeft}>Liabilities + Equity</td><td style={cellRight}>{fS(bs.totalLiabilities + bs.totalEquity)}</td></tr>
        <tr style={{ ...subtotalRow, color: Math.abs(bs.diff) > 0.01 ? "#b33" : "#3d7a52" }}><td style={cellLeft}></td><td style={cellLeft}>Difference (Assets − (L + E)) — should be 0</td><td style={cellRight}>{fS(bs.diff)}</td></tr>
      </tbody>
    </table>
  );
}

function PreviewTB({ coa, opening, journals }) {
  const tb = trialBalance(coa, opening || {}, journals);
  return (
    <table style={sheetTable}>
      <thead>
        <tr style={{ background: "#1a1a2e", color: "#c8a24e" }}>
          <th style={{ ...cellLeft, color: "#c8a24e", width: 90 }}>Code</th>
          <th style={{ ...cellLeft, color: "#c8a24e" }}>Account</th>
          <th style={{ ...cellLeft, color: "#c8a24e", width: 110 }}>Type</th>
          <th style={{ ...cellRight, color: "#c8a24e", width: 130 }}>Debit</th>
          <th style={{ ...cellRight, color: "#c8a24e", width: 130 }}>Credit</th>
        </tr>
      </thead>
      <tbody>
        {tb.rows.map((r, i) => (
          <tr key={i}>
            <td style={cellLeft}>{r.code}</td>
            <td style={cellLeft}>{r.name}</td>
            <td style={{ ...cellLeft, fontSize: 11, color: "#9a9488" }}>{r.type}</td>
            <td style={cellRight}>{r.dr ? "R " + f(r.dr) : ""}</td>
            <td style={cellRight}>{r.cr ? "R " + f(r.cr) : ""}</td>
          </tr>
        ))}
        <tr style={totalRow}>
          <td style={cellLeft}></td>
          <td style={cellLeft}>TOTALS</td>
          <td style={cellLeft}></td>
          <td style={cellRight}>R {f(tb.totalDr)}</td>
          <td style={cellRight}>R {f(tb.totalCr)}</td>
        </tr>
        <tr style={{ ...subtotalRow, color: Math.abs(tb.diff) > 0.01 ? "#b33" : "#3d7a52" }}>
          <td style={cellLeft}></td>
          <td style={cellLeft}>Difference (should be 0)</td>
          <td colSpan={2} style={cellLeft}></td>
          <td style={cellRight}>R {f(tb.diff)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function PreviewVAT201({ coa, journals, rate, registered }) {
  const v = vatSummary(coa, journals, rate);
  const payable = v.net >= 0;
  return (
    <div>
      {!registered && (
        <div style={{ background: "#fff7e6", border: "1px solid #f0d9a8", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#7a5a1a" }}>
          This client has no VAT number on file (not VAT registered) — the figures below are an estimate only.
        </div>
      )}
      <table style={sheetTable}>
        <thead>
          <tr style={{ background: "#1a1a2e", color: "#c8a24e" }}>
            <th style={{ ...cellLeft, color: "#c8a24e" }}>VAT201 (VAT @ {v.rate}%)</th>
            <th style={{ ...cellRight, color: "#c8a24e", width: 150 }}>Incl.</th>
            <th style={{ ...cellRight, color: "#c8a24e", width: 150 }}>VAT</th>
          </tr>
        </thead>
        <tbody>
          <tr style={subtotalRow}><td style={cellLeft}>Output VAT — standard-rated sales</td><td style={cellRight}>R {f(v.standardSales)}</td><td style={cellRight}>R {f(v.totalOutput)}</td></tr>
          <tr style={subtotalRow}><td style={cellLeft}>Input VAT — deductible purchases</td><td style={cellRight}>R {f(v.inputPurchases)}</td><td style={cellRight}>(R {f(v.totalInput)})</td></tr>
          <tr style={{ ...totalRow, color: payable ? "#b33" : "#3d7a52" }}><td style={cellLeft}>Net VAT {payable ? "payable to SARS" : "refundable"}</td><td style={cellLeft}></td><td style={cellRight}>R {f(v.net)}</td></tr>
        </tbody>
      </table>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a2e", marginBottom: 6 }}>Output VAT (sales)</div>
          <table style={sheetTable}><tbody>
            {v.output.length ? v.output.map((e, i) => (
              <tr key={i}><td style={cellLeft}>{e.code} {e.name}</td><td style={cellRight}>R {f(e.amount)}</td><td style={cellRight}>R {f(e.vat)}</td></tr>
            )) : <tr><td style={cellLeft} colSpan={3}>No standard-rated income</td></tr>}
          </tbody></table>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a2e", marginBottom: 6 }}>Input VAT (purchases)</div>
          <table style={sheetTable}><tbody>
            {v.input.length ? v.input.map((e, i) => (
              <tr key={i}><td style={cellLeft}>{e.code} {e.name}</td><td style={cellRight}>R {f(e.amount)}</td><td style={cellRight}>R {f(e.vat)}</td></tr>
            )) : <tr><td style={cellLeft} colSpan={3}>No claimable input VAT</td></tr>}
          </tbody></table>
        </div>
      </div>

      {v.excluded.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a2e", marginBottom: 6 }}>Excluded / denied</div>
          <table style={sheetTable}><tbody>
            {v.excluded.map((e, i) => (
              <tr key={i}><td style={cellLeft}>{e.code} {e.name}</td><td style={cellRight}>R {f(e.amount)}</td><td style={{ ...cellLeft, fontSize: 11, color: "#9a9488" }}>{e.reason}</td></tr>
            ))}
          </tbody></table>
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: 11, color: "#9a9488" }}>
        Estimate only: assumes amounts are VAT-inclusive and revenue is standard-rated. Input VAT is claimed on deductible accounts only (e.g. entertainment denied; interest/refunds out of scope). Verify against actual tax invoices before submitting a VAT201.
      </div>
    </div>
  );
}

function PreviewGL({ coa, opening, journals }) {
  const gl = generalLedger(coa, opening || {}, journals);
  return (
    <div>
      {gl.length === 0 && <div style={{ padding: 20, color: "#9a9488", textAlign: "center" }}>No ledger entries.</div>}
      {gl.map((g) => (
        <div key={g.code} style={{ marginBottom: 18, border: "1px solid #eceae4", borderRadius: 6, overflow: "hidden" }}>
          <div style={sectionTitle}>{g.code} — {g.name} <span style={{ fontWeight: 400, color: "#8a8a9a", marginLeft: 8 }}>({g.type})</span></div>
          <table style={sheetTable}>
            <thead>
              <tr style={{ background: "#f4f2ed", color: "#7a7568" }}>
                <th style={{ ...cellLeft, fontWeight: 600, width: 100 }}>Date</th>
                <th style={{ ...cellLeft, fontWeight: 600 }}>Narration</th>
                <th style={{ ...cellLeft, fontWeight: 600 }}>Source</th>
                <th style={{ ...cellRight, fontWeight: 600, width: 120 }}>Debit</th>
                <th style={{ ...cellRight, fontWeight: 600, width: 120 }}>Credit</th>
                <th style={{ ...cellRight, fontWeight: 600, width: 130 }}>Running</th>
              </tr>
            </thead>
            <tbody>
              {g.lines.map((l, i) => (
                <tr key={i}>
                  <td style={cellLeft}>{l.date}</td>
                  <td style={cellLeft}>{l.desc}</td>
                  <td style={{ ...cellLeft, color: "#9a9488", fontSize: 11 }}>{l.narration}</td>
                  <td style={cellRight}>{l.dr ? "R " + f(l.dr) : ""}</td>
                  <td style={cellRight}>{l.cr ? "R " + f(l.cr) : ""}</td>
                  <td style={cellRight}>R {f(l.running)}</td>
                </tr>
              ))}
              <tr style={subtotalRow}>
                <td colSpan={5} style={cellLeft}>Closing</td>
                <td style={cellRight}>R {f(g.closing)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function PreviewJournals({ journals, coa }) {
  if (!journals.length) return <div style={{ padding: 20, color: "#9a9488", textAlign: "center" }}>No journals in range.</div>;
  const accountLabel = (code) => { const a = (coa || []).find((x) => x.code === code); return a ? a.code + " — " + a.name : code; };
  return (
    <table style={sheetTable}>
      <thead>
        <tr style={{ background: "#1a1a2e", color: "#c8a24e" }}>
          <th style={{ ...cellLeft, color: "#c8a24e", width: 100 }}>Date</th>
          <th style={{ ...cellLeft, color: "#c8a24e" }}>Narration / Account</th>
          <th style={{ ...cellLeft, color: "#c8a24e" }}>Source</th>
          <th style={{ ...cellRight, color: "#c8a24e", width: 130 }}>Debit</th>
          <th style={{ ...cellRight, color: "#c8a24e", width: 130 }}>Credit</th>
        </tr>
      </thead>
      <tbody>
        {journals.map((j) => (
          <React.Fragment key={j.id || j.invoiceNumber || j.date + j.narration}>
            <tr style={{ background: "#faf9f6", borderTop: "2px solid #e0ddd8" }}>
              <td style={{ ...cellLeft, fontWeight: 600 }}>{j.date}</td>
              <td style={{ ...cellLeft, fontWeight: 600 }} colSpan={4}>{j.narration}</td>
            </tr>
            {(j.lines || []).map((l, li) => (
              <tr key={li}>
                <td style={cellLeft}></td>
                <td style={{ ...cellLeft, paddingLeft: 30 }}>{accountLabel(l.accountCode)}</td>
                <td style={{ ...cellLeft, color: "#9a9488", fontSize: 11 }}>{li === 0 && j.source && j.source.description ? j.source.description : ""}</td>
                <td style={cellRight}>{l.dr ? "R " + f(l.dr) : ""}</td>
                <td style={cellRight}>{l.cr ? "R " + f(l.cr) : ""}</td>
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

function PreviewBobCorp({ coa, journals }) {
  const months = monthsCovered(journals);
  return (
    <div>
      {months.length === 0 && <div style={{ padding: 20, color: "#9a9488", textAlign: "center" }}>No journals in this range.</div>}
      {months.map((ym) => {
        const monthJournals = journalsForMonth(journals, ym);
        return (
          <div key={ym} style={{ marginBottom: 18, border: "1px solid #eceae4", borderRadius: 6, overflow: "hidden" }}>
            <div style={sectionTitle}>{monthLabel(ym)} — {monthJournals.length} journals</div>
            <PreviewPnL coa={coa} journals={monthJournals} />
          </div>
        );
      })}
      {months.length > 0 && (
        <div style={{ marginBottom: 18, border: "2px solid #1a1a2e", borderRadius: 6, overflow: "hidden" }}>
          <div style={{ ...sectionTitle, background: "#c8a24e", color: "#1a1a2e" }}>CUMULATIVE — {journals.length} journals</div>
          <PreviewPnL coa={coa} journals={journals} />
        </div>
      )}
    </div>
  );
}

function PreviewFull({ coa, opening, journals }) {
  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <div style={sectionTitle}>PROFIT &amp; LOSS</div>
        <PreviewPnL coa={coa} journals={journals} />
      </div>
      <div style={{ marginBottom: 22 }}>
        <div style={sectionTitle}>BALANCE SHEET</div>
        <PreviewBS coa={coa} opening={opening} journals={journals} />
      </div>
      <div style={{ marginBottom: 22 }}>
        <div style={sectionTitle}>TRIAL BALANCE</div>
        <PreviewTB coa={coa} opening={opening} journals={journals} />
      </div>
      <div style={{ marginBottom: 22 }}>
        <div style={sectionTitle}>GENERAL LEDGER</div>
        <PreviewGL coa={coa} opening={opening} journals={journals} />
      </div>
      <div style={{ marginBottom: 22 }}>
        <div style={sectionTitle}>JOURNAL ENTRIES</div>
        <PreviewJournals coa={coa} journals={journals} />
      </div>
    </div>
  );
}

function Assistant({ project, coa, journals, onRefresh, canWrite = true }) {
  const [history, setHistory] = useState([]);   // [{ role, content }]
  const [pending, setPending] = useState(null); // { summary, operations }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [rules, setRules] = useState({});
  const scroller = useRef(null);

  useEffect(() => {
    let live = true;
    loadCategorisationRules(project.id).then((r) => { if (live) setRules(r || {}); }).catch(() => {});
    return () => { live = false; };
  }, [project.id]);

  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [history, pending, loading]);

  const coaByCode = {};
  coa.forEach((a) => { coaByCode[a.code] = a.name; });
  const byId = {};
  (journals || []).forEach((j) => { byId[j.id] = j; });

  const isBank = (code) => /^100/.test(String(code || ""));
  const balanceOf = (lines) => (lines || []).reduce((a, l) => ({ dr: a.dr + (Number(l.dr) || 0), cr: a.cr + (Number(l.cr) || 0) }), { dr: 0, cr: 0 });
  const lineSummary = (lines) => (lines || []).map((l) => {
    const nm = l.accountName || coaByCode[l.accountCode] || "";
    const amt = (Number(l.dr) || 0) > 0 ? "Dr R" + f(l.dr) : "Cr R" + f(l.cr);
    return l.accountCode + " " + nm + " " + amt;
  }).join("   ·   ");
  const cleanLines = (lines) => (lines || []).map((l) => ({
    accountCode: l.accountCode,
    accountName: l.accountName || coaByCode[l.accountCode] || "",
    dr: Number(l.dr) || 0,
    cr: Number(l.cr) || 0,
  }));

  const send = async (preset) => {
    const text = (preset != null ? preset : input).trim();
    if (!text || loading || applying) return;
    const nextHist = [...history, { role: "user", content: text }];
    setHistory(nextHist); setInput(""); setLoading(true); setError(""); setPending(null);
    try {
      const res = await askAssistant({ project, coa, journals, history: nextHist });
      const ops = (res.operations || []).filter((o) => o && o.op);
      const newAccounts = (res.newAccounts || []).filter((a) => a && a.code && a.name && a.type);
      if (ops.length || newAccounts.length) {
        setPending({ summary: res.summary || "", operations: ops, newAccounts });
        const bits = [];
        if (newAccounts.length) bits.push(newAccounts.length + " new account(s)");
        if (ops.length) bits.push(ops.length + " journal change(s)");
        setHistory([...nextHist, { role: "assistant", content: res.text || ("I've proposed " + bits.join(" and ") + " below — review and apply when ready.") }]);
      } else {
        setHistory([...nextHist, { role: "assistant", content: res.text || "(no response)" }]);
      }
    } catch (e) {
      setError(e.message);
      setHistory((h) => [...h, { role: "assistant", content: "⚠️ " + e.message }]);
    }
    setLoading(false);
  };

  const apply = async () => {
    if (!pending || applying) return;
    setApplying(true); setError("");
    const ops = pending.operations || [];
    const newAccounts = pending.newAccounts || [];
    const learned = { ...rules };
    let done = 0;
    try {
      // Create any new accounts first so the journal lines below resolve to them.
      for (const a of newAccounts) {
        await upsertAccount(project.id, { code: a.code, name: a.name, type: a.type, deductible: a.deductible == null ? (a.type === "expense" || a.type === "cogs" ? true : null) : a.deductible });
        done++;
      }
      for (const op of ops) {
        if (op.op === "delete") {
          // Append-only ledger: removal = a reversing entry, never a hard delete.
          if (op.id) await reverseJournal(project.id, op.id, op.reason || "Reversed via assistant");
        } else if (op.op === "update") {
          if (!op.id) continue;
          // Correction = reverse the original, then post the corrected entry.
          const orig = byId[op.id];
          await reverseJournal(project.id, op.id, op.reason || "Correction via assistant");
          await postJournal(project.id, { date: op.date, narration: op.narration, lines: cleanLines(op.lines), source: { type: "assistant", description: (orig && orig.source && orig.source.description) || op.narration || null } });
          const desc = orig && orig.source && orig.source.description;
          const contra = cleanLines(op.lines).filter((l) => !isBank(l.accountCode));
          if (desc && contra.length === 1) {
            const key = normDesc(desc);
            if (key) learned[key] = { accountCode: contra[0].accountCode, accountName: contra[0].accountName };
          }
        } else if (op.op === "create") {
          await postJournal(project.id, { date: op.date, narration: op.narration, lines: cleanLines(op.lines), source: { type: "assistant", description: op.narration || null } });
        }
        done++;
      }
      try { await saveCategorisationRules(project.id, learned); setRules(learned); } catch (e) { /* non-fatal */ }
      await onRefresh();
      setHistory((h) => [...h, { role: "assistant", content: "✅ Applied " + done + " change(s). Your journals and financials are updated." }]);
      setPending(null);
    } catch (e) {
      setError("Apply failed after " + done + " of " + (newAccounts.length + ops.length) + " change(s): " + e.message + (done ? " (the first " + done + " were applied)." : ""));
      try { await onRefresh(); } catch (e2) { /* ignore */ }
    }
    setApplying(false);
  };

  const examples = [
    "What does the total sales / revenue figure consist of?",
    "Create a Subcontractor Costs expense account and move all transactions referenced \"Payment\" to it.",
    "Recategorise all bank fee transactions to Bank Charges (610380).",
    "Any deposit described as a director or shareholder name should be a Shareholder's Loan, not income.",
  ];
  const opColor = { update: "#c8a24e", create: "#3d7a52", delete: "#b33" };
  const opLabel = { update: "✏️ Correct (reverse + repost)", create: "➕ Create", delete: "↩ Reverse" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", maxWidth: 900, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>✦ Assistant</div>
        <div style={{ fontSize: 12, color: "#9a9488", marginTop: 2 }}>Ask questions about the books, or describe corrections — including creating new accounts. Changes (and any new accounts) are proposed for your approval before they're applied. ({(journals || []).length} journals loaded)</div>
      </div>

      <div ref={scroller} style={{ flex: 1, overflowY: "auto", border: "1px solid #e8e4dc", borderRadius: 10, background: "#faf9f6", padding: 16, minHeight: 200 }}>
        {history.length === 0 && (
          <div style={{ color: "#9a9488", fontSize: 13 }}>
            <div style={{ marginBottom: 10 }}>Try one of these, or type your own:</div>
            {examples.map((ex, i) => (
              <div key={i} onClick={() => send(ex)} style={{ cursor: "pointer", padding: "8px 12px", background: "#fff", border: "1px solid #e8e4dc", borderRadius: 8, marginBottom: 6, fontSize: 12, color: "#1a1a2e" }}>{ex}</div>
            ))}
          </div>
        )}
        {history.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 8 }}>
            <div style={{ maxWidth: "80%", padding: "8px 12px", borderRadius: 10, fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5, background: m.role === "user" ? "#1a1a2e" : "#fff", color: m.role === "user" ? "#fff" : "#2c2a26", border: m.role === "user" ? "none" : "1px solid #e8e4dc" }}>{m.content}</div>
          </div>
        ))}
        {loading && <div style={{ fontSize: 12, color: "#9a9488" }}>Thinking…</div>}

        {pending && ((pending.operations && pending.operations.length > 0) || (pending.newAccounts && pending.newAccounts.length > 0)) && (
          <div style={{ marginTop: 10, background: "#fff", border: "2px solid #c8a24e", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>Proposed changes ({(pending.newAccounts || []).length + (pending.operations || []).length})</div>
            {pending.summary && <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>{pending.summary}</div>}
            {(pending.newAccounts || []).length > 0 && (
              <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #f0eeea" }}>
                {pending.newAccounts.map((a, i) => (
                  <div key={i} style={{ padding: "4px 0" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#2563a6" }}>＋ New account · {a.code} {a.name} <span style={{ color: "#9a9488", fontWeight: 400 }}>({a.type})</span></div>
                    {a.reason && <div style={{ fontSize: 11, color: "#9a9488", fontStyle: "italic", marginTop: 2 }}>{a.reason}</div>}
                  </div>
                ))}
              </div>
            )}
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {pending.operations.map((op, i) => {
                const before = op.id ? byId[op.id] : null;
                const bal = op.op === "delete" ? null : balanceOf(op.lines);
                const unbalanced = bal && Math.abs(bal.dr - bal.cr) > 0.01;
                return (
                  <div key={i} style={{ borderTop: i ? "1px solid #f0eeea" : "none", padding: "8px 0" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: opColor[op.op] || "#1a1a2e" }}>{opLabel[op.op] || op.op}{op.date ? " · " + op.date : ""}{op.narration ? " · " + op.narration : ""}</div>
                    {op.op !== "create" && before && (
                      <div style={{ fontSize: 11, color: "#b33", marginTop: 3 }}>− {before.date} {lineSummary(before.lines)}</div>
                    )}
                    {op.op !== "delete" && (
                      <div style={{ fontSize: 11, color: "#3d7a52", marginTop: 3 }}>+ {op.date} {lineSummary(op.lines)}</div>
                    )}
                    {op.reason && <div style={{ fontSize: 11, color: "#9a9488", marginTop: 3, fontStyle: "italic" }}>{op.reason}</div>}
                    {unbalanced && <div style={{ fontSize: 11, color: "#b33", marginTop: 3 }}>⚠ Does not balance (Dr R{f(bal.dr)} vs Cr R{f(bal.cr)}) — will be rejected.</div>}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
              {canWrite && <button onClick={apply} disabled={applying} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: applying ? "#ccc" : "#3d7a52", color: "#fff", fontSize: 13, fontWeight: 700, cursor: applying ? "default" : "pointer" }}>{applying ? "Applying…" : "✓ Apply changes"}</button>}
              {!canWrite && <span style={{ fontSize: 12, color: "#9a9488" }}>You don't have permission to apply changes.</span>}
              <button onClick={() => setPending(null)} disabled={applying} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 13, cursor: "pointer" }}>Dismiss</button>
            </div>
          </div>
        )}
      </div>

      {error && <div style={{ marginTop: 8, fontSize: 12, color: "#b33" }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Describe the fix… e.g. 'Recategorise all Woolworths payments to Groceries (Enter to send, Shift+Enter for newline)'"
          rows={2}
          style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: "1px solid #e0ddd8", fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none" }}
        />
        <button onClick={() => send()} disabled={loading || applying || !input.trim()} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: loading || !input.trim() ? "#ccc" : "#1a1a2e", color: loading || !input.trim() ? "#888" : "#c8a24e", fontSize: 13, fontWeight: 700, cursor: loading || !input.trim() ? "default" : "pointer" }}>Send</button>
      </div>
    </div>
  );
}

function AuditTrail({ project }) {
  const [log, setLog] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  const load = () => { setLog(null); setError(""); loadAuditLog(project.id, 300).then(setLog).catch((e) => setError(e.message)); };
  useEffect(load, [project.id]);

  const fmtTime = (at) => {
    if (!at) return "—";
    try { const d = at.toDate ? at.toDate() : new Date(at); return d.toLocaleString("en-ZA"); } catch (e) { return "—"; }
  };
  const toggle = (id) => { const n = new Set(expanded); if (n.has(id)) n.delete(id); else n.add(id); setExpanded(n); };
  const color = { delete_journal: "#b33", update_journal: "#c8a24e", update_account: "#c8a24e", post_journals: "#3d7a52", post_journal: "#3d7a52", create_account: "#2563a6", reverse_journal: "#b33" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>▣ Audit Trail</div>
          <div style={{ fontSize: 12, color: "#9a9488", marginTop: 2 }}>An append-only record of every change to the books — who, what and when. Entries cannot be edited or deleted.</div>
        </div>
        <button onClick={load} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>Refresh</button>
      </div>

      {error && <div style={{ fontSize: 12, color: "#b33", marginBottom: 10 }}>{error}</div>}
      {log === null && !error && <div style={{ fontSize: 13, color: "#9a9488" }}>Loading…</div>}
      {log && log.length === 0 && <div style={{ fontSize: 13, color: "#9a9488" }}>No activity recorded yet. Changes from now on will appear here.</div>}

      {log && log.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead style={{ background: "#1a1a2e", color: "#c8a24e" }}>
              <tr>
                <th style={{ textAlign: "left", padding: "8px 12px", width: 170 }}>When</th>
                <th style={{ textAlign: "left", padding: "8px 12px", width: 200 }}>Who</th>
                <th style={{ textAlign: "left", padding: "8px 12px", width: 130 }}>Action</th>
                <th style={{ textAlign: "left", padding: "8px 12px" }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {log.map((e) => (
                <React.Fragment key={e.id}>
                  <tr onClick={() => e.details && toggle(e.id)} style={{ borderBottom: "1px solid #f0eeea", cursor: e.details ? "pointer" : "default" }}>
                    <td style={{ padding: "7px 12px", color: "#666" }}>{fmtTime(e.at)}</td>
                    <td style={{ padding: "7px 12px", color: "#444" }}>{e.actor}</td>
                    <td style={{ padding: "7px 12px", fontWeight: 600, color: color[e.action] || "#1a1a2e" }}>{e.action}</td>
                    <td style={{ padding: "7px 12px", color: "#1a1a2e" }}>{e.details ? (expanded.has(e.id) ? "▾ " : "▸ ") : ""}{e.summary}</td>
                  </tr>
                  {expanded.has(e.id) && e.details && (
                    <tr style={{ background: "#faf9f6" }}>
                      <td colSpan={4} style={{ padding: "8px 16px" }}>
                        <pre style={{ margin: 0, fontSize: 11, color: "#555", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(e.details, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Reconciliation({ project, coa, opening, journals }) {
  const bankOptions = coa.filter((a) => a.type === "asset" && a.code.startsWith("100"));
  const [bankCode, setBankCode] = useState(project.defaultBankCode || (bankOptions[0] && bankOptions[0].code) || "100010");
  const ty = currentSATaxYear();
  const [from, setFrom] = useState(ty.start);
  const [to, setTo] = useState(ty.end);
  const [stmtOpening, setStmtOpening] = useState("");
  const [stmtClosing, setStmtClosing] = useState("");

  const rec = bankReconciliation(coa, opening || {}, journals, bankCode, from, to);
  const openingFromStmt = stmtOpening.trim() !== "" && !isNaN(parseFloat(stmtOpening));
  const effectiveOpening = openingFromStmt ? parseFloat(stmtOpening) : rec.openingPerBooks;
  const bookClosing = effectiveOpening + rec.movement;
  const hasClosing = stmtClosing.trim() !== "" && !isNaN(parseFloat(stmtClosing));
  const actualClosing = hasClosing ? parseFloat(stmtClosing) : null;
  const diff = hasClosing ? actualClosing - bookClosing : null;
  const reconciled = diff != null && Math.abs(diff) < 0.01;

  const lbl = { fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 };
  const inp = { padding: "8px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 13, width: "100%", boxSizing: "border-box" };
  const Row = ({ l, v, strong, color }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f0eeea", fontSize: 13, fontWeight: strong ? 700 : 400 }}>
      <span style={{ color: "#555" }}>{l}</span><span style={{ color: color || "#1a1a2e" }}>{v}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>⚖ Bank Reconciliation</div>
        <div style={{ fontSize: 12, color: "#9a9488", marginTop: 2 }}>Check that every transaction on a bank statement was captured. Enter the statement's brought-forward and closing balances; the app compares them to the journals it has.</div>
      </div>

      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={lbl}>Bank account</label>
            <select value={bankCode} onChange={(e) => setBankCode(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
              {bankOptions.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Period from</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Period to</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inp} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={lbl}>Statement opening (brought forward)</label><input type="number" step="0.01" value={stmtOpening} onChange={(e) => setStmtOpening(e.target.value)} placeholder={"Books: R " + f(rec.openingPerBooks)} style={inp} /></div>
          <div><label style={lbl}>Statement closing balance *</label><input type="number" step="0.01" value={stmtClosing} onChange={(e) => setStmtClosing(e.target.value)} placeholder="from the bank statement" style={inp} /></div>
        </div>
        <div style={{ fontSize: 11, color: "#9a9488", marginTop: 8 }}>Tip: enter overdrawn balances as negative (e.g. −29.04). Leave "opening" blank to use the balance the books already have.</div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <Row l={"Journals on this account in period"} v={rec.count} />
        <Row l={"Opening balance used"} v={(openingFromStmt ? "" : "(per books) ") + "R " + f(effectiveOpening)} />
        <Row l={"＋ Money received (captured)"} v={"R " + f(rec.moneyIn)} color="#3d7a52" />
        <Row l={"－ Money paid out (captured)"} v={"R " + f(rec.moneyOut)} color="#b33" />
        <Row l={"Net movement (captured)"} v={fS(rec.movement)} />
        <Row l={"= Closing balance per books"} v={fS(bookClosing)} strong />
        {hasClosing && <Row l={"Statement closing balance"} v={fS(actualClosing)} strong />}
      </div>

      {hasClosing ? (
        reconciled ? (
          <div style={{ background: "#eef7ee", border: "1px solid #bfe0bf", borderRadius: 10, padding: 16, color: "#2f6a3d" }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>✓ Reconciled</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>The captured journals match the bank statement for this period. All transactions appear to be captured.</div>
          </div>
        ) : (
          <div style={{ background: "#fdeeee", border: "1px solid #f0c0c0", borderRadius: 10, padding: 16, color: "#b33" }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>⚠ Out by {fS(diff)}</div>
            <div style={{ fontSize: 13, marginTop: 4, color: "#8a3030" }}>
              The books and the bank statement don't agree for this period by <strong>R {f(Math.abs(diff))}</strong>. {diff < 0
                ? "The books show MORE money than the bank — so net payments (money out) are likely missing, or receipts were over-recorded."
                : "The books show LESS money than the bank — so net receipts (deposits) are likely missing, or payments were over-recorded."} Re-process this statement and check again.
            </div>
          </div>
        )
      ) : (
        <div style={{ fontSize: 12, color: "#9a9488" }}>Enter the statement's closing balance above to reconcile.</div>
      )}
    </div>
  );
}

function Reports({ project, coa, opening: openingProp, journals, onRefresh }) {
  const ty = currentSATaxYear();
  const [from, setFrom] = useState(ty.start);
  const [to, setTo] = useState(ty.end);
  const [viewingId, setViewingId] = useState(null);
  // Opening balances are an output-time concern: load the set saved as at the
  // chosen report start, so each year of assessment gets its own opening position.
  const [opening, setOpening] = useState(openingProp || {});
  useEffect(() => {
    let live = true;
    loadOpeningBalancesAsAt(project.id, from).then((op) => { if (live) setOpening(op || {}); }).catch(() => {});
    return () => { live = false; };
  }, [project.id, from]);
  const yoaOptions = listYOAOptions(journals);
  const pInfo = periodLabelFor(from, to);
  const filtered = filterJournals(journals, from, to);

  if (!journals.length) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: "#9a9488" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>&#128196;</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#5a5a5a" }}>No journals yet</div>
        <div style={{ fontSize: 13, marginTop: 8 }}>Upload a bank statement in the Process tab first.</div>
      </div>
    );
  }

  const pl = profitAndLoss(coa, filtered);
  const tb = trialBalance(coa, opening || {}, filtered);
  const vatRate = Number(project.vatRate) || 15;
  const vatRegistered = !!(project.vatNumber && String(project.vatNumber).trim());
  const reports = [
    { id: "full", label: "Full Workbook (P&L, BS, TB, GL, Journals)", desc: "All statements in one file", icon: "📖" },
    { id: "bobcorp", label: "BobCorp-style: Monthly P&Ls + Cumulative", desc: "One P&L tab per month + cumulative tab", icon: "🗓️" },
    { id: "pnl", label: "Profit and Loss", desc: "Single-period income statement", icon: "📈" },
    { id: "bs", label: "Balance Sheet", desc: "Assets, Liabilities, Equity", icon: "📊" },
    { id: "tb", label: "Trial Balance", desc: "All accounts dr/cr", icon: "📋" },
    { id: "gl", label: "General Ledger", desc: "Journal lines grouped by account", icon: "📒" },
    { id: "vat", label: "VAT201 Summary", desc: "Output/input VAT, net payable to SARS", icon: "🧾" },
    { id: "journals", label: "Journal Entries", desc: "Chronological listing of all journals", icon: "✍️" },
  ];

  const download = (id) => {
    if (id === "full") exportFullWorkbook(coa, opening || {}, journals, project.name, from, to, pInfo);
    else if (id === "bobcorp") exportBobCorpStyle(coa, journals, project.name, from, to, pInfo);
    else if (id === "vat") exportVAT201(coa, journals, project.name, from, to, vatRate, pInfo);
    else exportSingle(id, coa, opening || {}, journals, project.name, from, to, pInfo);
  };

  // ─── Preview mode ───
  if (viewingId) {
    const r = reports.find((x) => x.id === viewingId);
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <button onClick={() => setViewingId(null)} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: "pointer", fontSize: 13 }}>← Back to reports</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>{r.icon} {r.label}</div>
            <div style={{ fontSize: 11, color: "#9a9488", marginTop: 2 }}>{project.name} · {pInfo.title} · {filtered.length} journals</div>
          </div>
          <button onClick={() => download(viewingId)} style={{ padding: "9px 22px", borderRadius: 6, border: "none", background: "#1a1a2e", color: "#c8a24e", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>⤓ Download XLSX</button>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 8, padding: 18 }}>
          {viewingId === "pnl" && <PreviewPnL coa={coa} journals={filtered} />}
          {viewingId === "bs" && <PreviewBS coa={coa} opening={opening} journals={filtered} />}
          {viewingId === "tb" && <PreviewTB coa={coa} opening={opening} journals={filtered} />}
          {viewingId === "gl" && <PreviewGL coa={coa} opening={opening} journals={filtered} />}
          {viewingId === "vat" && <PreviewVAT201 coa={coa} journals={filtered} rate={vatRate} registered={vatRegistered} />}
          {viewingId === "journals" && <PreviewJournals coa={coa} journals={filtered} />}
          {viewingId === "bobcorp" && <PreviewBobCorp coa={coa} journals={filtered} />}
          {viewingId === "full" && <PreviewFull coa={coa} opening={opening} journals={filtered} />}
        </div>
      </div>
    );
  }

  // ─── List mode ───
  const tileStyle = { padding: "14px 18px", background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 10, cursor: "pointer", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" };
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, padding: "14px 18px", background: "#eef0f4", borderRadius: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>Period:</div>
        <select
          value={(yoaOptions.find((o) => o.from === from && o.to === to) || {}).from || "custom"}
          onChange={(e) => { const o = yoaOptions.find((x) => x.from === e.target.value); if (o) { setFrom(o.from); setTo(o.to); } }}
          style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, background: "#fff", cursor: "pointer", fontWeight: 600, color: "#1a1a2e" }}
        >
          {yoaOptions.map((o) => <option key={o.from} value={o.from}>{o.label} ({o.from} → {o.to})</option>)}
          <option value="custom">Custom range…</option>
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12 }} />
        <span style={{ color: "#9a9488", fontSize: 12 }}>to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12 }} />
        <div style={{ fontSize: 12, color: "#9a9488" }}>{filtered.length} journals in range</div>
        <div style={{ fontSize: 12, color: pl.np < 0 ? "#b33" : "#1a1a2e", fontWeight: 600 }}>Net Profit / (Loss): {fS(pl.np)}</div>
        <div style={{ fontSize: 12, color: Math.abs(tb.diff) > 0.01 ? "#b33" : "#3d7a52", fontWeight: 600 }}>TB diff: {fS(tb.diff)}</div>
        <button onClick={onRefresh} style={{ marginLeft: "auto", padding: "6px 12px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>Refresh</button>
      </div>
      <div style={{ fontSize: 11, color: "#9a9488", marginBottom: 10 }}>Click a report to preview it. Use the Download button on the preview to save as XLSX.</div>
      {reports.map((r) => (
        <div key={r.id} style={tileStyle} onClick={() => setViewingId(r.id)}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 24 }}>{r.icon}</span>
            <div><div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{r.label}</div><div style={{ fontSize: 11, color: "#9a9488", marginTop: 2 }}>{r.desc}</div></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={(e) => { e.stopPropagation(); setViewingId(r.id); }} style={{ padding: "6px 14px", borderRadius: 6, background: "#eef0f4", color: "#1a1a2e", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer" }}>View</button>
            <button onClick={(e) => { e.stopPropagation(); download(r.id); }} style={{ padding: "6px 14px", borderRadius: 6, background: r.id === "full" || r.id === "bobcorp" ? "#1a1a2e" : "#fff", color: r.id === "full" || r.id === "bobcorp" ? "#c8a24e" : "#1a1a2e", fontSize: 12, fontWeight: 600, border: r.id === "full" || r.id === "bobcorp" ? "none" : "1px solid #e0ddd8", cursor: "pointer" }}>⤓ Download</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const INV_ACC = { inventory: "120010", payables: "220010", cogs: "600010", vat: "210010" };

function Inventory({ project, coa, onPosted, journals }) {
  const [tab, setTab] = useState("stock");
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [movements, setMovements] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const vatRate = Number(project.vatRate) || 15;
  const vatRegistered = !!(project.vatNumber && String(project.vatNumber).trim());
  const acctName = (code) => (coa.find((a) => a.code === code) || {}).name || "";
  const today = new Date().toISOString().slice(0, 10);

  const reload = async () => {
    try {
      const [it, su, mv] = await Promise.all([loadInvItems(project.id), loadInvSuppliers(project.id), loadInvMovements(project.id)]);
      setItems(it); setSuppliers(su); setMovements(mv);
    } catch (e) { setStatus("Load failed: " + e.message); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [project.id]);

  const card = { background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16, marginBottom: 16 };
  const inp = { padding: "7px 9px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 13, width: "100%", boxSizing: "border-box" };
  const btnP = { padding: "8px 16px", borderRadius: 6, border: "none", background: busy ? "#ccc" : "#1a1a2e", color: busy ? "#888" : "#c8a24e", cursor: busy ? "default" : "pointer", fontSize: 13, fontWeight: 600, marginTop: 10 };
  const th = { padding: "6px 8px", textAlign: "left", fontSize: 11, color: "#9a9488", borderBottom: "1px solid #e8e4dc" };
  const thR = { ...th, textAlign: "right" };
  const td = { padding: "6px 8px", fontSize: 12, borderBottom: "1px solid #f0eeea" };
  const tdR = { ...td, textAlign: "right" };
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 };
  const fld = (label, node) => <div><label style={{ display: "block", fontSize: 11, color: "#9a9488", marginBottom: 3 }}>{label}</label>{node}</div>;

  const [nf, setNf] = useState({ sku: "", name: "", category: "", unit: "ea", reorderLevel: "", sellPrice: "" });
  const addItem = async () => {
    if (!nf.name.trim()) { setStatus("Item name required"); return; }
    setBusy(true);
    try {
      await addInvItem(project.id, { sku: nf.sku.trim(), name: nf.name.trim(), category: nf.category.trim(), unit: (nf.unit || "ea").trim(), reorderLevel: Number(nf.reorderLevel) || 0, sellPrice: Number(nf.sellPrice) || 0, qtyOnHand: 0, avgCost: 0 });
      setNf({ sku: "", name: "", category: "", unit: "ea", reorderLevel: "", sellPrice: "" });
      setStatus("Item added"); await reload();
    } catch (e) { setStatus("Add failed: " + e.message); }
    setBusy(false);
  };

  const [sf, setSf] = useState({ name: "", phone: "", email: "", accountNo: "" });
  const addSupplier = async () => {
    if (!sf.name.trim()) { setStatus("Supplier name required"); return; }
    setBusy(true);
    try { await addInvSupplier(project.id, { name: sf.name.trim(), phone: sf.phone.trim(), email: sf.email.trim(), accountNo: sf.accountNo.trim() }); setSf({ name: "", phone: "", email: "", accountNo: "" }); setStatus("Supplier added"); await reload(); }
    catch (e) { setStatus("Add failed: " + e.message); }
    setBusy(false);
  };

  const [rf, setRf] = useState({ itemId: "", supplierId: "", qty: "", unitCost: "", date: today, incl: true });
  const receive = async () => {
    const item = items.find((i) => i.id === rf.itemId);
    const qty = Number(rf.qty) || 0, unitCost = Number(rf.unitCost) || 0;
    if (!item || qty <= 0) { setStatus("Choose an item and a positive quantity"); return; }
    const sup = suppliers.find((s) => s.id === rf.supplierId);
    const rate = vatRate / 100;
    const inclVat = vatRegistered && rf.incl;
    let exclUnit, vatTotal, gross;
    if (inclVat) { exclUnit = r2(unitCost / (1 + rate)); gross = r2(qty * unitCost); vatTotal = r2(gross - r2(qty * exclUnit)); }
    else if (vatRegistered) { exclUnit = r2(unitCost); vatTotal = r2(r2(qty * exclUnit) * rate); gross = r2(r2(qty * exclUnit) + vatTotal); }
    else { exclUnit = r2(unitCost); vatTotal = 0; gross = r2(qty * unitCost); }
    const exclTotal = r2(qty * exclUnit);
    const newQty = r2((item.qtyOnHand || 0) + qty);
    const newAvg = newQty > 0 ? r2(((item.qtyOnHand || 0) * (item.avgCost || 0) + exclTotal) / newQty) : exclUnit;
    setBusy(true);
    try {
      const lines = [{ accountCode: INV_ACC.inventory, accountName: acctName(INV_ACC.inventory), dr: exclTotal, cr: 0 }];
      if (vatTotal > 0) lines.push({ accountCode: INV_ACC.vat, accountName: acctName(INV_ACC.vat), dr: vatTotal, cr: 0 });
      lines.push({ accountCode: INV_ACC.payables, accountName: acctName(INV_ACC.payables), dr: 0, cr: gross });
      const jid = await postJournal(project.id, { date: rf.date, narration: "Stock received: " + qty + " x " + item.name + (sup ? (" from " + sup.name) : ""), source: { type: "inventory_in" }, lines });
      await updateInvItem(project.id, item.id, { qtyOnHand: newQty, avgCost: newAvg });
      await addInvMovement(project.id, { type: "in", itemId: item.id, itemName: item.name, qty, unitCost: exclUnit, total: exclTotal, vat: vatTotal, gross, date: rf.date, supplierId: rf.supplierId || null, supplierName: sup ? sup.name : "", journalId: jid });
      setRf({ ...rf, qty: "", unitCost: "" });
      setStatus("Received " + qty + " " + (item.unit || "") + " of " + item.name + " (posted to ledger)"); await reload(); if (onPosted) onPosted();
    } catch (e) { setStatus("Receive failed: " + e.message); }
    setBusy(false);
  };

  const [isf, setIsf] = useState({ itemId: "", qty: "", note: "", date: today });
  const issue = async () => {
    const item = items.find((i) => i.id === isf.itemId);
    const qty = Number(isf.qty) || 0;
    if (!item || qty <= 0) { setStatus("Choose an item and a positive quantity"); return; }
    if (qty > (item.qtyOnHand || 0)) { setStatus("Only " + (item.qtyOnHand || 0) + " " + (item.unit || "") + " on hand"); return; }
    const cost = r2(qty * (item.avgCost || 0));
    const newQty = r2((item.qtyOnHand || 0) - qty);
    setBusy(true);
    try {
      const lines = [
        { accountCode: INV_ACC.cogs, accountName: acctName(INV_ACC.cogs), dr: cost, cr: 0 },
        { accountCode: INV_ACC.inventory, accountName: acctName(INV_ACC.inventory), dr: 0, cr: cost },
      ];
      const jid = await postJournal(project.id, { date: isf.date, narration: "Stock issued: " + qty + " x " + item.name + (isf.note ? (" — " + isf.note) : ""), source: { type: "inventory_out" }, lines });
      await updateInvItem(project.id, item.id, { qtyOnHand: newQty });
      await addInvMovement(project.id, { type: "out", itemId: item.id, itemName: item.name, qty, unitCost: item.avgCost || 0, total: cost, note: isf.note || "", date: isf.date, journalId: jid });
      setIsf({ ...isf, qty: "", note: "" });
      setStatus("Issued " + qty + " " + (item.unit || "") + " of " + item.name + " to Cost of Sales"); await reload(); if (onPosted) onPosted();
    } catch (e) { setStatus("Issue failed: " + e.message); }
    setBusy(false);
  };

  const itemOpts = [<option key="" value="">— select item —</option>].concat(
    items.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((i) => <option key={i.id} value={i.id}>{(i.sku ? i.sku + " · " : "") + i.name + " (" + (i.qtyOnHand || 0) + " " + (i.unit || "") + ")"}</option>)
  );
  const totalVal = items.reduce((s, i) => s + (i.qtyOnHand || 0) * (i.avgCost || 0), 0);
  const tabs = [["stock", "Stock"], ["suppliers", "Suppliers & AP"], ["match", "Match payments"], ["receive", "Receive"], ["issue", "Issue"], ["movements", "Movements"]];

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e0ddd8", background: tab === id ? "#1a1a2e" : "#fff", color: tab === id ? "#c8a24e" : "#1a1a2e", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>
        ))}
      </div>
      {status && <div style={{ fontSize: 12, color: "#444", marginBottom: 10 }}>{status}</div>}

      {tab === "stock" && (
        <div>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Stock on hand — {items.length} items · value R {f(totalVal)}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>SKU</th><th style={th}>Name</th><th style={th}>Category</th><th style={thR}>Qty</th><th style={thR}>Avg cost</th><th style={thR}>Value</th><th style={thR}>Sell</th></tr></thead>
              <tbody>
                {items.length === 0 && <tr><td style={td} colSpan={7}>No items yet.</td></tr>}
                {items.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((i) => {
                  const low = (i.reorderLevel || 0) > 0 && (i.qtyOnHand || 0) <= i.reorderLevel;
                  return <tr key={i.id}><td style={td}>{i.sku || ""}</td><td style={td}>{i.name}{low ? " ⚠" : ""}</td><td style={td}>{i.category || ""}</td><td style={tdR}>{(i.qtyOnHand || 0) + " " + (i.unit || "")}</td><td style={tdR}>R {f(i.avgCost || 0)}</td><td style={tdR}>R {f((i.qtyOnHand || 0) * (i.avgCost || 0))}</td><td style={tdR}>R {f(i.sellPrice || 0)}</td></tr>;
                })}
              </tbody>
            </table>
          </div>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Add item</div>
            <div style={grid}>
              {fld("SKU", <input style={inp} value={nf.sku} onChange={(e) => setNf({ ...nf, sku: e.target.value })} />)}
              {fld("Name", <input style={inp} value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} />)}
              {fld("Category", <input style={inp} value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} />)}
              {fld("Unit", <input style={inp} value={nf.unit} onChange={(e) => setNf({ ...nf, unit: e.target.value })} />)}
              {fld("Reorder level", <input style={inp} type="number" value={nf.reorderLevel} onChange={(e) => setNf({ ...nf, reorderLevel: e.target.value })} />)}
              {fld("Sell price", <input style={inp} type="number" value={nf.sellPrice} onChange={(e) => setNf({ ...nf, sellPrice: e.target.value })} />)}
            </div>
            <button style={btnP} disabled={busy} onClick={addItem}>Add item</button>
          </div>
        </div>
      )}

      {tab === "suppliers" && <SuppliersAP project={project} coa={coa} suppliers={suppliers} movements={movements} onReload={reload} onPosted={onPosted} />}
      {tab === "match" && <MatchPayments project={project} coa={coa} suppliers={suppliers} movements={movements} journals={journals || []} onReload={reload} onPosted={onPosted} />}

      {tab === "receive" && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Receive stock — raises Inventory &amp; Trade Payables</div>
          {items.length === 0 ? <div style={{ color: "#9a9488", fontSize: 13 }}>Add an item first.</div> : (
            <div>
              <div style={grid}>
                {fld("Supplier", <select style={inp} value={rf.supplierId} onChange={(e) => setRf({ ...rf, supplierId: e.target.value })}><option value="">— none —</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>)}
                {fld("Item", <select style={inp} value={rf.itemId} onChange={(e) => setRf({ ...rf, itemId: e.target.value })}>{itemOpts}</select>)}
                {fld("Quantity", <input style={inp} type="number" value={rf.qty} onChange={(e) => setRf({ ...rf, qty: e.target.value })} />)}
                {fld("Unit cost (R)", <input style={inp} type="number" value={rf.unitCost} onChange={(e) => setRf({ ...rf, unitCost: e.target.value })} />)}
                {fld("Date", <input style={inp} type="date" value={rf.date} onChange={(e) => setRf({ ...rf, date: e.target.value })} />)}
              </div>
              {vatRegistered && <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#444", marginTop: 8 }}><input type="checkbox" checked={rf.incl} onChange={(e) => setRf({ ...rf, incl: e.target.checked })} /> Unit cost includes {vatRate}% VAT (input VAT claimed; stock valued ex-VAT)</label>}
              <button style={btnP} disabled={busy} onClick={receive}>Receive into stock</button>
            </div>
          )}
        </div>
      )}

      {tab === "issue" && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Issue stock — books to Cost of Sales at average cost</div>
          {items.length === 0 ? <div style={{ color: "#9a9488", fontSize: 13 }}>Add an item first.</div> : (
            <div>
              <div style={grid}>
                {fld("Item", <select style={inp} value={isf.itemId} onChange={(e) => setIsf({ ...isf, itemId: e.target.value })}>{itemOpts}</select>)}
                {fld("Quantity", <input style={inp} type="number" value={isf.qty} onChange={(e) => setIsf({ ...isf, qty: e.target.value })} />)}
                {fld("Reference (optional)", <input style={inp} value={isf.note} onChange={(e) => setIsf({ ...isf, note: e.target.value })} />)}
                {fld("Date", <input style={inp} type="date" value={isf.date} onChange={(e) => setIsf({ ...isf, date: e.target.value })} />)}
              </div>
              <button style={btnP} disabled={busy} onClick={issue}>Issue from stock</button>
            </div>
          )}
        </div>
      )}

      {tab === "movements" && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Stock movements</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Date</th><th style={th}>Type</th><th style={th}>Item</th><th style={thR}>Qty</th><th style={thR}>Value</th><th style={th}>Reference</th></tr></thead>
            <tbody>
              {movements.length === 0 && <tr><td style={td} colSpan={6}>No movements yet.</td></tr>}
              {movements.map((m) => <tr key={m.id}><td style={td}>{m.date || ""}</td><td style={td}>{m.type === "in" ? "IN" : "OUT"}</td><td style={td}>{m.itemName || ""}</td><td style={tdR}>{m.qty || 0}</td><td style={tdR}>R {f(m.total || 0)}</td><td style={td}>{m.supplierName || m.note || ""}</td></tr>)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PAY_ACC = { salaries: "610100", statutory: "210020", bank: "100010", payables: "220010" };

function Payroll({ project, coa, onPosted }) {
  const [tab, setTab] = useState("employees");
  const [emps, setEmps] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const acctName = (code) => (coa.find((a) => a.code === code) || {}).name || "";

  const reload = async () => {
    try { const [e, r] = await Promise.all([loadPayEmployees(project.id), loadPayRuns(project.id)]); setEmps(e); setRuns(r); }
    catch (err) { setStatus("Load failed: " + err.message); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [project.id]);

  const card = { background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 16, marginBottom: 16 };
  const inp = { padding: "7px 9px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 13, width: "100%", boxSizing: "border-box" };
  const btnP = { padding: "8px 16px", borderRadius: 6, border: "none", background: busy ? "#ccc" : "#1a1a2e", color: busy ? "#888" : "#c8a24e", cursor: busy ? "default" : "pointer", fontSize: 13, fontWeight: 600, marginTop: 10 };
  const th = { padding: "6px 8px", textAlign: "left", fontSize: 11, color: "#9a9488", borderBottom: "1px solid #e8e4dc" };
  const thR = { ...th, textAlign: "right" };
  const td = { padding: "6px 8px", fontSize: 12, borderBottom: "1px solid #f0eeea" };
  const tdR = { ...td, textAlign: "right" };
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 };
  const fld = (label, node) => <div><label style={{ display: "block", fontSize: 11, color: "#9a9488", marginBottom: 3 }}>{label}</label>{node}</div>;

  const [ef, setEf] = useState({ name: "", idNumber: "", dob: "", gross: "", allowance: "", frequency: "monthly", pension: "", medicalMembers: "", otherDeduction: "", uifApplicable: true, sdlApplicable: false });
  const addEmp = async () => {
    if (!ef.name.trim()) { setStatus("Employee name required"); return; }
    setBusy(true);
    try {
      await addPayEmployee(project.id, {
        name: ef.name.trim(), idNumber: ef.idNumber.trim(), dob: ef.dob || "",
        gross: Number(ef.gross) || 0, allowance: Number(ef.allowance) || 0, frequency: ef.frequency || "monthly",
        pension: Number(ef.pension) || 0, medicalMembers: Number(ef.medicalMembers) || 0, otherDeduction: Number(ef.otherDeduction) || 0,
        uifApplicable: ef.uifApplicable !== false, sdlApplicable: !!ef.sdlApplicable, active: true,
      });
      setEf({ name: "", idNumber: "", dob: "", gross: "", allowance: "", frequency: "monthly", pension: "", medicalMembers: "", otherDeduction: "", uifApplicable: true, sdlApplicable: false });
      setStatus("Employee added"); await reload();
    } catch (e) { setStatus("Add failed: " + e.message); }
    setBusy(false);
  };
  const removeEmp = async (id, name) => {
    if (!window.confirm("Remove " + (name || "this employee") + "?")) return;
    setBusy(true);
    try { await deletePayEmployee(project.id, id); setStatus("Employee removed"); await reload(); }
    catch (e) { setStatus("Remove failed: " + e.message); }
    setBusy(false);
  };

  const slipFor = (emp) => computePayslip({
    gross: emp.gross, allowance: emp.allowance, frequency: emp.frequency || "monthly", dob: emp.dob,
    pension: emp.pension, medicalMembers: emp.medicalMembers, otherDeduction: emp.otherDeduction,
    uifApplicable: emp.uifApplicable !== false, sdlApplicable: !!emp.sdlApplicable,
  });
  const activeEmps = emps.filter((e) => e.active !== false);
  const computeRun = () => {
    const lines = activeEmps.map((emp) => { const s = slipFor(emp); return { employeeId: emp.id, name: emp.name, ...s }; });
    const T = lines.reduce((a, l) => ({
      gross: a.gross + l.gross, paye: a.paye + l.paye, uifEmp: a.uifEmp + l.uifEmp, uifEr: a.uifEr + l.uifEr,
      sdl: a.sdl + l.sdl, pension: a.pension + l.pension, other: a.other + l.otherDeduction, net: a.net + l.net, mtc: a.mtc + l.mtc,
    }), { gross: 0, paye: 0, uifEmp: 0, uifEr: 0, sdl: 0, pension: 0, other: 0, net: 0, mtc: 0 });
    Object.keys(T).forEach((k) => { T[k] = r2(T[k]); });
    return { lines, totals: T };
  };

  const postRun = async () => {
    const run = computeRun();
    if (!run.lines.length) { setStatus("No active employees to pay"); return; }
    if (runs.some((r) => r.period === period)) { if (!window.confirm("A pay run for " + period + " already exists. Post another?")) return; }
    const T = run.totals;
    const y = Number(period.slice(0, 4)), m = Number(period.slice(5, 7));
    const payDate = period + "-" + String(new Date(y, m, 0).getDate()).padStart(2, "0");
    const debitSalaries = r2(T.gross + T.uifEr + T.sdl);
    const creditStatutory = r2(T.paye + T.uifEmp + T.uifEr + T.sdl);
    const creditPayables = r2(T.pension + T.other);
    const lines = [{ accountCode: PAY_ACC.salaries, accountName: acctName(PAY_ACC.salaries), dr: debitSalaries, cr: 0 }];
    if (creditStatutory > 0) lines.push({ accountCode: PAY_ACC.statutory, accountName: acctName(PAY_ACC.statutory), dr: 0, cr: creditStatutory });
    if (T.net > 0) lines.push({ accountCode: PAY_ACC.bank, accountName: acctName(PAY_ACC.bank), dr: 0, cr: r2(T.net) });
    if (creditPayables > 0) lines.push({ accountCode: PAY_ACC.payables, accountName: acctName(PAY_ACC.payables), dr: 0, cr: creditPayables });
    setBusy(true);
    try {
      const jid = await postJournal(project.id, { date: payDate, narration: "Payroll " + period + " (" + run.lines.length + " employees)", source: { type: "payroll", period }, lines });
      await addPayRun(project.id, { period, date: payDate, lines: run.lines, totals: T, journalId: jid });
      setStatus("Posted payroll for " + period + " — net pay R " + f(T.net) + " (journal posted to ledger)");
      await reload(); if (onPosted) onPosted();
    } catch (e) { setStatus("Post failed: " + e.message); }
    setBusy(false);
  };

  const preview = computeRun();
  const tabs = [["employees", "Employees"], ["run", "Run payroll"], ["history", "History"]];

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e0ddd8", background: tab === id ? "#1a1a2e" : "#fff", color: tab === id ? "#c8a24e" : "#1a1a2e", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#9a9488", alignSelf: "center" }}>SARS {PAY_DEFAULTS.taxYear} tables</span>
      </div>
      {status && <div style={{ fontSize: 12, color: "#444", marginBottom: 10 }}>{status}</div>}

      {tab === "employees" && (
        <div>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Employees — {activeEmps.length} active</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Name</th><th style={th}>Freq</th><th style={thR}>Gross</th><th style={thR}>PAYE</th><th style={thR}>UIF</th><th style={thR}>Net</th><th style={th}></th></tr></thead>
              <tbody>
                {emps.length === 0 && <tr><td style={td} colSpan={7}>No employees yet.</td></tr>}
                {emps.map((emp) => { const s = slipFor(emp); return (
                  <tr key={emp.id}><td style={td}>{emp.name}</td><td style={td}>{emp.frequency || "monthly"}</td><td style={tdR}>R {f(s.gross)}</td><td style={tdR}>R {f(s.paye)}</td><td style={tdR}>R {f(s.uifEmp)}</td><td style={tdR}>R {f(s.net)}</td>
                    <td style={td}><button onClick={() => removeEmp(emp.id, emp.name)} style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, color: "#b33", cursor: "pointer" }}>Remove</button></td></tr>
                ); })}
              </tbody>
            </table>
          </div>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Add employee</div>
            <div style={grid}>
              {fld("Name", <input style={inp} value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} />)}
              {fld("ID number", <input style={inp} value={ef.idNumber} onChange={(e) => setEf({ ...ef, idNumber: e.target.value })} />)}
              {fld("Date of birth", <input style={inp} type="date" value={ef.dob} onChange={(e) => setEf({ ...ef, dob: e.target.value })} />)}
              {fld("Frequency", <select style={inp} value={ef.frequency} onChange={(e) => setEf({ ...ef, frequency: e.target.value })}><option value="monthly">Monthly</option><option value="fortnightly">Fortnightly</option><option value="weekly">Weekly</option></select>)}
              {fld("Gross pay (R)", <input style={inp} type="number" value={ef.gross} onChange={(e) => setEf({ ...ef, gross: e.target.value })} />)}
              {fld("Allowance (R)", <input style={inp} type="number" value={ef.allowance} onChange={(e) => setEf({ ...ef, allowance: e.target.value })} />)}
              {fld("Pension (R)", <input style={inp} type="number" value={ef.pension} onChange={(e) => setEf({ ...ef, pension: e.target.value })} />)}
              {fld("Medical members", <input style={inp} type="number" value={ef.medicalMembers} onChange={(e) => setEf({ ...ef, medicalMembers: e.target.value })} />)}
              {fld("Other deduction (R)", <input style={inp} type="number" value={ef.otherDeduction} onChange={(e) => setEf({ ...ef, otherDeduction: e.target.value })} />)}
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#444" }}><input type="checkbox" checked={ef.uifApplicable} onChange={(e) => setEf({ ...ef, uifApplicable: e.target.checked })} /> UIF applicable</label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#444" }}><input type="checkbox" checked={ef.sdlApplicable} onChange={(e) => setEf({ ...ef, sdlApplicable: e.target.checked })} /> SDL applicable (employer)</label>
            </div>
            <button style={btnP} disabled={busy} onClick={addEmp}>Add employee</button>
          </div>
        </div>
      )}

      {tab === "run" && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>Run payroll</div>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12 }} />
            <button style={{ ...btnP, marginTop: 0, background: busy || !activeEmps.length ? "#ccc" : "#3d7a52", color: "#fff" }} disabled={busy || !activeEmps.length} onClick={postRun}>Post payroll to ledger</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Employee</th><th style={thR}>Gross</th><th style={thR}>PAYE</th><th style={thR}>UIF (ee)</th><th style={thR}>Pension</th><th style={thR}>Other</th><th style={thR}>Net</th></tr></thead>
            <tbody>
              {preview.lines.length === 0 && <tr><td style={td} colSpan={7}>No active employees.</td></tr>}
              {preview.lines.map((l) => <tr key={l.employeeId}><td style={td}>{l.name}</td><td style={tdR}>R {f(l.gross)}</td><td style={tdR}>R {f(l.paye)}</td><td style={tdR}>R {f(l.uifEmp)}</td><td style={tdR}>R {f(l.pension)}</td><td style={tdR}>R {f(l.otherDeduction)}</td><td style={tdR}>R {f(l.net)}</td></tr>)}
              <tr style={{ fontWeight: 700, borderTop: "2px solid #e0ddd8" }}><td style={td}>Totals</td><td style={tdR}>R {f(preview.totals.gross)}</td><td style={tdR}>R {f(preview.totals.paye)}</td><td style={tdR}>R {f(preview.totals.uifEmp)}</td><td style={tdR}>R {f(preview.totals.pension)}</td><td style={tdR}>R {f(preview.totals.other)}</td><td style={tdR}>R {f(preview.totals.net)}</td></tr>
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "#9a9488", marginTop: 10 }}>
            Employer cost this run: gross R {f(preview.totals.gross)} + employer UIF R {f(preview.totals.uifEr)} + SDL R {f(preview.totals.sdl)}. Journal: Dr Salaries &amp; Wages; Cr PAYE/UIF/SDL Payable, Bank (net), and Payables (pension + other).
          </div>
        </div>
      )}

      {tab === "history" && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}>Pay run history</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Period</th><th style={th}>Date</th><th style={thR}>Employees</th><th style={thR}>Gross</th><th style={thR}>PAYE</th><th style={thR}>Net</th></tr></thead>
            <tbody>
              {runs.length === 0 && <tr><td style={td} colSpan={6}>No pay runs yet.</td></tr>}
              {runs.map((r) => <tr key={r.id}><td style={td}>{r.period}</td><td style={td}>{r.date}</td><td style={tdR}>{(r.lines || []).length}</td><td style={tdR}>R {f((r.totals || {}).gross || 0)}</td><td style={tdR}>R {f((r.totals || {}).paye || 0)}</td><td style={tdR}>R {f((r.totals || {}).net || 0)}</td></tr>)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function COAManager({ project, coa, journals, onChanged }) {
  const [editing, setEditing] = useState(null); // {code, name, type, deductible, isNew}
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const usageByCode = (() => {
    const m = {};
    (journals || []).forEach((j) => (j.lines || []).forEach((l) => { m[l.accountCode] = (m[l.accountCode] || 0) + 1; }));
    return m;
  })();

  const filtered = !filter.trim() ? coa : coa.filter((a) => {
    const q = filter.toLowerCase();
    return a.code.toLowerCase().indexOf(q) >= 0 || a.name.toLowerCase().indexOf(q) >= 0 || a.type.toLowerCase().indexOf(q) >= 0;
  });

  const grouped = {};
  filtered.forEach((a) => { if (!grouped[a.type]) grouped[a.type] = []; grouped[a.type].push(a); });
  Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.code.localeCompare(b.code)));
  const order = ["asset", "liability", "equity", "revenue", "other_income", "cogs", "expense", "other_expense"];

  const startAdd = () => setEditing({ code: "", name: "", type: "expense", deductible: true, isNew: true });
  const startEdit = (a) => setEditing({ ...a, isNew: false });
  const cancelEdit = () => { setEditing(null); setStatus(""); };

  const onSaveEdit = async () => {
    if (!editing) return;
    const code = String(editing.code).trim();
    const name = String(editing.name).trim();
    if (!code) return setStatus("Code is required");
    if (!name) return setStatus("Name is required");
    if (!ACCOUNT_TYPES[editing.type]) return setStatus("Pick a valid type");
    if (editing.isNew && coa.find((a) => a.code === code)) return setStatus("That code already exists");
    setBusy(true);
    setStatus("Saving " + code + "...");
    try {
      await upsertAccount(project.id, { code, name, type: editing.type, deductible: editing.deductible == null ? null : !!editing.deductible });
      setStatus("Saved.");
      setEditing(null);
      if (onChanged) await onChanged();
    } catch (err) {
      setStatus("Save failed: " + err.message);
    }
    setBusy(false);
  };

  const onDelete = async (a) => {
    const usage = usageByCode[a.code] || 0;
    if (usage > 0) {
      alert("Cannot delete " + a.code + " — it's referenced by " + usage + " journal line" + (usage > 1 ? "s" : "") + ". Reassign or delete those journals first.");
      return;
    }
    if (!window.confirm("Delete account " + a.code + " (" + a.name + ")? This cannot be undone.")) return;
    setBusy(true);
    setStatus("Deleting " + a.code + "...");
    try {
      await deleteAccount(project.id, a.code);
      setStatus("Deleted.");
      if (onChanged) await onChanged();
    } catch (err) {
      setStatus("Delete failed: " + err.message);
    }
    setBusy(false);
  };

  const onRestoreDefaults = async () => {
    if (!window.confirm("Restore default accounts? This re-seeds the " + DEFAULT_COA.length + " built-in accounts (overwriting any edits to those codes). Your custom accounts are kept.")) return;
    setBusy(true);
    setStatus("Restoring defaults...");
    try {
      await seedCOA(project.id);
      setStatus("Defaults restored.");
      if (onChanged) await onChanged();
    } catch (err) {
      setStatus("Restore failed: " + err.message);
    }
    setBusy(false);
  };

  const labelStyle = { fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 };
  const inputStyle = { width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, outline: "none", background: "#fff" };

  return (
    <div>
      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 12, marginBottom: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by code, name, or type..." style={{ ...inputStyle, flex: 1, minWidth: 220, width: "auto" }} />
        <div style={{ fontSize: 12, color: "#1a1a2e", fontWeight: 600 }}>{filtered.length} of {coa.length} accounts</div>
        <button onClick={startAdd} disabled={busy || !!editing} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: busy || editing ? "#ccc" : "#1a1a2e", color: busy || editing ? "#888" : "#c8a24e", cursor: busy || editing ? "default" : "pointer", fontSize: 12, fontWeight: 600 }}>+ New Account</button>
        <button onClick={onRestoreDefaults} disabled={busy || !!editing} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: busy || editing ? "default" : "pointer", fontSize: 12 }}>Restore Defaults</button>
        {status && <span style={{ fontSize: 11, color: status.indexOf("failed") >= 0 || status.indexOf("required") >= 0 || status.indexOf("exists") >= 0 ? "#b33" : "#3d7a52" }}>{status}</span>}
      </div>

      {editing && (
        <div style={{ background: "#fff8e6", border: "1px solid #f0d97f", borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 10 }}>{editing.isNew ? "New Account" : "Edit Account " + editing.code}</div>
          <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 200px 160px", gap: 10, alignItems: "end" }}>
            <div>
              <label style={labelStyle}>Code *</label>
              <input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} placeholder="e.g. 610600" disabled={!editing.isNew} style={{ ...inputStyle, background: editing.isNew ? "#fff" : "#f4f2ed", color: editing.isNew ? "#1a1a2e" : "#9a9488" }} />
            </div>
            <div>
              <label style={labelStyle}>Name *</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Marketing Expenses" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Type *</label>
              <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}>
                {Object.keys(ACCOUNT_TYPES).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")} ({ACCOUNT_TYPES[t].statement === "bs" ? "Balance Sheet" : "P&L"})</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Deductible (expenses)</label>
              <select value={editing.deductible == null ? "null" : (editing.deductible ? "true" : "false")} onChange={(e) => setEditing({ ...editing, deductible: e.target.value === "null" ? null : e.target.value === "true" })} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="null">N/A</option>
                <option value="true">Yes — deductible</option>
                <option value="false">No — non-deductible</option>
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button onClick={onSaveEdit} disabled={busy} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: busy ? "#ccc" : "#3d7a52", color: "#fff", cursor: busy ? "default" : "pointer", fontSize: 12, fontWeight: 600 }}>{busy ? "Saving..." : "Save"}</button>
            <button onClick={cancelEdit} disabled={busy} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: busy ? "default" : "pointer", fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}

      {coa.length === 0 ? (
        <div style={{ padding: 40, color: "#9a9488", textAlign: "center" }}>No accounts loaded.</div>
      ) : (
        order.filter((t) => grouped[t]).map((t) => (
          <div key={t} style={{ marginBottom: 14, border: "1px solid #e8e4dc", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#c8a24e", padding: "6px 12px", background: "#1a1a2e", textTransform: "uppercase", letterSpacing: 1, display: "flex", justifyContent: "space-between" }}>
              <span>{t.replace(/_/g, " ")}</span>
              <span style={{ color: "#8a8a9a", fontSize: 10 }}>{ACCOUNT_TYPES[t].side === "dr" ? "dr-natural" : "cr-natural"} · {ACCOUNT_TYPES[t].statement === "bs" ? "Balance Sheet" : "P&L"} · {grouped[t].length} accounts</span>
            </div>
            <div>
              {grouped[t].map((a, i) => {
                const usage = usageByCode[a.code] || 0;
                const isDefault = DEFAULT_COA.some((d) => d.code === a.code);
                return (
                  <div key={a.code} style={{ display: "grid", gridTemplateColumns: "90px 1fr 100px 100px 160px", gap: 10, alignItems: "center", padding: "7px 12px", borderTop: i > 0 ? "1px solid #f0eeea" : "none", fontSize: 12, background: "#fff" }}>
                    <span style={{ color: "#9a9488", fontFamily: "monospace" }}>{a.code}</span>
                    <span style={{ color: "#1a1a2e" }}>{a.name}</span>
                    <span style={{ fontSize: 10, color: a.deductible === true ? "#3d7a52" : a.deductible === false ? "#b33" : "#9a9488" }}>
                      {a.deductible === true ? "Deductible" : a.deductible === false ? "Non-deductible" : ""}
                    </span>
                    <span style={{ fontSize: 10, color: usage > 0 ? "#1a1a2e" : "#c0c0c0" }}>
                      {usage > 0 ? usage + " line" + (usage > 1 ? "s" : "") : "unused"}
                      {isDefault && <span style={{ marginLeft: 6, padding: "1px 5px", background: "#eef0f4", color: "#666", borderRadius: 3 }}>default</span>}
                    </span>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button onClick={() => startEdit(a)} disabled={busy || !!editing} style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: busy || editing ? "default" : "pointer", color: "#1a1a2e" }}>Edit</button>
                      <button onClick={() => onDelete(a)} disabled={busy || !!editing || usage > 0} title={usage > 0 ? "Used by " + usage + " journal line(s)" : "Delete this account"} style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: busy || editing || usage > 0 ? "default" : "pointer", color: usage > 0 ? "#ccc" : "#b33" }}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function InvoicePreview({ invoice, onBack, onUpdated }) {
  const fmtR = (n) => "R " + (Number(n) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cs = invoice.customerSnapshot || {};
  const pageRef = useRef(null);
  const sendFormRef = useRef(null);
  const statusRef = useRef(null);
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState("");
  const [sendOk, setSendOk] = useState(false);
  const [sendTo, setSendTo] = useState(cs.email || "");
  const [sendCc, setSendCc] = useState("");
  const [sendSubject, setSendSubject] = useState("Invoice " + invoice.invoiceNumber + " from " + ISSUER.tradingAs);
  const [sendBody, setSendBody] = useState(
    "Dear " + (cs.contactPerson || cs.name || "Sir/Madam") + ",\n\n" +
    "Please find attached invoice " + invoice.invoiceNumber + " for the amount of " + fmtR(invoice.total) + ", due " + invoice.dueDate + ".\n\n" +
    "Payment can be made to the banking details on the invoice, using " + invoice.invoiceNumber + " as the reference.\n\n" +
    "Kind regards,\n" + ISSUER.tradingAs
  );

  const buildPdfBase64 = async () => {
    const node = pageRef.current;
    if (!node) throw new Error("Invoice page not ready");
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;
    let heightLeft = imgH;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgW, imgH);
    heightLeft -= pageH;
    while (heightLeft > 0) {
      position = heightLeft - imgH;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgW, imgH);
      heightLeft -= pageH;
    }
    const dataUri = pdf.output("datauristring");
    return dataUri.split(",")[1];
  };

  const onSend = async () => {
    if (!sendTo.trim()) { setSendOk(false); setSendStatus("Recipient email required."); return; }
    setSending(true);
    setSendOk(false);
    setSendStatus("Generating PDF...");
    try {
      const pdfBase64 = await buildPdfBase64();
      setSendStatus("Sending email...");
      const resp = await fetch((process.env.REACT_APP_ENGINE_URL ?? "http://localhost:3001") + "/api/send-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify({
          to: sendTo.trim(),
          cc: sendCc.trim() || undefined,
          subject: sendSubject,
          bodyText: sendBody,
          pdfBase64,
          filename: invoice.invoiceNumber + ".pdf",
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || ("HTTP " + resp.status));
      const sentAt = new Date().toISOString();
      await saveInvoice({
        id: invoice.id || invoice.invoiceNumber,
        invoiceNumber: invoice.invoiceNumber,
        status: "sent",
        sentAt,
        sentTo: sendTo.trim(),
        sentCc: sendCc.trim() || null,
      });
      setSendOk(true);
      setSendStatus("Sent to " + sendTo.trim() + " at " + new Date(sentAt).toLocaleString("en-ZA") + ".");
      setShowSend(false);
      if (onUpdated) await onUpdated();
    } catch (err) {
      setSendOk(false);
      setSendStatus("Send failed: " + err.message);
    }
    setSending(false);
  };

  useEffect(() => {
    if (showSend && sendFormRef.current) {
      sendFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showSend]);

  useEffect(() => {
    if (sendStatus && statusRef.current) {
      statusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [sendStatus]);

  return (
    <div>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .invoice-page { box-shadow: none !important; margin: 0 !important; padding: 24px !important; max-width: 100% !important; }
          aside, header { display: none !important; }
        }
      `}</style>
      <div className="no-print" style={{ position: "sticky", top: 0, zIndex: 5, background: "#f5f4f0", paddingTop: 4, paddingBottom: 4, display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={onBack} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: "pointer", fontSize: 13 }}>← Back</button>
        <button onClick={() => window.print()} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: "pointer", fontSize: 13 }}>Print / Save as PDF</button>
        <button onClick={() => setShowSend((s) => !s)} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: "#1a1a2e", color: "#c8a24e", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{showSend ? "Hide Send" : "Send via Email"}</button>
        {invoice.sentAt && <div style={{ fontSize: 11, color: "#3d7a52" }}>Last sent: {new Date(invoice.sentAt).toLocaleString("en-ZA")} → {invoice.sentTo}</div>}
      </div>
      {sendStatus && (
        <div ref={statusRef} className="no-print" style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13, fontWeight: 600, border: "1px solid " + (sendOk ? "#a7d3b3" : "#f0c0c0"), background: sendOk ? "#e9f5ee" : "#fff4f4", color: sendOk ? "#2a5a3a" : "#a02020", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{sendOk ? "✓ " : ""}{sendStatus}</span>
          <button onClick={() => setSendStatus("")} style={{ background: "none", border: "none", color: "inherit", fontSize: 16, cursor: "pointer", padding: "0 4px" }} title="Dismiss">×</button>
        </div>
      )}
      {showSend && (
        <div ref={sendFormRef} className="no-print" style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a2e", marginBottom: 10 }}>Send Invoice (from your configured invoice email)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 }}>To *</label>
              <input value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="customer@example.com" style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, background: "#fff" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 }}>Cc (optional)</label>
              <input value={sendCc} onChange={(e) => setSendCc(e.target.value)} placeholder="copy@example.com" style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, background: "#fff" }} />
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 }}>Subject</label>
            <input value={sendSubject} onChange={(e) => setSendSubject(e.target.value)} style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, background: "#fff" }} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 }}>Message</label>
            <textarea value={sendBody} onChange={(e) => setSendBody(e.target.value)} rows={6} style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, background: "#fff", fontFamily: "inherit", resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={onSend} disabled={sending} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: sending ? "#ccc" : "#3d7a52", color: "#fff", cursor: sending ? "default" : "pointer", fontSize: 12, fontWeight: 600 }}>{sending ? "Sending..." : "Send"}</button>
            <button onClick={() => setShowSend(false)} disabled={sending} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: sending ? "default" : "pointer", fontSize: 12 }}>Cancel</button>
            {sendStatus && <span style={{ fontSize: 11, color: sendStatus.indexOf("failed") >= 0 || sendStatus.indexOf("required") >= 0 ? "#b33" : "#3d7a52" }}>{sendStatus}</span>}
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: "#9a9488" }}>The invoice is attached as a PDF ({invoice.invoiceNumber}.pdf). A copy is BCC'd to your configured invoice address.</div>
        </div>
      )}
      <div ref={pageRef} className="invoice-page" style={{ background: "#fff", maxWidth: 820, margin: "0 auto", padding: 40, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderRadius: 4, fontFamily: "'DM Sans', Arial, sans-serif", color: "#1a1a2e" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 30 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#c8a24e", marginBottom: 4 }}>{ISSUER.tradingAs}</div>
            <div style={{ fontSize: 11, color: "#666", lineHeight: 1.5 }}>
              {ISSUER.name}<br />
              Reg: {ISSUER.regNumber} · Tax: {ISSUER.taxNumber}{ISSUER.vatNumber ? " · VAT: " + ISSUER.vatNumber : ""}<br />
              {formatAddress(ISSUER.address)}<br />
              {ISSUER.email}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 2, color: "#1a1a2e" }}>INVOICE</div>
            <div style={{ fontSize: 12, marginTop: 6 }}><strong>{invoice.invoiceNumber}</strong></div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Issued: {invoice.date}<br />Due: {invoice.dueDate}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, marginBottom: 26 }}>
          <div>
            <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>Bill To</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{cs.name || "—"}</div>
            {cs.contactPerson && <div style={{ fontSize: 11, color: "#666" }}>Attn: {cs.contactPerson}</div>}
            {cs.address && <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{cs.address}</div>}
            {cs.email && <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{cs.email}</div>}
            {cs.regNumber && <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>Reg: {cs.regNumber}</div>}
            {cs.vatNumber && <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>VAT: {cs.vatNumber}</div>}
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>Status</div>
            <div style={{ fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>{invoice.status || "draft"}</div>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20, fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#1a1a2e", color: "#c8a24e" }}>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>Description</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, width: 80 }}>Hours</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, width: 110 }}>Rate</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, width: 130 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines || []).map((l, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eceae4" }}>
                <td style={{ padding: "10px 12px" }}>{l.description || (SERVICE_CATALOG.find((s) => s.code === l.serviceCode) || {}).label || ""}</td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>{Number(l.hours || 0).toFixed(2)}</td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>{fmtR(l.rate)}/hr</td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>{fmtR(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 30 }}>
          <table style={{ fontSize: 12 }}>
            <tbody>
              <tr><td style={{ padding: "4px 16px", textAlign: "right", color: "#666" }}>Subtotal</td><td style={{ padding: "4px 0", textAlign: "right", minWidth: 140 }}>{fmtR(invoice.subtotal)}</td></tr>
              {Number(invoice.vatRate) > 0 && (
                <tr><td style={{ padding: "4px 16px", textAlign: "right", color: "#666" }}>VAT ({(invoice.vatRate * 100).toFixed(0)}%)</td><td style={{ padding: "4px 0", textAlign: "right" }}>{fmtR(invoice.vatAmount)}</td></tr>
              )}
              <tr style={{ borderTop: "2px solid #1a1a2e", fontWeight: 700, fontSize: 14 }}>
                <td style={{ padding: "8px 16px", textAlign: "right" }}>TOTAL DUE</td>
                <td style={{ padding: "8px 0", textAlign: "right" }}>{fmtR(invoice.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ border: "1px solid #eceae4", borderRadius: 6, padding: 14, background: "#faf9f6", marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>Banking Details</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11 }}>
            <div><strong>Bank:</strong> {BANKING.bankName}</div>
            <div><strong>Account Type:</strong> {BANKING.accountType}</div>
            <div><strong>Account Holder:</strong> {BANKING.accountHolder}</div>
            <div><strong>Branch:</strong> {BANKING.branch}</div>
            <div><strong>Account Number:</strong> {BANKING.accountNumber}</div>
            <div><strong>Branch Code:</strong> {BANKING.branchCode}</div>
            <div><strong>SWIFT:</strong> {BANKING.swift}</div>
            <div><strong>Reference:</strong> {invoice.invoiceNumber}</div>
          </div>
        </div>

        {invoice.notes && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 6 }}>Notes</div>
            <div style={{ fontSize: 11, color: "#444", whiteSpace: "pre-wrap" }}>{invoice.notes}</div>
          </div>
        )}

        <div style={{ textAlign: "center", fontSize: 10, color: "#999", borderTop: "1px solid #eceae4", paddingTop: 14, marginTop: 30 }}>
          Thank you for your business — {ISSUER.tradingAs} · {ISSUER.email}
        </div>
      </div>
    </div>
  );
}

function Invoices({ projects }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [view, setView] = useState("list"); // 'list' | 'edit' | 'preview'
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const rows = await loadInvoices();
      setList(rows);
    } catch (err) {
      setStatus("Could not load invoices: " + err.message);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const todayIso = new Date().toISOString().slice(0, 10);
  const addDays = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

  const startNew = async () => {
    setStatus("Generating invoice number...");
    let invNum;
    try { invNum = await nextInvoiceNumber(new Date().getFullYear()); }
    catch (err) { invNum = "INV-" + new Date().getFullYear() + "-001"; }
    setEditing({
      invoiceNumber: invNum,
      date: todayIso,
      dueDate: addDays(todayIso, 30),
      customerProjectId: "",
      customerSnapshot: { name: "", address: "", email: "", contactPerson: "", vatNumber: "", regNumber: "" },
      lines: [{ serviceCode: "BOOKKEEPING", description: "Monthly Bookkeeping", hours: 1, rate: 200 }],
      notes: "",
      status: "draft",
      vatEnabled: false,
    });
    setView("edit");
    setStatus("");
  };

  const startEdit = (inv) => {
    setEditing({ ...inv, vatEnabled: Number(inv.vatRate) > 0 });
    setView("edit");
  };

  const startPreview = (inv) => {
    setEditing(inv);
    setView("preview");
  };

  const onDelete = async (inv) => {
    if (!window.confirm("Delete invoice " + inv.invoiceNumber + "? This cannot be undone.")) return;
    try {
      await deleteInvoice(inv.id);
      await refresh();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const filtered = !filter.trim() ? list : list.filter((i) => {
    const q = filter.toLowerCase();
    return (i.invoiceNumber || "").toLowerCase().indexOf(q) >= 0
      || ((i.customerSnapshot && i.customerSnapshot.name) || "").toLowerCase().indexOf(q) >= 0
      || (i.status || "").toLowerCase().indexOf(q) >= 0;
  });

  if (view === "preview" && editing) {
    return <InvoicePreview invoice={editing} onBack={() => { setView("list"); refresh(); }} onUpdated={refresh} />;
  }
  if (view === "edit" && editing) {
    return (
      <InvoiceEditor
        invoice={editing}
        projects={projects}
        onCancel={() => { setView("list"); setEditing(null); }}
        onSaved={async (saved, action) => {
          await refresh();
          if (action === "preview") { setEditing(saved); setView("preview"); }
          else { setView("list"); setEditing(null); }
        }}
      />
    );
  }
  return (
    <div>
      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 12, marginBottom: 14, display: "flex", gap: 10, alignItems: "center" }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by invoice no, customer, status..." style={{ flex: 1, padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, background: "#fff" }} />
        <div style={{ fontSize: 12, color: "#1a1a2e", fontWeight: 600 }}>{filtered.length} of {list.length}</div>
        <button onClick={startNew} disabled={loading} style={{ padding: "7px 16px", borderRadius: 6, border: "none", background: "#1a1a2e", color: "#c8a24e", cursor: loading ? "default" : "pointer", fontSize: 12, fontWeight: 600 }}>+ New Invoice</button>
        <button onClick={refresh} style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: "pointer", fontSize: 11 }}>Refresh</button>
      </div>
      {status && <div style={{ marginBottom: 10, fontSize: 12, color: status.indexOf("not load") >= 0 ? "#b33" : "#444" }}>{status}</div>}
      <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "150px 110px 1fr 110px 130px 100px 150px", background: "#1a1a2e", color: "#c8a24e", fontSize: 11, padding: "8px 12px", fontWeight: 600 }}>
          <div>Invoice #</div><div>Date</div><div>Customer</div><div>Status</div><div style={{ textAlign: "right" }}>Total</div><div></div><div></div>
        </div>
        {filtered.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#9a9488", fontSize: 13 }}>{list.length === 0 ? "No invoices yet. Click + New Invoice." : "No invoices match the filter."}</div>}
        {filtered.map((inv) => (
          <div key={inv.id} style={{ display: "grid", gridTemplateColumns: "150px 110px 1fr 110px 130px 100px 150px", padding: "7px 12px", fontSize: 12, borderTop: "1px solid #f0eeea", alignItems: "center" }}>
            <div style={{ fontFamily: "monospace" }}>{inv.invoiceNumber}</div>
            <div>{inv.date}</div>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.customerSnapshot && inv.customerSnapshot.name}</div>
            <div style={{ textTransform: "capitalize" }}>{inv.status || "draft"}</div>
            <div style={{ textAlign: "right" }}>R {(Number(inv.total) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
              <button onClick={() => startPreview(inv)} style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>View</button>
            </div>
            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
              <button onClick={() => startEdit(inv)} style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer" }}>Edit</button>
              <button onClick={() => onDelete(inv)} style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer", color: "#b33" }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvoiceEditor({ invoice, projects, onCancel, onSaved }) {
  const [inv, setInv] = useState(invoice);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  const u = (patch) => setInv((p) => ({ ...p, ...patch }));

  const pickCustomer = (projectId) => {
    if (!projectId) {
      u({ customerProjectId: "", customerSnapshot: { name: "", address: "", email: "", contactPerson: "", vatNumber: "", regNumber: "" } });
      return;
    }
    const p = projects.find((x) => x.id === projectId);
    if (!p) return;
    u({
      customerProjectId: projectId,
      customerSnapshot: {
        name: p.name || "",
        address: p.address || "",
        email: p.email || "",
        contactPerson: p.contactPerson || "",
        vatNumber: p.vatNumber || "",
        regNumber: p.regNumber || "",
      },
    });
  };

  const updateCustomer = (k, v) => u({ customerSnapshot: { ...(inv.customerSnapshot || {}), [k]: v } });

  const updateLine = (i, patch) => u({ lines: inv.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const addLine = () => u({ lines: [...(inv.lines || []), { serviceCode: "CUSTOM", description: "", hours: 1, rate: 200 }] });
  const removeLine = (i) => u({ lines: inv.lines.filter((_, j) => j !== i) });
  const onServiceChange = (i, code) => {
    const svc = SERVICE_CATALOG.find((s) => s.code === code);
    if (svc) updateLine(i, { serviceCode: svc.code, description: svc.label, rate: svc.defaultRate });
    else updateLine(i, { serviceCode: "CUSTOM" });
  };

  const lines = (inv.lines || []).map((l) => ({ ...l, amount: (Number(l.hours) || 0) * (Number(l.rate) || 0) }));
  const subtotal = lines.reduce((s, l) => s + l.amount, 0);
  const vatRate = inv.vatEnabled ? VAT_RATE : 0;
  const vatAmount = subtotal * vatRate;
  const total = subtotal + vatAmount;

  const canSave = !!inv.customerSnapshot && !!inv.customerSnapshot.name && lines.length > 0 && lines.every((l) => l.description && l.amount > 0);

  const save = async (action) => {
    if (!canSave) { setStatus("Pick a customer and add at least one line with hours × rate > 0."); return; }
    setSaving(true);
    setStatus("Saving...");
    try {
      // SARS VAT-Modernisation readiness: build a structured EN16931-aligned
      // e-invoice alongside the record. Dormant (not transmitted) until the
      // Interoperability Framework is live; must never break a save.
      let eInvoice = null;
      try {
        eInvoice = buildEInvoice({
          invoiceNumber: inv.invoiceNumber,
          issueDate: inv.date,
          seller: {
            name: ISSUER.name,
            tradingName: ISSUER.tradingAs,
            vatNumber: ISSUER.vatNumber || undefined,
            registrationNumber: ISSUER.regNumber,
            email: ISSUER.email,
          },
          buyer: inv.customerSnapshot && inv.customerSnapshot.name
            ? {
                name: inv.customerSnapshot.name,
                vatNumber: inv.customerSnapshot.vatNumber || undefined,
                registrationNumber: inv.customerSnapshot.regNumber || undefined,
                email: inv.customerSnapshot.email || undefined,
              }
            : undefined,
          pricesIncludeVat: false,
          lines: lines.map((l) => ({
            description: l.description,
            quantity: Number(l.hours) || 1,
            unitPrice: Number(l.rate) || 0,
            vatCategory: inv.vatEnabled ? "S" : "O",
          })),
          source: inv.invoiceNumber,
        }, { appId: "tlhakiso" });
      } catch (e) {
        /* readiness scaffold must never block invoicing */
      }

      const toSave = {
        id: inv.invoiceNumber,
        invoiceNumber: inv.invoiceNumber,
        date: inv.date,
        dueDate: inv.dueDate,
        customerProjectId: inv.customerProjectId,
        customerSnapshot: inv.customerSnapshot,
        lines: lines.map((l) => ({ serviceCode: l.serviceCode, description: l.description, hours: Number(l.hours) || 0, rate: Number(l.rate) || 0, amount: l.amount })),
        subtotal,
        vatRate,
        vatAmount,
        total,
        notes: inv.notes || "",
        status: inv.status || "draft",
        eInvoice,
      };
      await saveInvoice(toSave);
      setStatus("Saved.");
      onSaved(toSave, action);
    } catch (err) {
      setStatus("Save failed: " + err.message);
    }
    setSaving(false);
  };

  const labelStyle = { fontSize: 11, color: "#8a8578", fontWeight: 600, display: "block", marginBottom: 4 };
  const inputStyle = { width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 12, outline: "none", background: "#fff" };
  const fmtR = (n) => "R " + (Number(n) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>{invoice.invoiceNumber} · {invoice.status === "draft" ? "Draft" : "Edit"}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: "pointer", fontSize: 12 }}>Cancel</button>
          <button onClick={() => save("save")} disabled={saving || !canSave} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", cursor: saving || !canSave ? "default" : "pointer", fontSize: 12 }}>{saving ? "Saving..." : "Save"}</button>
          <button onClick={() => save("preview")} disabled={saving || !canSave} style={{ padding: "7px 16px", borderRadius: 6, border: "none", background: saving || !canSave ? "#ccc" : "#1a1a2e", color: saving || !canSave ? "#888" : "#c8a24e", cursor: saving || !canSave ? "default" : "pointer", fontSize: 12, fontWeight: 600 }}>Save & Preview</button>
        </div>
      </div>

      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px 130px", gap: 10 }}>
          <div>
            <label style={labelStyle}>Customer *</label>
            <select value={inv.customerProjectId || ""} onChange={(e) => pickCustomer(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">— Select customer —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Invoice Date</label>
            <input type="date" value={inv.date || ""} onChange={(e) => u({ date: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Due Date</label>
            <input type="date" value={inv.dueDate || ""} onChange={(e) => u({ dueDate: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select value={inv.status || "draft"} onChange={(e) => u({ status: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        {inv.customerProjectId && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eceae4" }}>
            <div style={{ fontSize: 10, color: "#9a9488", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>Customer details (frozen on this invoice — edit here for this invoice only)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={labelStyle}>Name</label><input value={inv.customerSnapshot.name || ""} onChange={(e) => updateCustomer("name", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Contact Person</label><input value={inv.customerSnapshot.contactPerson || ""} onChange={(e) => updateCustomer("contactPerson", e.target.value)} style={inputStyle} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginTop: 6 }}>
              <div><label style={labelStyle}>Address</label><input value={inv.customerSnapshot.address || ""} onChange={(e) => updateCustomer("address", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Reg Number</label><input value={inv.customerSnapshot.regNumber || ""} onChange={(e) => updateCustomer("regNumber", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>VAT Number</label><input value={inv.customerSnapshot.vatNumber || ""} onChange={(e) => updateCustomer("vatNumber", e.target.value)} style={inputStyle} /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, marginTop: 6 }}>
              <div><label style={labelStyle}>Email</label><input value={inv.customerSnapshot.email || ""} onChange={(e) => updateCustomer("email", e.target.value)} style={inputStyle} /></div>
            </div>
          </div>
        )}
      </div>

      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>Line items</div>
        <div style={{ border: "1px solid #eceae4", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
          <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 90px 110px 130px 50px", background: "#1a1a2e", color: "#c8a24e", fontSize: 11, padding: "7px 10px", fontWeight: 600 }}>
            <div>Service</div><div>Description</div><div style={{ textAlign: "right" }}>Hours</div><div style={{ textAlign: "right" }}>Rate</div><div style={{ textAlign: "right" }}>Amount</div><div></div>
          </div>
          {lines.map((l, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "180px 1fr 90px 110px 130px 50px", gap: 6, padding: "5px 10px", borderTop: "1px solid #f0eeea", alignItems: "center" }}>
              <select value={l.serviceCode || "CUSTOM"} onChange={(e) => onServiceChange(i, e.target.value)} style={{ padding: 5, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4, background: "#fff" }}>
                {SERVICE_CATALOG.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                <option value="CUSTOM">Custom...</option>
              </select>
              <input value={l.description || ""} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Description" style={{ padding: 5, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4 }} />
              <input type="number" step="0.25" min="0" value={l.hours || ""} onChange={(e) => updateLine(i, { hours: parseFloat(e.target.value) || 0 })} style={{ padding: 5, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4, textAlign: "right" }} />
              <input type="number" step="0.01" min="0" value={l.rate || ""} onChange={(e) => updateLine(i, { rate: parseFloat(e.target.value) || 0 })} style={{ padding: 5, fontSize: 11, border: "1px solid #e0ddd8", borderRadius: 4, textAlign: "right" }} />
              <div style={{ textAlign: "right", fontWeight: 600 }}>{fmtR(l.amount)}</div>
              <button onClick={() => removeLine(i)} disabled={lines.length <= 1} style={{ padding: "2px 6px", background: "none", border: "1px solid #e0ddd8", borderRadius: 4, color: lines.length <= 1 ? "#ccc" : "#b33", cursor: lines.length <= 1 ? "default" : "pointer", fontSize: 10 }}>✕</button>
            </div>
          ))}
          <div style={{ padding: "6px 10px", borderTop: "1px solid #f0eeea" }}>
            <button onClick={addLine} style={{ padding: "3px 10px", background: "none", border: "1px dashed #1a1a2e", borderRadius: 4, color: "#1a1a2e", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ Add line</button>
          </div>
          <div style={{ borderTop: "1px solid #d8d8d8", background: "#eef0f4", padding: "8px 10px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 20, fontSize: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 5, color: "#666" }}>
                <input type="checkbox" checked={!!inv.vatEnabled} onChange={(e) => u({ vatEnabled: e.target.checked })} />
                Apply VAT ({(VAT_RATE * 100).toFixed(0)}%)
              </label>
              <div>Subtotal: <strong>{fmtR(subtotal)}</strong></div>
              {inv.vatEnabled && <div>VAT: <strong>{fmtR(vatAmount)}</strong></div>}
              <div style={{ fontSize: 14 }}>Total: <strong>{fmtR(total)}</strong></div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 14 }}>
        <label style={labelStyle}>Notes (optional)</label>
        <textarea value={inv.notes || ""} onChange={(e) => u({ notes: e.target.value })} rows={3} placeholder="Payment terms, thank-you message, etc." style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }} />
      </div>

      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 8, padding: 14, marginTop: 14 }}>
        <div style={{ fontSize: 10, color: "#9a9488", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>Banking Details (printed on invoice)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11, color: "#2c2a26" }}>
          <div><strong>Bank:</strong> {BANKING.bankName}</div>
          <div><strong>Account Type:</strong> {BANKING.accountType}</div>
          <div><strong>Account Holder:</strong> {BANKING.accountHolder}</div>
          <div><strong>Branch:</strong> {BANKING.branch}</div>
          <div><strong>Account Number:</strong> {BANKING.accountNumber}</div>
          <div><strong>Branch Code:</strong> {BANKING.branchCode}</div>
          <div><strong>SWIFT:</strong> {BANKING.swift}</div>
          <div><strong>Reference:</strong> {inv.invoiceNumber}</div>
        </div>
      </div>

      {status && <div style={{ marginTop: 10, fontSize: 12, color: status.indexOf("failed") >= 0 || status.indexOf("Pick") >= 0 ? "#b33" : "#3d7a52" }}>{status}</div>}
    </div>
  );
}

function Profile({ D }) {
  const S = [{ t: "Company", d: [["Name", D.name], ["Type", D.type || "Not set"], ["Address", D.address || "Not set"], ["Tax", (D.taxType || "?") + " at " + (D.taxRate || 27) + "%"]] }];
  return (
    <div>{S.map((s, i) => (
      <div key={i} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a2e", padding: "7px 14px", background: "#eef0f4", borderRadius: "6px 6px 0 0" }}>{s.t}</div>
        <div style={{ border: "1px solid #e8e4dc", borderTop: "none", borderRadius: "0 0 6px 6px" }}>
          {s.d.map(([k, v], j) => (
            <div key={j} style={{ display: "flex", padding: "6px 14px", borderBottom: j < s.d.length - 1 ? "1px solid #f0eeea" : "none", fontSize: 12 }}>
              <span style={{ width: 180, color: "#9a9488", flexShrink: 0 }}>{k}</span><span style={{ color: "#2c2a26" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    ))}</div>
  );
}

function AccessManager({ project, authUser, onChanged }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("auditor");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const members = project.members || {};
  const entries = Object.keys(members).map((uid) => ({ uid, ...members[uid] }));
  const roleDesc = {
    admin: "Full control, incl. managing access",
    preparer: "Capture & reverse entries, import statements",
    reviewer: "View + lock/unlock periods",
    readonly: "View only",
    auditor: "View & export only (external auditors, e.g. KPMG)",
  };

  const add = async () => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    setBusy(true); setMsg("");
    try {
      const u = await findUserByEmail(e);
      if (!u) { setMsg("No user found with that email. They must sign in to Tlhakiso once before you can grant them access."); setBusy(false); return; }
      await addProjectMember(project.id, u.uid, e, role);
      setEmail(""); setMsg("✓ Granted " + role + " access to " + e + ".");
      await onChanged();
    } catch (ex) { setMsg("Failed: " + ex.message); }
    setBusy(false);
  };
  const remove = async (uid) => {
    if (uid === authUser.uid && !window.confirm("Remove your OWN access to this company? You'll lose access to it.")) return;
    if (!window.confirm("Remove this person's access to " + (project.name || "this company") + "?")) return;
    setBusy(true); setMsg("");
    try { await removeProjectMember(project.id, uid); await onChanged(); }
    catch (ex) { setMsg("Failed: " + ex.message); }
    setBusy(false);
  };

  const inp = { padding: "9px 11px", borderRadius: 6, border: "1px solid #e0ddd8", fontSize: 13, outline: "none" };
  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>⚷ Team & Access — {project.name}</div>
        <div style={{ fontSize: 12, color: "#9a9488", marginTop: 2 }}>Who can access this company's books. Each company is isolated; people only see companies they're added to. Add external auditors as <strong>Auditor</strong> (view &amp; export only).</div>
      </div>

      <div style={{ background: "#faf9f6", border: "1px solid #e8e4dc", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", marginBottom: 8 }}>Grant access</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@firm.co.za" style={{ ...inp, flex: 1, minWidth: 220 }} />
          <select value={role} onChange={(e) => setRole(e.target.value)} style={{ ...inp, cursor: "pointer" }}>
            <option value="auditor">Auditor (view & export)</option>
            <option value="readonly">Read-only</option>
            <option value="reviewer">Reviewer</option>
            <option value="preparer">Preparer</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={add} disabled={busy || !email.trim()} style={{ padding: "9px 18px", borderRadius: 6, border: "none", background: busy || !email.trim() ? "#ccc" : "#1a1a2e", color: busy || !email.trim() ? "#888" : "#c8a24e", fontSize: 13, fontWeight: 600, cursor: busy || !email.trim() ? "default" : "pointer" }}>Add</button>
        </div>
        <div style={{ fontSize: 11, color: "#9a9488", marginTop: 8 }}>{roleDesc[role]}</div>
        {msg && <div style={{ fontSize: 12, color: msg.startsWith("✓") ? "#3d7a52" : "#b33", marginTop: 8 }}>{msg}</div>}
      </div>

      <div style={{ background: "#fff", border: "1px solid #e8e4dc", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 90px", background: "#1a1a2e", color: "#c8a24e", fontSize: 11, fontWeight: 600, padding: "8px 14px" }}>
          <div>User</div><div>Role</div><div style={{ textAlign: "right" }}>Action</div>
        </div>
        {entries.length === 0 && <div style={{ padding: 20, fontSize: 13, color: "#9a9488" }}>No members yet.</div>}
        {entries.map((m) => (
          <div key={m.uid} style={{ display: "grid", gridTemplateColumns: "1fr 140px 90px", padding: "9px 14px", fontSize: 13, borderTop: "1px solid #f0eeea", alignItems: "center" }}>
            <div>{m.email || m.uid}{m.uid === authUser.uid ? <span style={{ color: "#9a9488", fontSize: 11 }}> (you)</span> : null}</div>
            <div style={{ textTransform: "capitalize" }}>{m.role}</div>
            <div style={{ textAlign: "right" }}>
              <button onClick={() => remove(m.uid)} disabled={busy} style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer", color: "#b33" }}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function friendlyAuthError(ex) {
  const code = (ex && ex.code) || "";
  if (code === "auth/operation-not-allowed") return "Email/password sign-in isn't enabled for this project yet. Enable it in the Firebase console (Authentication → Sign-in method).";
  if (code === "auth/invalid-email") return "That doesn't look like a valid email address.";
  if (code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found") return "Incorrect email or password.";
  if (code === "auth/email-already-in-use") return "An account already exists for that email. Switch to “Sign in” instead.";
  if (code === "auth/weak-password") return "That password is too weak — use at least 6 characters.";
  if (code === "auth/too-many-requests") return "Too many attempts. Please wait a moment and try again.";
  if (code === "auth/network-request-failed") return "Network error — check your connection.";
  return (ex && ex.message) || "Sign-in failed.";
}

const authInp = { width: "100%", padding: "11px 13px", borderRadius: 8, border: "1px solid #2a2a4a", background: "#1a1a2e", color: "#e8e4dc", fontSize: 14, outline: "none", marginTop: 6, boxSizing: "border-box" };
const authShell = { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#111118", fontFamily: "'DM Sans','Segoe UI',sans-serif", padding: 16 };
const authCard = { width: 360, maxWidth: "100%", background: "#16161f", border: "1px solid #2a2a4a", borderRadius: 14, padding: "32px 28px" };
const authBtn = (busy) => ({ width: "100%", marginTop: 20, padding: "11px", borderRadius: 8, border: "none", background: busy ? "#33334a" : "#c8a24e", color: busy ? "#888" : "#1a1a2e", fontSize: 14, fontWeight: 700, cursor: busy ? "default" : "pointer" });

// Which platform is this URL serving? The stokvel product runs on its OWN hosting
// site (tlhakiso-stokvel.web.app) and shows ONLY the stokvel app to everyone — never
// the accounting/reconciliation dashboard used for client bookkeeping, which stays on
// tlhakiso-ff53f.web.app. One build serves both sites; the hostname decides the mode.
function detectStokvelPlatform() {
  try {
    const h = (window.location.hostname || "").toLowerCase();
    if (h.includes("stokvel")) return true;                 // the stokvel hosting site
    const p = new URLSearchParams(window.location.search).get("platform");
    if (p === "stokvel") return true;                       // ?platform=stokvel for local testing
    if ((process.env.REACT_APP_PLATFORM || "") === "stokvel") return true;
    return false;
  } catch (e) { return false; }
}

// The login screen adapts to the platform (URL) it's served on. On the STOKVEL site
// (tlhakiso-stokvel.web.app) it offers public self-registration + sign-in. On the
// ACCOUNTING site (tlhakiso-ff53f.web.app) it is sign-in only — those accounts are
// provisioned by an administrator. A "Create account" only ever makes a stokvel user.
function LoginScreen({ joinCode, platform, onSetJoinCode }) {
  const isStokvel = platform === "stokvel";
  const allowSignup = isStokvel || !!joinCode; // never advertise signup on the private books tool
  const [mode, setMode] = useState(allowSignup && joinCode ? "signup" : "signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState(joinCode || "");
  // Partner referral code — pre-filled from a ?ref=CODE link so partners can just share a URL.
  const [referral, setReferral] = useState(() => { try { return new URLSearchParams(window.location.search).get("ref") || ""; } catch (e) { return ""; } });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const isSignup = allowSignup && mode === "signup";

  const forgot = async () => {
    setErr(""); setMsg("");
    if (!email.trim()) { setErr("Enter your email above, then click “Forgot password?”."); return; }
    setBusy(true);
    try {
      await resetPassword(email);
      setMsg("If an account exists for " + email.trim() + ", a password-reset link is on its way. Check your inbox (and spam folder).");
    } catch (ex) { setErr(friendlyAuthError(ex)); }
    setBusy(false);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setMsg("");
    if (!email.trim() || !password) { setErr("Enter your email and password."); return; }
    if (isSignup && password.length < 6) { setErr("Choose a password of at least 6 characters."); return; }
    setBusy(true);
    try {
      if (isSignup) {
        // Mark this browser as a stokvel signup so the app scopes the user correctly
        // even before the Firestore profile write is confirmed. onAuth then shows the
        // verify-email screen (the account is created but sign-in follows verification).
        try { localStorage.setItem("tlhakiso_kind", "stokvel"); } catch (e2) {}
        // If they're joining a group, remember the code so we submit it once their email
        // is verified (they'll land in the group's waiting room until the chair approves).
        const jc = (code || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
        if (jc && onSetJoinCode) onSetJoinCode(jc);
        const ref = (referral || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
        await registerStokvelUser(email.trim(), password, name, ref);
        // onAuth fires → App shows the "verify your email" gate. Nothing more to do here.
      } else {
        await signIn(email.trim(), password); // onAuth handles the rest
      }
    } catch (ex) { setErr(friendlyAuthError(ex)); setBusy(false); }
  };

  return (
    <div style={authShell}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <form onSubmit={submit} style={authCard}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#e8e4dc" }}>{isStokvel ? "Tlhakiso Stokvel" : "Tlhakiso"}</div>
        <div style={{ fontSize: 11, color: "#6a6a80", letterSpacing: 0.5, marginBottom: 20 }}>{joinCode ? "Stokvel invite" : (isStokvel ? "Savings groups" : "Reconciliation Platform")}</div>

        {joinCode && <div style={{ background: "#1c2a1f", border: "1px solid #2f5c40", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#a7d7b6", marginBottom: 16, lineHeight: 1.5 }}>You've been invited to a stokvel (code <strong>{joinCode}</strong>). {isSignup ? "Create an account" : "Sign in"} and we'll add you automatically.</div>}

        {allowSignup && <div style={{ display: "flex", gap: 6, marginBottom: 18, background: "#111118", borderRadius: 8, padding: 3 }}>
          {[["signin", "Sign in"], ["signup", "Create account"]].map(([m, label]) => (
            <button key={m} type="button" onClick={() => { setMode(m); setErr(""); setMsg(""); }} style={{ flex: 1, padding: "8px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: mode === m ? "#26263a" : "transparent", color: mode === m ? "#e8e4dc" : "#6a6a80" }}>{label}</button>
          ))}
        </div>}

        {isSignup && <>
          <label style={{ fontSize: 11, color: "#8a8a9a", fontWeight: 600 }}>Your name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={authInp} />
          <div style={{ height: 14 }} />
          {isStokvel && <>
            <label style={{ fontSize: 11, color: "#8a8a9a", fontWeight: 600 }}>Invite code <span style={{ color: "#6a6a80", fontWeight: 400 }}>(optional — if joining a group)</span></label>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. MAS7QK" style={{ ...authInp, fontFamily: "monospace", textTransform: "uppercase" }} />
            <div style={{ fontSize: 10.5, color: "#6a6a80", marginTop: 4, lineHeight: 1.5 }}>Have a code from your chairperson? Enter it to request to join — you'll wait for their approval. Leave blank to just create an account (you can create or join a group afterwards).</div>
            <div style={{ height: 14 }} />
            <label style={{ fontSize: 11, color: "#8a8a9a", fontWeight: 600 }}>Referral code <span style={{ color: "#6a6a80", fontWeight: 400 }}>(optional — if a partner referred you)</span></label>
            <input value={referral} onChange={(e) => setReferral(e.target.value)} placeholder="e.g. WIS" style={{ ...authInp, fontFamily: "monospace", textTransform: "uppercase" }} />
            <div style={{ fontSize: 10.5, color: "#6a6a80", marginTop: 4, lineHeight: 1.5 }}>Were you referred by a partner or group? Enter their code so they get the credit.</div>
            <div style={{ height: 14 }} />
          </>}
        </>}
        <label style={{ fontSize: 11, color: "#8a8a9a", fontWeight: 600 }}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus style={authInp} />
        <div style={{ height: 14 }} />
        <label style={{ fontSize: 11, color: "#8a8a9a", fontWeight: 600 }}>Password</label>
        <div style={{ position: "relative" }}>
          <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isSignup ? "At least 6 characters" : ""} style={{ ...authInp, paddingRight: 44 }} />
          <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} title={showPw ? "Hide password" : "Show password"} style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 6, opacity: 0.75 }}>{showPw ? "🙈" : "👁️"}</button>
        </div>
        {err && <div style={{ marginTop: 14, fontSize: 12, color: "#ff8a8a", lineHeight: 1.4 }}>{err}</div>}
        {msg && <div style={{ marginTop: 14, fontSize: 12, color: "#7ee0a0", lineHeight: 1.5 }}>{msg}</div>}
        <button type="submit" disabled={busy} style={authBtn(busy)}>{busy ? (isSignup ? "Creating…" : "Signing in…") : (isSignup ? "Create account" : "Sign in")}</button>
        {!isSignup && <div style={{ textAlign: "center", marginTop: 12 }}><button type="button" onClick={busy ? undefined : forgot} disabled={busy} style={{ background: "none", border: "none", padding: 0, cursor: busy ? "default" : "pointer", color: "#c9a24b", fontSize: 12, fontWeight: 600 }}>Forgot password?</button></div>}

        {isSignup
          ? <div style={{ marginTop: 16, fontSize: 11, color: "#6a6a80", lineHeight: 1.5 }}>Creating an account sets up your stokvel access. We'll email you a link to verify your address.</div>
          : (allowSignup
              ? <div style={{ marginTop: 16, fontSize: 11, color: "#6a6a80", lineHeight: 1.5 }}>New here? Choose <strong>Create account</strong> to start or join a stokvel.</div>
              : <div style={{ marginTop: 16, fontSize: 11, color: "#6a6a80", lineHeight: 1.5 }}>Accounts are provisioned by your administrator. Contact them if you can't sign in.</div>)}
      </form>
    </div>
  );
}

// Shown to a self-registered stokvel user until they verify their email address.
function VerifyEmailScreen({ email, onResend, onRecheck, onSignOut }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const resend = async () => { setBusy(true); setMsg(""); try { await onResend(); setMsg("Sent again — check your inbox and your spam/promotions folder."); } catch (e) { setMsg(e.message || "Could not resend right now."); } setBusy(false); };
  const check = async () => { setBusy(true); setMsg(""); try { const ok = await onRecheck(); if (!ok) setMsg("Not verified yet. Open the link in the email first, then tap again."); } catch (e) { setMsg(e.message || "Could not check."); } setBusy(false); };
  return (
    <div style={authShell}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={authCard}>
        <div style={{ fontSize: 30, marginBottom: 8 }}>📧</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#e8e4dc" }}>Verify your email</div>
        <div style={{ fontSize: 12, color: "#8a8a9a", marginTop: 8, lineHeight: 1.6 }}>We sent a verification link to <strong style={{ color: "#e8e4dc" }}>{email}</strong>. Open it, then come back and tap “I've verified”.</div>
        {msg && <div style={{ marginTop: 14, fontSize: 12, color: "#a7d7b6", lineHeight: 1.4 }}>{msg}</div>}
        <button onClick={check} disabled={busy} style={authBtn(busy)}>{busy ? "Checking…" : "I've verified — continue"}</button>
        <button onClick={resend} disabled={busy} style={{ width: "100%", marginTop: 10, padding: "10px", borderRadius: 8, border: "1px solid #2a2a4a", background: "transparent", color: "#8a8a9a", fontSize: 13, cursor: "pointer" }}>Resend the email</button>
        <div style={{ marginTop: 16, textAlign: "center" }}><button onClick={onSignOut} style={{ background: "none", border: "none", color: "#6a6a80", fontSize: 11, cursor: "pointer" }}>Use a different account</button></div>
      </div>
    </div>
  );
}

// In-app account deletion (required by Apple, and by Google Play's data-safety rules).
// Re-authenticates with the password, then permanently deletes the Firebase account.
function DeleteAccountModal({ onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await deleteUserAccount(pw);
      window.location.reload();
    } catch (e) {
      const c = e && e.code;
      setErr(c === "auth/wrong-password" || c === "auth/invalid-credential" ? "Incorrect password — please try again." : (c === "auth/too-many-requests" ? "Too many attempts. Try again later." : (e.message || "Could not delete the account.")));
      setBusy(false);
    }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 24, maxWidth: 440, width: "100%", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#b3261e", marginBottom: 8 }}>Delete your account</div>
        <p style={{ fontSize: 13.5, color: "#444", lineHeight: 1.6, marginTop: 0 }}>
          This permanently deletes your Tlhakiso Stokvel account and personal profile, and signs you out everywhere. Shared group records may be kept by your stokvels as their own history — see our <a href="https://tlhakiso-app.web.app/data-deletion.html" target="_blank" rel="noreferrer" style={{ color: "#1f3a67" }}>Account &amp; Data Deletion policy</a>. <strong>This cannot be undone.</strong>
        </p>
        <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Confirm your password to continue</label>
        <input type="password" value={pw} autoFocus onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && pw && !busy) submit(); }} placeholder="Your password" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d8d5cd", fontSize: 14, boxSizing: "border-box" }} />
        {err && <div style={{ color: "#b3261e", fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button onClick={onClose} disabled={busy} style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid #d8d5cd", background: "#fff", fontSize: 13, cursor: "pointer", color: "#333" }}>Cancel</button>
          <button onClick={submit} disabled={busy || !pw} style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: (busy || !pw) ? "#e0a0a0" : "#b3261e", color: "#fff", fontSize: 13, fontWeight: 600, cursor: (busy || !pw) ? "default" : "pointer" }}>{busy ? "Deleting…" : "Delete my account"}</button>
        </div>
      </div>
    </div>
  );
}

// Dedicated shell for self-registered stokvel users: ONLY the stokvel product, none
// of the accounting/reconciliation surface. Keeps the two products cleanly separate.
function StokvelOnlyShell({ authUser, joinMsg, onDismissJoinMsg, refreshSignal, onSignOut }) {
  const [showDelete, setShowDelete] = useState(false);
  return (
    <div style={{ minHeight: "100vh", background: "#f5f4f0", fontFamily: "'DM Sans','Segoe UI',sans-serif", color: "#2c2a26" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ background: "#111118", color: "#e8e4dc", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Tlhakiso Stokvel</div>
          <div style={{ fontSize: 10, color: "#6a6a80", letterSpacing: 0.5 }}>Savings-group records</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 11, color: "#8a8a9a" }}>{authUser.email}</div>
          <button onClick={onSignOut} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #2a2a4a", background: "transparent", color: "#e8e4dc", fontSize: 11, cursor: "pointer" }}>Sign out</button>
          <button onClick={() => setShowDelete(true)} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #4a2020", background: "transparent", color: "#e0a0a0", fontSize: 11, cursor: "pointer" }}>Delete account</button>
        </div>
      </div>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: 20 }}>
        {joinMsg && <div style={{ background: "#eef6f0", border: "1px solid #cfe3d6", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#2f5c40", display: "flex", justifyContent: "space-between", alignItems: "center" }}><span>{joinMsg}</span><button onClick={onDismissJoinMsg} style={{ background: "none", border: "none", color: "#2f5c40", cursor: "pointer", fontSize: 15 }}>✕</button></div>}
        <StokvelView authUser={authUser} refreshSignal={refreshSignal} />
      </div>
      {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} />}
    </div>
  );
}

export default function App() {
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState("dashboard");
  const [sb, setSb] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [projOpen, setProjOpen] = useState(false);
  const [projSearch, setProjSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [coa, setCoa] = useState([]);
  const [journals, setJournals] = useState([]);
  const [opening, setOpening] = useState({});
  const [bootError, setBootError] = useState("");
  const [loadingProject, setLoadingProject] = useState(false);
  const [authUser, setAuthUser] = useState(undefined); // undefined = checking, null = signed out
  const [profile, setProfile] = useState(undefined);   // undefined = loading, {} = none, {stokvelOnly,...}
  const [verifiedOverride, setVerifiedOverride] = useState(false); // set true after a live re-check
  // Invite deep-link: /join/CODE → join this stokvel once signed in AND verified.
  const [pendingJoinCode, setPendingJoinCode] = useState(() => {
    try { const m = /^\/join\/([A-Za-z0-9]+)/.exec(window.location.pathname || ""); return m ? m[1].toUpperCase() : ""; } catch (e) { return ""; }
  });
  const [joinMsg, setJoinMsg] = useState("");

  // Track sign-in state, ensure the identity record exists, and load the profile
  // (which carries the stokvelOnly flag that scopes what product the user sees).
  useEffect(() => {
    const unsub = onAuth(async (u) => {
      setAuthUser(u || null);
      setVerifiedOverride(false);
      if (u) {
        try { await ensureUserRecord(u.uid, u.email); } catch (e) { /* non-fatal */ }
        try {
          const prof = await getUserProfile(u.uid);
          setProfile(prof || {});
          // Keep the local scope hint accurate for established users, but never clear
          // it for an unverified user (that would drop a brand-new stokvel signup's
          // scope before the profile write is visible).
          try {
            if (prof && prof.stokvelOnly) localStorage.setItem("tlhakiso_kind", "stokvel");
            else if (u.emailVerified) localStorage.removeItem("tlhakiso_kind");
          } catch (e2) {}
        } catch (e) { setProfile({}); }
      } else {
        setProfile(undefined);
        // Clear the scope hint on sign-out so a different user on this browser is
        // scoped by their OWN profile, not the previous session's residue.
        try { localStorage.removeItem("tlhakiso_kind"); } catch (e2) {}
      }
    });
    return () => unsub();
  }, []);

  // Re-check email verification against the server (used by the verify screen).
  const recheckVerified = async () => {
    const ok = await refreshEmailVerified();
    if (ok) setVerifiedOverride(true);
    return ok;
  };

  // Process an invite deep-link once the user is signed in AND verified.
  useEffect(() => {
    if (!pendingJoinCode || !authUser) return;
    if (!(authUser.emailVerified || verifiedOverride)) return; // wait for verification
    let cancelled = false;
    (async () => {
      try {
        const r = await joinStokvelByCode(pendingJoinCode);
        if (!cancelled) setJoinMsg(
          r.status === "active" ? ("You're already a member of " + (r.name || "that group") + ".")
          : ("Request sent to " + (r.name || "the group") + " — you'll get access once the chairperson approves you. 🎉"));
      } catch (e) {
        if (!cancelled) setJoinMsg("We couldn't add you automatically: " + (e.message || "please ask your chairperson for a fresh code."));
      }
      if (!cancelled) {
        setPendingJoinCode("");
        try { window.history.replaceState({}, "", "/"); } catch (e) {}
      }
    })();
    return () => { cancelled = true; };
  }, [pendingJoinCode, authUser, verifiedOverride]);

  const reloadProjects = async () => {
    if (!authUser) return;
    try {
      let list = await listProjectsForUser(authUser.uid);
      // Migration: claim any legacy project that has no members yet.
      const unclaimed = list.filter((p) => !p.memberUids || p.memberUids.length === 0);
      if (unclaimed.length) {
        for (const p of unclaimed) { try { await claimProjectIfUnclaimed(p.id, authUser.uid, authUser.email); } catch (e) { /* ignore */ } }
        list = await listProjectsForUser(authUser.uid);
      }
      setProjects(list);
      if (list.length > 0 && !activeId) setActiveId(list[0].id);
    } catch (err) {
      setBootError("Could not load projects from Firestore: " + err.message);
    }
  };

  // Load projects once signed in.
  useEffect(() => { reloadProjects(); }, [authUser]);

  // Complete a Stitch bank-link if we're returning from the consent flow (?code=…).
  // Dormant-safe: no-ops unless the bank feed is enabled and a link is pending.
  useEffect(() => {
    handleStitchRedirect({
      onLinked: async ({ linkId, projectId, parent, error }) => {
        if (error || !linkId || !projectId) return;
        try { await saveBankFeedLink(projectId, { linkId }, parent || "projects"); await reloadProjects(); } catch (e) { /* ignore */ }
      },
    }).catch(() => {});
  }, []);

  const loadAllForProject = async (projectId) => {
    setLoadingProject(true);
    try {
      let acoa = await loadCOA(projectId);
      if (acoa.length === 0) {
        await seedCOA(projectId);
        acoa = await loadCOA(projectId);
      }
      setCoa(acoa);
      const project = projects.find((p) => p.id === projectId);
      // No fixed project year anymore — load the latest opening-balance set on or
      // before the current tax-year start for the dashboard / journals views.
      const op = await loadOpeningBalancesAsAt(projectId, currentSATaxYear().start);
      setOpening(op || {});
      const js = await loadJournals(projectId);
      setJournals(js);
    } catch (err) {
      setBootError("Failed to load project data: " + err.message);
    }
    setLoadingProject(false);
  };

  useEffect(() => {
    if (activeId) loadAllForProject(activeId);
  }, [activeId]);

  const createProject = async (p) => {
    try {
      // The creator becomes the company's admin member.
      const withOwner = authUser
        ? { ...p, members: { [authUser.uid]: { role: "admin", email: (authUser.email || "").toLowerCase() } }, memberUids: [authUser.uid] }
        : p;
      await saveProject(p.id, withOwner);
      await seedCOA(p.id);
      await reloadProjects();
      setActiveId(p.id);
      setView("process");
      setShowNew(false);
    } catch (err) {
      alert("Could not create project: " + err.message);
    }
  };

  const project = projects.find((p) => p.id === activeId) || EMPTY_PROJECT;
  // Effective role for THIS company, from its membership map.
  const myMember = authUser && project.members ? project.members[authUser.uid] : null;
  const myRole = myMember ? myMember.role : ((!project.memberUids || !project.memberUids.length) ? "admin" : "readonly");
  const canWrite = myRole === "admin" || myRole === "preparer";
  const canLock = canWrite || myRole === "reviewer";
  const isAdmin = myRole === "admin";
  const role = myRole; // for the header label
  const isHeadOffice = isHeadOfficeAdmin(authUser && authUser.email);
  const nav = [
    { id: "headoffice", label: "Head Office", ic: "🏢", topLevel: true, headOffice: true, group: "Head Office" },
    { id: "dashboard", label: "Dashboard", ic: "◼", group: "Overview" },
    { id: "insights", label: "Insights", ic: "📊", group: "Overview" },
    { id: "reports", label: "Reports & Exports", ic: "⤓", group: "Overview" },
    { id: "tasks", label: "Tasks", ic: "✔", topLevel: true, group: "Workspace" },
    { id: "process", label: "Process Statement", ic: "⇥", write: true, group: "Bookkeeping" },
    { id: "prior", label: "Opening Balances", ic: "◐", write: true, group: "Bookkeeping" },
    { id: "manual", label: "Manual Journal", ic: "✚", write: true, group: "Bookkeeping" },
    { id: "journals", label: "Journals", ic: "✎", group: "Bookkeeping" },
    { id: "reconcile", label: "Reconciliation", ic: "⚖", group: "Bookkeeping" },
    { id: "assistant", label: "Assistant", ic: "✦", write: true, group: "Bookkeeping" },
    { id: "audit", label: "Audit Trail", ic: "▣", group: "Bookkeeping" },
    { id: "inventory", label: "Inventory", ic: "▥", write: true, group: "Operations" },
    { id: "payroll", label: "Payroll", ic: "☷", write: true, group: "Operations" },
    { id: "clients", label: "Clients", ic: "👥", topLevel: true, group: "CRM" },
    { id: "pipeline", label: "Pipeline", ic: "⚑", topLevel: true, group: "CRM" },
    { id: "compliance", label: "Compliance", ic: "📅", topLevel: true, group: "CRM" },
    { id: "invoices", label: "Invoices", ic: "₪", topLevel: true, write: true, group: "CRM" },
    { id: "stokvel", label: "Stokvel", ic: "🤝", topLevel: true, group: "Stokvel" },
    { id: "coa", label: "Chart of Accounts", ic: "◈", group: "Setup" },
    { id: "profile", label: "Client Profile", ic: "⚙", group: "Setup" },
    { id: "access", label: "Team & Access", ic: "⚷", admin: true, group: "Setup" },
    // Hidden until the bank feed is provisioned (BANK_FEED_ENABLED === false today).
    ...(BANK_FEED_ENABLED ? [{ id: "bankfeed", label: "Bank Feed", ic: "🏦", admin: true, group: "Setup" }] : []),
  ].filter((n) => (canWrite || !n.write) && (!n.admin || isAdmin) && (!n.headOffice || isHeadOffice));

  // If the current view isn't available to this role, fall back to the dashboard.
  const allowedView = nav.some((n) => n.id === view) ? view : "dashboard";

  // ── Auth gate ──
  if (authUser === undefined) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#111118", color: "#8a8a9a", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>Loading…</div>;
  }
  const stokvelPlatform = detectStokvelPlatform();
  if (!authUser) {
    return <LoginScreen joinCode={pendingJoinCode} platform={stokvelPlatform ? "stokvel" : "accounting"} onSetJoinCode={setPendingJoinCode} />;
  }
  // Head-office admin dashboard — reachable at ?headoffice=1 by a signed-in user.
  // Access is enforced server-side (admin email allowlist); non-admins get a 403.
  try { if (new URLSearchParams(window.location.search).has("headoffice")) return <AdminDashboard />; } catch (e) {}
  // Wait for the profile so we can decide which product (stokvel-only vs full) to show.
  if (profile === undefined) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#111118", color: "#8a8a9a", fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>Loading…</div>;
  }
  const stokvelHint = (() => { try { return localStorage.getItem("tlhakiso_kind") === "stokvel"; } catch (e) { return false; } })();
  const stokvelOnly = (profile && profile.stokvelOnly) || stokvelHint;
  const emailOk = authUser.emailVerified || verifiedOverride;
  // Anyone using the stokvel platform (or any self-registered stokvel user) must verify
  // their email before entering — so they can't create or run a stokvel unverified.
  if ((stokvelOnly || stokvelPlatform) && !emailOk) {
    return <VerifyEmailScreen email={authUser.email} onResend={resendVerification} onRecheck={recheckVerified} onSignOut={() => signOutUser()} />;
  }
  // The stokvel platform (its own URL) shows ONLY the stokvel app — to EVERYONE, so
  // even an accounting user who lands here never sees the client-bookkeeping surface.
  // Stokvel-scoped users get the same dedicated shell on any URL.
  if (stokvelPlatform || stokvelOnly) {
    return <StokvelOnlyShell authUser={authUser} joinMsg={joinMsg} onDismissJoinMsg={() => setJoinMsg("")} refreshSignal={joinMsg} onSignOut={() => signOutUser()} />;
  }

  const roleLabel = { admin: "Admin", preparer: "Preparer", reviewer: "Reviewer", readonly: "Read-only", auditor: "Auditor" }[role] || role || "";

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", fontFamily: "'DM Sans','Segoe UI',sans-serif", color: "#2c2a26" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        .nav-scroll { scrollbar-width: thin; scrollbar-color: #2f2f40 transparent; }
        .nav-scroll::-webkit-scrollbar { width: 6px; }
        .nav-scroll::-webkit-scrollbar-track { background: transparent; }
        .nav-scroll::-webkit-scrollbar-thumb { background: #2a2a3a; border-radius: 3px; }
        .nav-scroll::-webkit-scrollbar-thumb:hover { background: #3a3a4f; }
        .nav-scroll:hover { scrollbar-color: #3a3a4f transparent; }
      `}</style>
      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreate={createProject} />}
      <div style={{ width: sb ? 240 : 0, overflow: "hidden", transition: "width 0.2s", background: "#111118", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 18px 10px", color: "#e8e4dc" }}><Logo /><div style={{ fontSize: 10, color: "#5a5a6a", marginTop: 4, letterSpacing: 0.5 }}>Reconciliation Platform</div></div>
        <div style={{ padding: "8px 10px" }}>
          <div style={{ fontSize: 9, color: "#44445a", textTransform: "uppercase", letterSpacing: 1.8, padding: "8px 8px 4px", fontWeight: 700 }}>Projects</div>
          {projects.length === 0 && <div style={{ padding: "9px 10px", fontSize: 11, color: "#6a6a80" }}>No projects yet</div>}
          {projects.length > 0 && (
            <div onClick={() => setProjOpen((o) => !o)} style={{ padding: "9px 10px", borderRadius: 7, cursor: "pointer", marginTop: 3, background: "#1a1a2e", border: "1px solid #2a2a4a", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#e8e4dc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{activeId ? (project.name || "Company") : "Select a company"}</div>
                {activeId && <div style={{ fontSize: 10, color: "#6a6a80", marginTop: 1 }}>{project.year || ""}{project.type ? " · " + project.type : ""}</div>}
              </div>
              <span style={{ fontSize: 9, color: "#8a8a9a" }}>{projOpen ? "▲" : "▼"}</span>
            </div>
          )}
          {projOpen && projects.length > 0 && (
            <div style={{ marginTop: 5 }}>
              {projects.length > 6 && <input value={projSearch} onChange={(e) => setProjSearch(e.target.value)} placeholder="Search companies…" autoFocus style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", fontSize: 12, borderRadius: 6, border: "1px solid #2a2a4a", background: "#16161f", color: "#e8e4dc", outline: "none", marginBottom: 4 }} />}
              <div style={{ maxHeight: 300, overflowY: "auto" }}>
                {projects.filter((p) => !projSearch || (p.name || "").toLowerCase().includes(projSearch.toLowerCase())).map((p) => (
                  <div key={p.id} onClick={() => { setActiveId(p.id); setView("dashboard"); setProjOpen(false); setProjSearch(""); }} style={{ padding: "8px 10px", borderRadius: 7, cursor: "pointer", marginTop: 2, background: p.id === activeId ? "#23233a" : "transparent" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: p.id === activeId ? "#e8e4dc" : "#8a8a9a" }}>{p.name && p.name.length > 24 ? p.name.substring(0, 24) + "…" : p.name}</div>
                    <div style={{ fontSize: 10, color: "#6a6a80", marginTop: 1 }}>{p.year || ""}{p.type ? " · " + p.type : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canWrite && <div onClick={() => setShowNew(true)} style={{ padding: "9px 10px", borderRadius: 7, cursor: "pointer", marginTop: 3, border: "1px dashed #2a2a4a", textAlign: "center" }}><span style={{ fontSize: 11, color: "#44445a" }}>+ New Project</span></div>}
        </div>
        <div className="nav-scroll" style={{ padding: "12px 10px", borderTop: "1px solid #1a1a2e", marginTop: 6, flex: 1, minHeight: 0, overflowY: "auto" }}>
          {(() => {
            const groups = [];
            nav.forEach((n) => {
              const name = n.group || "Views";
              let g = groups.find((x) => x.name === name);
              if (!g) { g = { name, items: [] }; groups.push(g); }
              g.items.push(n);
            });
            return groups.map((g) => {
              const collapsed = !!collapsedGroups[g.name];
              return (
                <div key={g.name} style={{ marginBottom: 4 }}>
                  <div onClick={() => setCollapsedGroups((s) => ({ ...s, [g.name]: !s[g.name] }))} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "6px 8px 4px" }}>
                    <span style={{ fontSize: 9, color: "#44445a", textTransform: "uppercase", letterSpacing: 1.6, fontWeight: 700 }}>{g.name}</span>
                    <span style={{ fontSize: 9, color: "#44445a" }}>{collapsed ? "▸" : "▾"}</span>
                  </div>
                  {!collapsed && g.items.map((n) => (
                    <div key={n.id} onClick={() => setView(n.id)} style={{ padding: "7px 10px", borderRadius: 7, cursor: "pointer", marginBottom: 1, background: view === n.id ? "#1a1a2e" : "transparent", display: "flex", alignItems: "center", gap: 9 }}>
                      <span style={{ fontSize: 12, opacity: 0.7 }}>{n.ic}</span>
                      <span style={{ fontSize: 12, color: view === n.id ? "#c8a24e" : "#8a8a9a", fontWeight: view === n.id ? 600 : 400 }}>{n.label}</span>
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </div>
        <div style={{ marginTop: "auto", padding: 14, borderTop: "1px solid #1a1a2e" }}>
          <div style={{ fontSize: 10, color: "#44445a" }}>Journals</div>
          <div style={{ fontSize: 11, color: "#6a6a80", marginTop: 3 }}>{journals.length} posted</div>
        </div>
      </div>
      <div style={{ flex: 1, background: "#f5f4f0", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "10px 20px", borderBottom: "1px solid #e8e4dc", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setSb(!sb)} style={{ background: "none", border: "1px solid #e0ddd8", borderRadius: 5, padding: "3px 7px", cursor: "pointer", fontSize: 14 }}>{"☰"}</button>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>{project.name || "No project selected"}</div>
              <div style={{ fontSize: 10, color: "#9a9488" }}>{(nav.find((n) => n.id === allowedView) || {}).label} {"·"} {project.year || ""}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {loadingProject && <div style={{ fontSize: 11, color: "#9a9488" }}>Loading...</div>}
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "#1a1a2e", fontWeight: 600 }}>{authUser.email}</div>
              <div style={{ fontSize: 10, color: "#9a9488" }}>{roleLabel}</div>
            </div>
            <button onClick={() => signOutUser()} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #e0ddd8", background: "#fff", fontSize: 11, cursor: "pointer", color: "#1a1a2e" }}>Sign out</button>
          </div>
        </div>
        {bootError && <div style={{ padding: "8px 20px", background: "#fff4f4", color: "#b33", fontSize: 12, borderBottom: "1px solid #f0c0c0" }}>{bootError}</div>}
        <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
          {!activeId && allowedView !== "invoices" && allowedView !== "headoffice" && (
            <div style={{ textAlign: "center", padding: 60, color: "#9a9488" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>&#128193;</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#5a5a5a" }}>No project selected</div>
              <div style={{ fontSize: 13, marginTop: 8 }}>Create a new project from the sidebar to start, or open Invoices.</div>
            </div>
          )}
          {activeId && allowedView === "dashboard" && <Dash project={project} journals={journals} opening={opening} coa={coa} />}
          {activeId && allowedView === "insights" && <InsightsView project={project} coa={coa} opening={opening} journals={journals} />}
          {activeId && allowedView === "process" && <Process project={project} coa={coa} journals={journals} onPosted={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "prior" && <PriorImport project={project} coa={coa} opening={opening} onSaved={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "manual" && <ManualJournal project={project} coa={coa} onPosted={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "journals" && <JournalsBrowser project={project} coa={coa} opening={opening} journals={journals} canWrite={canWrite} canLock={canLock} onRefresh={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "reconcile" && <><Reconciliation project={project} coa={coa} opening={opening} journals={journals} /><DuplicateScan project={project} journals={journals} coa={coa} onRefresh={() => loadAllForProject(activeId)} /></>}
          {activeId && allowedView === "audit" && <AuditTrail project={project} />}
          {activeId && allowedView === "assistant" && <Assistant project={project} coa={coa} journals={journals} canWrite={canWrite} onRefresh={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "reports" && <Reports project={project} coa={coa} opening={opening} journals={journals} onRefresh={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "inventory" && <Inventory project={project} coa={coa} journals={journals} onPosted={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "payroll" && <Payroll project={project} coa={coa} onPosted={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "coa" && <COAManager project={project} coa={coa} journals={journals} onChanged={() => loadAllForProject(activeId)} />}
          {activeId && allowedView === "access" && <AccessManager project={project} authUser={authUser} onChanged={reloadProjects} />}
          {activeId && allowedView === "profile" && <Profile D={project} />}
          {activeId && allowedView === "bankfeed" && BANK_FEED_ENABLED && <BankFeedSettings project={project} />}
          {allowedView === "invoices" && <Invoices projects={projects} />}
          {allowedView === "clients" && <ClientsView projects={projects} onChanged={reloadProjects} />}
          {allowedView === "pipeline" && <PipelineView />}
          {allowedView === "compliance" && <ComplianceView projects={projects} onSave={(pid, comp) => saveProject(pid, { compliance: comp }).then(reloadProjects)} />}
          {allowedView === "tasks" && <TasksView projects={projects} authUser={authUser} />}
          {allowedView === "stokvel" && <StokvelView authUser={authUser} refreshSignal={joinMsg} />}
          {allowedView === "headoffice" && isHeadOffice && <AdminDashboard embedded />}
        </div>
      </div>
    </div>
  );
}
